import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useImpersonation, applyImpersonation, clearImpersonationStorage } from './context/ImpersonationContext';
import { useLocation } from './context/LocationContext';
import { canAccessSettings as computeCanAccessSettings, canAccessByKey, canModule } from './config/permissions';
import TreatmentLogViewer, { FITZPATRICK_QUESTIONS } from './components/TreatmentLogViewer';
import { useApiHeaders, buildAuthHeaders } from './lib/api';
import './StaffRecommend.css';
import './ChangeRequests.css';

// Lazy-load heavy components — only downloaded when the user navigates to that tab
const StaffKPIs = React.lazy(() => import('./StaffKPIs'));
const StaffMe = React.lazy(() => import('./StaffMe'));
const StaffTasks = React.lazy(() => import('./StaffTasks'));
const TaskCalendar = React.lazy(() => import('./TaskCalendar'));
const StaffInventory = React.lazy(() => import('./StaffInventory'));
const StaffReporting = React.lazy(() => import('./StaffReporting'));
const PoppyReports = React.lazy(() => import('./PoppyReports'));
const PoppyBook = React.lazy(() => import('./PoppyBook'));
const MasterData = React.lazy(() => import('./MasterData'));
const PoppyBlockoutTypes = React.lazy(() => import('./PoppyBlockoutTypes'));
const PoppyDivergenceReview = React.lazy(() => import('./PoppyDivergenceReview'));
const ServiceClearances = React.lazy(() => import('./ServiceClearances'));
const PoppyConfiguration = React.lazy(() => import('./PoppyConfiguration'));
const PoppyBusinessDetails = React.lazy(() => import('./PoppyBusinessDetails'));
const PoppyAppointmentsConfig = React.lazy(() => import('./PoppyAppointmentsConfig'));
const PoppyNotificationsConfig = React.lazy(() => import('./PoppyNotificationsConfig'));
const PoppyGuestProfile = React.lazy(() => import('./PoppyGuestProfile'));
const MasterTracker = React.lazy(() => import('./MasterTracker'));
const ScheduleSync = React.lazy(() => import('./ScheduleSync'));
const StaffHR = React.lazy(() => import('./StaffHR'));
const CallOffAdvisor = React.lazy(() => import('./CallOffAdvisor'));
const StaffAdvisor = React.lazy(() => import('./StaffAdvisor'));
const KioskAnalytics = React.lazy(() => import('./KioskAnalytics'));
const CampaignMonitor = React.lazy(() => import('./CampaignMonitor'));
const ChecklistSettings = React.lazy(() => import('./components/ChecklistSettings'));
const AuditHome = React.lazy(() => import('./components/AuditHome'));
const PerformanceInbox = React.lazy(() => import('./components/PerformanceInbox'));
const PerformanceRouting = React.lazy(() => import('./components/PerformanceRouting'));
const SettingsHub = React.lazy(() => import('./components/SettingsHub'));
const TeamSettings = React.lazy(() => import('./components/TeamSettings'));
const PayConfigSettings = React.lazy(() => import('./settings/PayConfigSettings'));
const SlowTimePool = React.lazy(() => import('./components/SlowTimePool'));
const KioskPaymentSettings = React.lazy(() => import('./settings/KioskPaymentSettings'));
const LateStartSettings = React.lazy(() => import('./settings/LateStartSettings'));
const EarlyDepartureSettings = React.lazy(() => import('./settings/EarlyDepartureSettings'));
const MarketingSettings = React.lazy(() => import('./components/MarketingSettings'));
const KPIReportsSettings = React.lazy(() => import('./components/KPIReportsSettings'));
const KPITargetsSettings = React.lazy(() => import('./settings/KPITargetsSettings'));
const BookingOrderSettings = React.lazy(() => import('./components/BookingOrderSettings'));
const ChangeRequestsLog = React.lazy(() => import('./ChangeRequestsLog'));
const ScheduleOptimizer = React.lazy(() => import('./components/ScheduleOptimizer'));
const Payroll = React.lazy(() => import('./Payroll'));
const MonthEnd = React.lazy(() => import('./MonthEnd'));
const ServiceLogs = React.lazy(() => import('./components/ServiceLogs'));

function TabLoading() {
  return <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>;
}

// "HH:mm" (24h, from Zenoti attendance feed) → "9:00 AM"
function formatClockTime(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return hhmm || '';
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(); d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// Stored clock timestamps are Pacific wall-clock kept in the UTC components
// (repo-wide convention). Read them with getUTC* and format directly — NEVER
// toLocaleTimeString with a timeZone, which re-applies the offset and shifts
// the displayed time (the recurring "7:08 AM" tz bug).
function formatStoredTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  let h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
}

// Schedule card visit badge: visitCount is # of completed prior visits, so
// the appointment being viewed is the (visitCount + 1)th. visitCount=0 is
// handled by the NEW badge upstream and never reaches this helper.
function visitBadgeLabel(visitCount) {
  const n = visitCount + 1;
  if (n >= 11) return '11+ visits';
  const ords = { 2: '2nd', 3: '3rd', 4: '4th', 5: '5th', 6: '6th', 7: '7th', 8: '8th', 9: '9th', 10: '10th' };
  return `${ords[n] || `${n}th`} visit`;
}

// Results-screen title reflects what actually happened, not a blanket
// "Booking Updated" regardless of outcome (see badge-color house rule —
// don't paper over failed/flagged/skipped changes with a success-sounding
// title).
function correctionsResultsTitle(results) {
  if (!results || results.length === 0) return 'Booking Updated';
  const succeeded = results.filter(r => r.status === 'success' || r.status === 'past_shifted').length;
  if (succeeded === results.length) return 'Booking Updated';
  if (succeeded === 0) return 'Correction failed';
  return 'Partially applied';
}

// Most-specific keyword wins. `tint` is last because combo names like
// "Lash Lift + Filler + Tint" contain it as a substring.
// KEEP IN LOCKSTEP with backend/lib/treatment-log-type.js — the serviceType this
// derives is sent to POST /api/staff/treatment-log; if the two ladders diverge the
// client-classified type will desync from the server's.
function getTreatmentLogType(serviceName) {
  const n = (serviceName || '').toLowerCase();
  if (/lash\s*lift/.test(n)) return 'lash_lift';
  if (/brow\s*lam/.test(n))  return 'brow_lam';
  if (/laser/.test(n))       return 'laser';
  if (/tint/.test(n))        return 'tinting';
  return 'other';
}

// Only mounts children on first activation, then keeps mounted with display:none
function LazyTab({ active, children }) {
  const [hasBeenActive, setHasBeenActive] = useState(active);
  useEffect(() => { if (active) setHasBeenActive(true); }, [active]);
  if (!hasBeenActive) return null;
  return <div style={{ display: active ? 'block' : 'none' }}>{children}</div>;
}

const CR_VISIT_FRESH_MS = 30000; // Modify re-reads the invoice when the preloaded copy is older than this
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Change-request status chips (schedule strip). Sent = still with the desk
// (open/applying); real colors for real outcomes per the badge-color house
// rule — green only when it actually landed, amber when the desk couldn't.
const CR_STATUS_META = {
  open:         { label: 'Sent',      cls: 'cr-chip-sent' },
  applying:     { label: 'Sent',      cls: 'cr-chip-sent' },
  done:         { label: 'Done ✓',    cls: 'cr-chip-done' },
  auto_applied: { label: 'Applied ✓', cls: 'cr-chip-done' },
  cant:         { label: "Couldn't",  cls: 'cr-chip-cant' },
  expired:      { label: 'Expired',   cls: 'cr-chip-expired' },
};

// "$22" / "$22.50" — invoice prices are dollars; hide trailing .00 like the
// rest of the staff UI but never round real cents away.
const crMoney = (v) => {
  const r = Math.round((Number(v) || 0) * 100) / 100;
  return Number.isInteger(r) ? `$${r}` : `$${r.toFixed(2)}`;
};

// Module access by role group
// G1: owner — G2: ecr_manager — G3: salon_coordinator — G4: lead_waxer, lead_nail_tech — G5: receptionist — G6: esthetician, nail_tech
const ROLE_MODULES = {
  owner:            ['tasks', 'hr', 'performance', 'guests', 'analytics', 'kiosk', 'reporting', 'inventory', 'sync', 'calloff', 'campaigns', 'month_end'],
  ecr_manager:      ['tasks', 'hr', 'performance', 'guests', 'analytics', 'kiosk', 'reporting', 'inventory', 'sync', 'calloff'],
  salon_coordinator:['tasks', 'performance', 'guests', 'analytics', 'kiosk', 'reporting', 'inventory', 'calloff'],
  lead_waxer:       ['schedule', 'tasks', 'performance', 'reporting', 'inventory', 'me'],
  lead_nail_tech:   ['schedule', 'tasks', 'performance', 'reporting', 'inventory', 'me'],
  receptionist:     ['tasks', 'reporting'],
  esthetician:      ['schedule', 'tasks', 'me'],
  nail_tech:        ['schedule', 'tasks', 'me'],
  employee:         ['schedule', 'tasks', 'me'],
};

function hasModule(role, mod) {
  return (ROLE_MODULES[role] || ROLE_MODULES.employee).includes(mod);
}

// Center coordinates for geofencing (200m radius)
const CENTER_COORDS = [
  { slug: 'lonsdale', name: 'Lonsdale', lat: 49.3103, lng: -123.0826, centerId: '68f9a3f8-4927-4808-b91a-68f71a47d771' },
  { slug: 'mountain-hwy', name: 'Mountain Hwy', lat: 49.3231, lng: -123.0350, centerId: 'bb3035e6-32a2-451a-95ba-de4f281ceb8e' },
  { slug: 'west-vancouver', name: 'West Vancouver', lat: 49.3266, lng: -123.1617, centerId: '099f4638-5eaa-4528-8730-54ee7b22555b' },
];

// Geofence temporarily disabled — will re-enable
// const GEOFENCE_RADIUS = 200;
// function haversineDistance(lat1, lon1, lat2, lon2) {
//   const R = 6371000;
//   const dLat = (lat2 - lat1) * Math.PI / 180;
//   const dLon = (lon2 - lon1) * Math.PI / 180;
//   const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
//   return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
// }

function getSession() {
  try { return JSON.parse(localStorage.getItem('staff-session')); } catch { return null; }
}

function saveSession(data) {
  localStorage.setItem('staff-session', JSON.stringify(data));
}

function clearSession() {
  localStorage.removeItem('staff-session');
  clearImpersonationStorage();
}

// Room color coding — same number = same color regardless of type (Room/Mani/Pedi)
const ROOM_COLORS = {
  1: { bg: '#e8f5e9', color: '#2e7d32' },
  2: { bg: '#e3f2fd', color: '#1565c0' },
  3: { bg: '#fce4ec', color: '#c62828' },
  4: { bg: '#fff3e0', color: '#e65100' },
  5: { bg: '#f3e5f5', color: '#7b1fa2' },
  6: { bg: '#e0f7fa', color: '#00838f' },
  7: { bg: '#fff9c4', color: '#f57f17' },
  8: { bg: '#efebe9', color: '#4e342e' },
};
function getRoomColor(roomName) {
  if (!roomName) return null;
  const match = roomName.match(/(\d+)/);
  if (!match) return null;
  return ROOM_COLORS[parseInt(match[1])] || null;
}

const GUEST_FORM_FIELDS = [
  { key: 'textField', label: 'First Name' },
  { key: 'textField1', label: 'Last Name' },
  { key: 'datetimezenGIBirthday', label: 'Birthday' },
  { key: 'textField2', label: 'Phone' },
  { key: 'textField3', label: 'Email' },
  { key: 'selectBoxes', label: 'Services Interested In', type: 'checks' },
  { key: 'selectBoxes6', label: 'Services Interested In', type: 'checks' },
  { key: 'selectBoxes5', label: 'How Did You Hear About Us?', type: 'checks' },
  { key: 'textField4', label: 'Allergies or Medications?' },
  { key: 'textField5', label: 'Are You Pregnant?' },
  { key: 'textField6', label: 'Any Skin Conditions?' },
  { key: 'selectBoxes1', label: 'Consent & Acknowledgment', type: 'checks' },
  { key: 'selectBoxes2', label: 'Current Products or Medications', type: 'checks' },
  { key: 'selectBoxes3', label: 'Previous Waxing Experience?', type: 'checks' },
  { key: 'selectBoxes4', label: 'Past Reactions to Waxing?', type: 'checks' },
  { key: 'signature', label: 'Signature', type: 'signature' },
];

function formatCheckVal(k) {
  return k.replace(/([A-Z])/g, ' $1').replace(/Or/g, ' / ').trim().replace(/^./, s => s.toUpperCase());
}

function GuestFormViewer({ data }) {
  const rowStyle = { padding: '10px 0', borderBottom: '1px solid #f0f0f0' };
  const labelStyle = { fontSize: 11, fontWeight: 700, color: '#999', letterSpacing: 0.3, marginBottom: 3 };
  const valStyle = { fontSize: 14, color: '#333', fontWeight: 500 };
  return (
    <div>
      {GUEST_FORM_FIELDS.map(({ key, label, type }) => {
        const val = data[key];
        if (val === undefined || val === null) return null;
        if (type === 'signature' && typeof val === 'string' && val.startsWith('data:image')) {
          return (
            <div key={key} style={rowStyle}>
              <div style={labelStyle}>{label}</div>
              <img src={val} alt="Signature" style={{ maxWidth: '100%', height: 70, borderRadius: 8, border: '1px solid #eee', background: '#fafafa', marginTop: 4 }} />
            </div>
          );
        }
        if (type === 'checks' && typeof val === 'object') {
          const selected = Object.entries(val).filter(([, v]) => v).map(([k]) => formatCheckVal(k));
          if (selected.length === 0) return (
            <div key={key} style={rowStyle}>
              <div style={labelStyle}>{label}</div>
              <div style={{ ...valStyle, color: '#bbb', fontStyle: 'italic' }}>None selected</div>
            </div>
          );
          return (
            <div key={key} style={rowStyle}>
              <div style={labelStyle}>{label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {selected.map((s, i) => (
                  <span key={i} style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 8, background: '#f0e8f0', color: '#6a3d7d' }}>{s}</span>
                ))}
              </div>
            </div>
          );
        }
        if (typeof val === 'string' && val.trim()) {
          let displayVal = val;
          if (val.match(/^\d{4}-\d{2}-\d{2}T/)) {
            try { displayVal = new Date(val).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); } catch(e) {}
          }
          return (
            <div key={key} style={rowStyle}>
              <div style={labelStyle}>{label}</div>
              <div style={valStyle}>{displayVal}</div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

const THUYA_COLORS = ['Ash Brown', 'Bluish Back', 'Chestnut', 'Deep Black', 'Light Brown', 'Natural Brown', 'Taupe'];
const BERRYWELL_COLORS = ['Black', 'Blue Black', 'Chestnut', 'Light Brown', 'Natural Brown'];

function TintingForm({ onSubmit, saving, existingData }) {
  const [serviceArea, setServiceArea] = useState(existingData?.serviceArea || []);
  const [clientAsked, setClientAsked] = useState(existingData?.clientAsked || '');
  const [thuyaColors, setThuyaColors] = useState(existingData?.thuyaColors || []);
  const [berrywellColors, setBerrywellColors] = useState(existingData?.berrywellColors || []);
  const [mixingFormulas, setMixingFormulas] = useState(existingData?.mixingFormulas || '');
  const [processingNotes, setProcessingNotes] = useState(existingData?.processingNotes || '');

  const toggleArr = (arr, setArr, val) => {
    setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  const checkStyle = (checked) => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
    borderRadius: 8, border: `1.5px solid ${checked ? '#d4899e' : '#e8e0e4'}`,
    background: checked ? '#fdf0f4' : '#fff', cursor: 'pointer', marginBottom: 6,
    fontSize: 13, fontWeight: checked ? 600 : 400, color: '#333',
  });
  const boxStyle = (checked) => ({
    width: 18, height: 18, borderRadius: 4, border: `2px solid ${checked ? '#d4899e' : '#ccc'}`,
    background: checked ? '#d4899e' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, color: '#fff', fontSize: 12, fontWeight: 700,
  });
  const labelStyle = { fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6, marginTop: 14 };
  const textareaStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e0d0d6', fontSize: 13, fontFamily: 'inherit', background: '#fff', color: '#333', minHeight: 70, resize: 'vertical', boxSizing: 'border-box' };

  const canSubmit = serviceArea.length > 0;

  return (
    <div>
      <div style={labelStyle}>Service Area <span style={{ color: '#e74c3c' }}>*</span></div>
      {['Brows', 'Lashes'].map(area => (
        <div key={area} style={checkStyle(serviceArea.includes(area))} onClick={() => toggleArr(serviceArea, setServiceArea, area)}>
          <div style={boxStyle(serviceArea.includes(area))}>{serviceArea.includes(area) ? '✓' : ''}</div>
          {area}
        </div>
      ))}

      <div style={labelStyle}>What the client asked for</div>
      <textarea style={textareaStyle} value={clientAsked} onChange={e => setClientAsked(e.target.value)} placeholder="Describe what the client requested..." />

      <div style={labelStyle}>Tint Colour(s) — Thuya</div>
      {THUYA_COLORS.map(c => (
        <div key={c} style={checkStyle(thuyaColors.includes(c))} onClick={() => toggleArr(thuyaColors, setThuyaColors, c)}>
          <div style={boxStyle(thuyaColors.includes(c))}>{thuyaColors.includes(c) ? '✓' : ''}</div>
          Thuya - {c}
        </div>
      ))}

      <div style={labelStyle}>Tint Colour(s) — Berrywell</div>
      {BERRYWELL_COLORS.map(c => (
        <div key={c} style={checkStyle(berrywellColors.includes(c))} onClick={() => toggleArr(berrywellColors, setBerrywellColors, c)}>
          <div style={boxStyle(berrywellColors.includes(c))}>{berrywellColors.includes(c) ? '✓' : ''}</div>
          Berrywell - {c}
        </div>
      ))}

      <div style={labelStyle}>Mixing Formulas</div>
      <textarea style={textareaStyle} value={mixingFormulas} onChange={e => setMixingFormulas(e.target.value)} placeholder="e.g. 1:1 ratio, custom mix..." />

      <div style={labelStyle}>Processing Time Notes</div>
      <textarea style={textareaStyle} value={processingNotes} onChange={e => setProcessingNotes(e.target.value)} placeholder="e.g. 10 min brows, 5 min lashes..." />

      <button
        onClick={() => onSubmit({ serviceArea, clientAsked, thuyaColors, berrywellColors, mixingFormulas, processingNotes })}
        disabled={!canSubmit || saving}
        style={{
          width: '100%', marginTop: 16, padding: '14px 0', borderRadius: 12, border: 'none',
          background: canSubmit && !saving ? '#d4899e' : '#e0d0d6', color: '#fff',
          fontSize: 15, fontWeight: 700, cursor: canSubmit && !saving ? 'pointer' : 'default',
        }}
      >
        {saving ? 'Saving...' : existingData ? 'Update Treatment Log' : 'Submit Treatment Log'}
      </button>
    </div>
  );
}

// --- Shared form styles ---
const formLabelStyle = { fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6, marginTop: 14 };
const formTextareaStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e0d0d6', fontSize: 13, fontFamily: 'inherit', background: '#fff', color: '#333', minHeight: 50, resize: 'vertical', boxSizing: 'border-box' };
const formInputStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e0d0d6', fontSize: 13, fontFamily: 'inherit', background: '#fff', color: '#333', boxSizing: 'border-box' };
const formCheckStyle = (checked) => ({
  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
  borderRadius: 8, border: `1.5px solid ${checked ? '#d4899e' : '#e8e0e4'}`,
  background: checked ? '#fdf0f4' : '#fff', cursor: 'pointer', marginBottom: 6,
  fontSize: 13, fontWeight: checked ? 600 : 400, color: '#333',
});
const formBoxStyle = (checked) => ({
  width: 18, height: 18, borderRadius: 4, border: `2px solid ${checked ? '#d4899e' : '#ccc'}`,
  background: checked ? '#d4899e' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0, color: '#fff', fontSize: 12, fontWeight: 700,
});
const formSubmitStyle = (canSubmit, saving) => ({
  width: '100%', marginTop: 16, padding: '14px 0', borderRadius: 12, border: 'none',
  background: canSubmit && !saving ? '#d4899e' : '#e0d0d6', color: '#fff',
  fontSize: 15, fontWeight: 700, cursor: canSubmit && !saving ? 'pointer' : 'default',
});
function toggleInArr(arr, val) {
  return arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];
}

function LashLiftForm({ onSubmit, saving, existingData }) {
  const [shieldColour, setShieldColour] = useState(existingData?.shieldColour || '');
  const [shieldSize, setShieldSize] = useState(existingData?.shieldSize || '');
  const [permTime, setPermTime] = useState(existingData?.permTime || '');
  const [setTime, setSetTime] = useState(existingData?.setTime || '');
  const [tintColour, setTintColour] = useState(existingData?.tintColour || '');
  const [notes, setNotes] = useState(existingData?.notes || '');
  return (
    <div>
      <div style={formLabelStyle}>Shield Colour</div>
      <input style={formInputStyle} value={shieldColour} onChange={e => setShieldColour(e.target.value)} placeholder="e.g. Clear, Blue..." />
      <div style={formLabelStyle}>Shield Size</div>
      <input style={formInputStyle} value={shieldSize} onChange={e => setShieldSize(e.target.value)} placeholder="e.g. Small, Medium, Large..." />
      <div style={formLabelStyle}>Perm Time</div>
      <input style={formInputStyle} value={permTime} onChange={e => setPermTime(e.target.value)} placeholder="e.g. 8 min" />
      <div style={formLabelStyle}>Set Time</div>
      <input style={formInputStyle} value={setTime} onChange={e => setSetTime(e.target.value)} placeholder="e.g. 6 min" />
      <div style={formLabelStyle}>Tint Colour</div>
      <input style={formInputStyle} value={tintColour} onChange={e => setTintColour(e.target.value)} placeholder="e.g. Dark Brown" />
      <div style={formLabelStyle}>Notes</div>
      <textarea style={formTextareaStyle} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional notes..." />
      <button onClick={() => onSubmit({ shieldColour, shieldSize, permTime, setTime, tintColour, notes })} disabled={saving} style={formSubmitStyle(true, saving)}>
        {saving ? 'Saving...' : existingData ? 'Update Treatment Log' : 'Submit Treatment Log'}
      </button>
    </div>
  );
}

function BrowLamForm({ onSubmit, saving, existingData }) {
  const [permTime, setPermTime] = useState(existingData?.permTime || '');
  const [setTime, setSetTime] = useState(existingData?.setTime || '');
  const [tintColour, setTintColour] = useState(existingData?.tintColour || '');
  const [notes, setNotes] = useState(existingData?.notes || '');
  return (
    <div>
      <div style={formLabelStyle}>Perm Time</div>
      <input style={formInputStyle} value={permTime} onChange={e => setPermTime(e.target.value)} placeholder="e.g. 8 min" />
      <div style={formLabelStyle}>Set Time</div>
      <input style={formInputStyle} value={setTime} onChange={e => setSetTime(e.target.value)} placeholder="e.g. 6 min" />
      <div style={formLabelStyle}>Tint Colour</div>
      <input style={formInputStyle} value={tintColour} onChange={e => setTintColour(e.target.value)} placeholder="e.g. Dark Brown" />
      <div style={formLabelStyle}>Notes</div>
      <textarea style={formTextareaStyle} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional notes..." />
      <button onClick={() => onSubmit({ permTime, setTime, tintColour, notes })} disabled={saving} style={formSubmitStyle(true, saving)}>
        {saving ? 'Saving...' : existingData ? 'Update Treatment Log' : 'Submit Treatment Log'}
      </button>
    </div>
  );
}

function getFitzpatrickType(score) {
  if (score <= 7) return 'Type I — Very Fair';
  if (score <= 16) return 'Type II — Fair';
  if (score <= 25) return 'Type III — Medium';
  if (score <= 30) return 'Type IV — Olive';
  return 'Type V-VI — Brown/Dark Brown';
}

function FitzpatrickForm({ onSubmit, saving, existingData }) {
  const [answers, setAnswers] = useState(existingData?.answers || {});
  const score = Object.values(answers).reduce((s, v) => s + v, 0);
  const allAnswered = FITZPATRICK_QUESTIONS.every(q => answers[q.key] !== undefined);
  const radioStyle = (selected) => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
    borderRadius: 8, border: `1.5px solid ${selected ? '#d4899e' : '#e8e0e4'}`,
    background: selected ? '#fdf0f4' : '#fff', cursor: 'pointer', marginBottom: 4,
    fontSize: 12, fontWeight: selected ? 600 : 400, color: '#333',
  });
  const dotStyle = (selected) => ({
    width: 16, height: 16, borderRadius: '50%', border: `2px solid ${selected ? '#d4899e' : '#ccc'}`,
    background: selected ? '#d4899e' : '#fff', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  });
  return (
    <div>
      {FITZPATRICK_QUESTIONS.map(q => (
        <div key={q.key}>
          <div style={formLabelStyle}>{q.label} <span style={{ color: '#e74c3c' }}>*</span></div>
          {q.options.map((opt, idx) => (
            <div key={opt} style={radioStyle(answers[q.key] === idx)} onClick={() => setAnswers(prev => ({ ...prev, [q.key]: idx }))}>
              <div style={dotStyle(answers[q.key] === idx)}>
                {answers[q.key] === idx && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
              </div>
              {opt}
            </div>
          ))}
        </div>
      ))}
      {allAnswered && (
        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 10, background: '#f5f0fa', border: '1.5px solid #d5c8e0' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6a3d7d', textTransform: 'uppercase', letterSpacing: 0.3 }}>Score: {score}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#333', marginTop: 4 }}>{getFitzpatrickType(score)}</div>
        </div>
      )}
      <button onClick={() => onSubmit({ answers, score, skinType: getFitzpatrickType(score) })} disabled={!allAnswered || saving} style={formSubmitStyle(allAnswered, saving)}>
        {saving ? 'Saving...' : existingData ? 'Update Assessment' : 'Submit Assessment'}
      </button>
    </div>
  );
}

const SKIN_TYPES = ['1', '2', '3', '4', '5', '6'];
const HAIR_COLORS = ['Blonde', 'Light Brown', 'Dark Brown', 'Black'];
const COARSENESS_OPTIONS = ['Fine', 'Mid', 'Coarse'];
const DENSITY_OPTIONS = ['Sparse', 'Mid', 'Dense'];

function LaserForm({ onSubmit, saving, existingData }) {
  const [fitzAnswers, setFitzAnswers] = useState(existingData?.fitzpatrick?.answers || {});
  const fitzScore = Object.values(fitzAnswers).reduce((s, v) => s + v, 0);
  const fitzComplete = FITZPATRICK_QUESTIONS.every(q => fitzAnswers[q.key] !== undefined);
  const [showFitz, setShowFitz] = useState(!existingData);
  const [sections, setSections] = useState(existingData?.sections || [{ area: '', spotSite: [], jMs2: '', ms: '', pulses: '', skinType: [], hairColor: [], coarseness: [], density: [] }]);
  const [reaction, setReaction] = useState(existingData?.reaction || '');
  const [notes, setNotes] = useState(existingData?.notes || '');

  const updateSection = (idx, field, value) => {
    setSections(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const addSection = () => {
    setSections(prev => [...prev, { area: '', spotSite: [], jMs2: '', ms: '', pulses: '', skinType: [], hairColor: [], coarseness: [], density: [] }]);
  };
  const removeSection = (idx) => {
    if (sections.length > 1) setSections(prev => prev.filter((_, i) => i !== idx));
  };
  const canSubmit = sections.some(s => s.area.trim());

  const radioStyle = (selected) => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
    borderRadius: 8, border: `1.5px solid ${selected ? '#d4899e' : '#e8e0e4'}`,
    background: selected ? '#fdf0f4' : '#fff', cursor: 'pointer', marginBottom: 4,
    fontSize: 12, fontWeight: selected ? 600 : 400, color: '#333',
  });
  const dotStyle = (selected) => ({
    width: 16, height: 16, borderRadius: '50%', border: `2px solid ${selected ? '#d4899e' : '#ccc'}`,
    background: selected ? '#d4899e' : '#fff', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <div>
      {/* Fitzpatrick Skin Score Assessment */}
      <button onClick={() => setShowFitz(!showFitz)} style={{ width: '100%', padding: '10px 14px', marginBottom: 10, borderRadius: 10, border: '1.5px solid #d5c8e0', background: '#f5f0fa', cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#6a3d7d' }}>Fitzpatrick Skin Score {fitzComplete ? `— ${getFitzpatrickType(fitzScore)}` : ''}</span>
        <span style={{ fontSize: 10, color: '#ccc' }}>{showFitz ? '▲' : '▼'}</span>
      </button>
      {showFitz && (
        <div style={{ padding: '0 4px 12px', borderBottom: '2px dashed #e8e0e4', marginBottom: 12 }}>
          {FITZPATRICK_QUESTIONS.map(q => (
            <div key={q.key}>
              <div style={formLabelStyle}>{q.label} <span style={{ color: '#e74c3c' }}>*</span></div>
              {q.options.map((opt, idx2) => (
                <div key={opt} style={radioStyle(fitzAnswers[q.key] === idx2)} onClick={() => setFitzAnswers(prev => ({ ...prev, [q.key]: idx2 }))}>
                  <div style={dotStyle(fitzAnswers[q.key] === idx2)}>
                    {fitzAnswers[q.key] === idx2 && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                  {opt}
                </div>
              ))}
            </div>
          ))}
          {fitzComplete && (
            <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 10, background: '#f5f0fa', border: '1.5px solid #d5c8e0' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6a3d7d' }}>Score: {fitzScore}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#333', marginTop: 2 }}>{getFitzpatrickType(fitzScore)}</div>
            </div>
          )}
        </div>
      )}

      {/* Treatment Areas */}
      {sections.map((sec, idx) => (
        <div key={idx} style={{ padding: '12px 0', borderBottom: idx < sections.length - 1 ? '2px dashed #e8e0e4' : 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ ...formLabelStyle, marginTop: 4, fontSize: 13, color: '#d4899e' }}>Treatment Area {idx + 1}</div>
            {sections.length > 1 && (
              <button onClick={() => removeSection(idx)} style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Remove</button>
            )}
          </div>
          <div style={formLabelStyle}>Area <span style={{ color: '#e74c3c' }}>*</span></div>
          <input style={formInputStyle} value={sec.area} onChange={e => updateSection(idx, 'area', e.target.value)} placeholder="e.g. Upper Lip, Underarm..." />
          <div style={formLabelStyle}>Spot Site</div>
          {['Small', 'Large'].map(s => (
            <div key={s} style={formCheckStyle(sec.spotSite.includes(s))} onClick={() => updateSection(idx, 'spotSite', toggleInArr(sec.spotSite, s))}>
              <div style={formBoxStyle(sec.spotSite.includes(s))}>{sec.spotSite.includes(s) ? '✓' : ''}</div>{s}
            </div>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <div>
              <div style={formLabelStyle}>J/ms²</div>
              <input style={formInputStyle} value={sec.jMs2} onChange={e => updateSection(idx, 'jMs2', e.target.value)} placeholder="—" />
            </div>
            <div>
              <div style={formLabelStyle}>Ms</div>
              <input style={formInputStyle} value={sec.ms} onChange={e => updateSection(idx, 'ms', e.target.value)} placeholder="—" />
            </div>
            <div>
              <div style={formLabelStyle}>Pulses</div>
              <input style={formInputStyle} value={sec.pulses} onChange={e => updateSection(idx, 'pulses', e.target.value)} placeholder="—" />
            </div>
          </div>
          <div style={formLabelStyle}>Skin Type</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {SKIN_TYPES.map(t => (
              <div key={t} style={{ ...formCheckStyle(sec.skinType.includes(t)), flex: '0 0 auto', marginBottom: 4 }} onClick={() => updateSection(idx, 'skinType', toggleInArr(sec.skinType, t))}>
                <div style={formBoxStyle(sec.skinType.includes(t))}>{sec.skinType.includes(t) ? '✓' : ''}</div>{t}
              </div>
            ))}
          </div>
          <div style={formLabelStyle}>Hair Color</div>
          {HAIR_COLORS.map(c => (
            <div key={c} style={formCheckStyle(sec.hairColor.includes(c))} onClick={() => updateSection(idx, 'hairColor', toggleInArr(sec.hairColor, c))}>
              <div style={formBoxStyle(sec.hairColor.includes(c))}>{sec.hairColor.includes(c) ? '✓' : ''}</div>{c}
            </div>
          ))}
          <div style={formLabelStyle}>Coarseness</div>
          {COARSENESS_OPTIONS.map(c => (
            <div key={c} style={formCheckStyle(sec.coarseness.includes(c))} onClick={() => updateSection(idx, 'coarseness', toggleInArr(sec.coarseness, c))}>
              <div style={formBoxStyle(sec.coarseness.includes(c))}>{sec.coarseness.includes(c) ? '✓' : ''}</div>{c}
            </div>
          ))}
          <div style={formLabelStyle}>Hair Density</div>
          {DENSITY_OPTIONS.map(d => (
            <div key={d} style={formCheckStyle(sec.density.includes(d))} onClick={() => updateSection(idx, 'density', toggleInArr(sec.density, d))}>
              <div style={formBoxStyle(sec.density.includes(d))}>{sec.density.includes(d) ? '✓' : ''}</div>{d}
            </div>
          ))}
        </div>
      ))}
      <button onClick={addSection} style={{ width: '100%', padding: '10px 0', marginTop: 8, borderRadius: 10, border: '1.5px dashed #d4899e', background: '#fdf8fa', color: '#d4899e', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        + Add Treatment Area
      </button>
      <div style={formLabelStyle}>Reaction</div>
      <textarea style={formTextareaStyle} value={reaction} onChange={e => setReaction(e.target.value)} placeholder="Describe skin reaction..." />
      <div style={formLabelStyle}>Notes</div>
      <textarea style={formTextareaStyle} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional notes..." />
      <button onClick={() => onSubmit({ fitzpatrick: fitzComplete ? { answers: fitzAnswers, score: fitzScore, skinType: getFitzpatrickType(fitzScore) } : null, sections, reaction, notes })} disabled={!canSubmit || saving} style={formSubmitStyle(canSubmit, saving)}>
        {saving ? 'Saving...' : existingData ? 'Update Treatment Log' : 'Submit Treatment Log'}
      </button>
    </div>
  );
}

export default function StaffRecommend({ staffView: externalView, setStaffView: externalSetView, onLayoutSignOut, onSessionChange, signOutRef, logDeepLink, onLogDeepLinkConsumed }) {
  const [step, setStep] = useState('geofence'); // geofence, blocked, login, clock-in, clients, actions, recommend, saved, referral-sent, review-sent, modify-select, modify-pick, modify-confirm, modify-done, forms, notes
  const [internalView, setInternalView] = useState('clients');
  const staffView = externalView || internalView;
  const setStaffView = externalSetView || setInternalView;
  const [lastRefresh, setLastRefresh] = useState(null);
  const [noShowLoading, setNoShowLoading] = useState(false);
  const [opsToken, setOpsToken] = useState(() => {
    try { return JSON.parse(localStorage.getItem('staff-session'))?.opsToken || null; } catch { return null; }
  });
  const [center, setCenter] = useState(null);
  const [session, setSession] = useState(getSession);
  const [geoError, setGeoError] = useState('');

  // View-as / impersonation. dataSession swaps the data identity (employeeId,
  // centerId, employeeName) of the impersonated user onto the real session,
  // leaving auth fields (token, opsToken, opsRole) untouched. canWrite is false
  // while impersonating — gates all mutation handlers below.
  const { impersonatedAs, impersonatedSession, canWrite, isImpersonating } = useImpersonation();
  const dataSession = useMemo(() => applyImpersonation(session, impersonatedAs), [session, impersonatedAs]);
  // Module-gate checks use the impersonated session during View As, same
  // pattern as StaffTasks/StaffMe — checking the real (owner) session would
  // show gated action cards to every impersonated waxer view.
  const effSession = isImpersonating ? impersonatedSession : session;
  // Auth headers for the new JWT-protected /api/staff/corrections/* routes
  // (unlike the legacy /api/staff/* endpoints above, which have no auth).
  const apiHeaders = useApiHeaders(opsToken);

  // Active center for staff-scoped data fetches (schedule, today's actions,
  // catalog). Header LocationPicker is the source of truth when a specific
  // center is selected. Falls back to the session's login center for the
  // default 'all' state so a fresh login isn't empty.
  const { centerId: pickerCenterId, setLocation, LOCATIONS } = useLocation();
  const activeCenterId = pickerCenterId || dataSession?.centerId || null;
  function blockIfImpersonating() {
    if (!canWrite) {
      alert('Read-only while viewing as someone else. Exit View As to make changes.');
      return true;
    }
    return false;
  }

  // Settings + sub-tab access. Helpers in config/permissions.js apply the
  // shared rule: owner → always; admin → granular toggle (default ON);
  // non-admin → never. When impersonating, use the target's synthetic
  // session so the preview matches their actual access.
  const canAccessSettings = useMemo(
    () => computeCanAccessSettings(impersonatedAs ? impersonatedSession : session),
    [session, impersonatedAs, impersonatedSession]
  );

  // Push session changes up to Layout so it can show user name / sign out
  useEffect(() => {
    if (onSessionChange) onSessionChange(session);
  }, [session, onSessionChange]);

  // Refresh effective modules from /me once per app load so Team→Access
  // toggles apply on the next refresh instead of requiring a re-login.
  useEffect(() => {
    if (!opsToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/ops-auth/me`, {
          headers: { Authorization: `Bearer ${opsToken}` },
        });
        if (!res.ok || cancelled) return;
        const me = await res.json();
        if (!Array.isArray(me.modules)) return;
        setSession(prev => {
          if (!prev || cancelled) return prev;
          const next = {
            ...prev,
            modules: me.modules,
            opsRole: me.role ?? prev.opsRole,
            opsIsAdmin: me.isAdmin ?? prev.opsIsAdmin,
          };
          saveSession(next);
          return next;
        });
      } catch { /* offline or expired token — keep the stored session */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opsToken]);

  // Clock in/out state
  const [clockStatus, setClockStatus] = useState(null); // { clockedIn, clockInTime, scheduledStart, ... }
  const [clockLoading, setClockLoading] = useState(false);
  const [clockError, setClockError] = useState('');
  const [showClockOut, setShowClockOut] = useState(false);

  // Login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Client list
  const [appointments, setAppointments] = useState([]);
  const [blockouts, setBlockouts] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  // Schedule date navigation — Pacific 'YYYY-MM-DD' for today, and the
  // currently-viewed schedule date. Back 7 days (for late log entry),
  // forward 14 days (view-only, upcoming schedule).
  const pacificTodayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Vancouver' });
  const SCHEDULE_BACK_DAYS = 7;
  const SCHEDULE_FWD_DAYS = 14;
  const [scheduleDate, setScheduleDate] = useState(pacificTodayStr());
  // Shift a 'YYYY-MM-DD' string by N days without tz double-conversion.
  const shiftDateStr = (s, n) => {
    const [y, m, d] = s.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + n));
    return dt.toISOString().slice(0, 10);
  };
  const today = pacificTodayStr();
  const scheduleOffset = Math.round((Date.parse(`${scheduleDate}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);
  const isTodaySchedule = scheduleDate === today;

  // Approved duties for the person whose schedule this is (spec 2026-10-07 §7):
  // rendered as blocks inside their gaps. Shares the sheet's query; refetches on
  // focus and every 60 s — duty assignments change on lead taps, not
  // appointment-by-appointment.
  // Spec v2 §9: same react-query key as the sheet (StaffTasks), so a lead's tap there
  // invalidates this view too; focus refetch covers phones coming back from the lock screen.
  const scheduleEmployeeId = dataSession?.employeeId;
  const { data: dutyToday } = useQuery({
    queryKey: ['duty-today', activeCenterId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/ops/duty/today?centerId=${activeCenterId}`, { headers: buildAuthHeaders(opsToken, impersonatedAs) });
      if (!r.ok) throw new Error(`${r.status}`);
      return r.json();
    },
    enabled: !!isTodaySchedule && !!activeCenterId && !!opsToken && !!scheduleEmployeeId,
    staleTime: 10 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000,
  });
  // A disabled query still hands back cached data — gate here too so today's
  // duties never leak onto another day's schedule.
  const myDuties = useMemo(() => (isTodaySchedule && !!activeCenterId && !!opsToken && !!scheduleEmployeeId && dutyToday?.enabled
    ? (dutyToday.assignments || []).filter(a => a.status === 'approved' && a.assignedEmployeeId === scheduleEmployeeId && (a.plannedStartMin != null || a.noGap))
    : []), [isTodaySchedule, activeCenterId, opsToken, dutyToday, scheduleEmployeeId]);
  const dutyHm = (m) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
  const dutyPill = (d, { showTime = false } = {}) => (
    <div key={d.itemId} style={{ margin: '4px 4px 0', display: 'inline-block', padding: '2px 8px', borderRadius: 999, background: '#fce7f3', color: '#be185d', border: '1px solid #f472b6', fontWeight: 600 }}>
      🧹 {showTime && d.plannedStartMin != null ? `${dutyHm(d.plannedStartMin)} ` : ''}{d.text}{d.assignedName ? ` · ${d.assignedName}` : ''} {d.durationMin}m{d.noGap ? ' · no gap' : ''}
    </div>
  );

  // Recommend state
  const [selectedClient, setSelectedClient] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);

  // Tabs & categories
  const [staffTab, setStaffTab] = useState('packages');
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentSubCategory, setCurrentSubCategory] = useState(null);

  // Data for each tab
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState([]);
  const [catalog, setCatalog] = useState(null); // { categories: [...], services: { catId: [...] } }

  // Selected items (each item has: id, name, price, description, type, discount?)
  const [selectedItems, setSelectedItems] = useState([]);

  // Staff actions tracking
  const [todayActions, setTodayActions] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [pendingAction, setPendingAction] = useState(null); // 'referral' or 'review' — confirms before sending

  // Forms state
  const [guestForms, setGuestForms] = useState([]);
  const [guestFormData, setGuestFormData] = useState(null);
  const [formsLoading, setFormsLoading] = useState(false);
  const [expandedForm, setExpandedForm] = useState(null);
  const [treatmentLogs, setTreatmentLogs] = useState([]);
  const [treatmentLogsLoading, setTreatmentLogsLoading] = useState(false);
  const [treatmentLogData, setTreatmentLogData] = useState({}); // keyed by logKey(appointmentId, serviceId): { formData, id }
  const [treatmentLogSaving, setTreatmentLogSaving] = useState(false);
  const [treatmentLogError, setTreatmentLogError] = useState(''); // shown inline in the form on save failure
  const [editingLogKey, setEditingLogKey] = useState(null); // which filled log is being re-edited
  const [settingsHistory, setSettingsHistory] = useState({}); // { 'Laser Brazilian': [rows] }

  // Client notes state
  const [clientNotes, setClientNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteCategory, setNoteCategory] = useState('Lash and Brow');
  const [noteSaving, setNoteSaving] = useState(false);

  // Past appointments state
  const [pastAppointments, setPastAppointments] = useState([]);
  const [pastLoading, setPastLoading] = useState(false);

  // Client feedback state
  const [feedbackType, setFeedbackType] = useState('client_feedback');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSaving, setFeedbackSaving] = useState(false);

  // Modify service state
  const [modifyService, setModifyService] = useState(null); // the service being replaced
  const [modifyCatalog, setModifyCatalog] = useState(null); // full service catalog
  const [modifyCategory, setModifyCategory] = useState(null);
  const [modifyLoading, setModifyLoading] = useState(false);
  const [modifySearch, setModifySearch] = useState('');

  // Booking corrections state — "Fix booking" sheet (receptionless
  // swap/add/remove, separate from the legacy Modify Booking flow above).
  const [correctionsOpen, setCorrectionsOpen] = useState(false);
  const [correctionsStep, setCorrectionsStep] = useState('lines'); // lines | picker | addons | results
  const [correctionsLoading, setCorrectionsLoading] = useState(false);
  const [correctionsVisit, setCorrectionsVisit] = useState(null); // GET /visit response
  const [correctionsError, setCorrectionsError] = useState('');
  const [correctionsBasket, setCorrectionsBasket] = useState([]); // [{type, ...}]
  const [correctionsPickerMode, setCorrectionsPickerMode] = useState(null); // 'replace' | 'add'
  const [correctionsReplaceTarget, setCorrectionsReplaceTarget] = useState(null); // line being replaced
  const [correctionsSearch, setCorrectionsSearch] = useState('');
  const [correctionsCategory, setCorrectionsCategory] = useState(null);
  const [correctionsPendingService, setCorrectionsPendingService] = useState(null); // svc chosen, picking add-ons next
  const [correctionsAddOnIds, setCorrectionsAddOnIds] = useState([]);
  const [correctionsSubmitting, setCorrectionsSubmitting] = useState(false);
  const [correctionsResults, setCorrectionsResults] = useState(null); // POST /apply response

  // Appointment change-request state — schedule-card "Modify" flow
  // (backend/routes/staff-change-requests.js: guest-approved swap/add/remove
  // that auto-applies or lands on the front desk as a card). Deliberately
  // self-contained with a `cr` prefix: it reuses the corrections flow's
  // VISUAL patterns and change payload shapes but shares none of its state —
  // the corrections live-write flow above must keep working unchanged.
  const [crOpen, setCrOpen] = useState(false);
  const [crStep, setCrStep] = useState('edit'); // edit | picker | addons | attach | attach-pick | confirm | done
  const [crLoading, setCrLoading] = useState(false);
  const [crVisit, setCrVisit] = useState(null); // GET /visit response { lines, catalog, mode, guestName, ... }
  const [crError, setCrError] = useState('');
  const [crTarget, setCrTarget] = useState(null); // the schedule card the flow opened from
  const [crBasket, setCrBasket] = useState([]); // pending changes, corrections-shaped (+ display names)
  const crBasketRef = useRef(crBasket); // read by the async fresh-invoice adoption
  crBasketRef.current = crBasket;
  const [crPickerMode, setCrPickerMode] = useState(null); // 'replace' | 'add'
  const [crReplaceTarget, setCrReplaceTarget] = useState(null); // BOOKED line being changed
  const [crSearch, setCrSearch] = useState('');
  const [crPendingService, setCrPendingService] = useState(null); // svc chosen, picking its add-ons next
  const [crAddOnIds, setCrAddOnIds] = useState([]);
  const [crAttachTarget, setCrAttachTarget] = useState(null); // "+ Add add-on" → which service it rides on
  const [crNote, setCrNote] = useState('');
  const [crSubmitting, setCrSubmitting] = useState(false);
  const [crResult, setCrResult] = useState(null); // 'applied' | 'sent'
  const [crMine, setCrMine] = useState([]); // GET /mine — today's requests for the status strip
  // Idempotency key: minted ONCE per entry to the confirm screen so retries
  // of the same confirm dedupe server-side; cleared when leaving confirm
  // back to editing (an edited request is a NEW submission).
  const crKeyRef = useRef(null);

  // Service/add-on price+duration lookup from the /visit capability catalog
  // (display-only math — the server recomputes totals authoritatively).
  const crPrice = useMemo(() => {
    const svcById = new Map();
    const aoById = new Map();
    for (const s of crVisit?.catalog || []) {
      svcById.set(s.id, s);
      for (const a of s.addOns || []) if (!aoById.has(a.id)) aoById.set(a.id, a);
    }
    return { svcById, aoById };
  }, [crVisit]);

  // Geofence check on mount — skip if already logged in
  useEffect(() => {
    if (step !== 'geofence') return;

    // TEMPORARY: Geofence disabled — show center picker on login
    setCenter(CENTER_COORDS[1]); // default Mountain Hwy
    const existing = getSession();
    if (existing && existing.token) {
      const savedCenter = CENTER_COORDS.find(c => c.centerId === existing.centerId) || CENTER_COORDS[1];
      setCenter(savedCenter);
      setSession(existing);
      if (existing.opsToken) setOpsToken(existing.opsToken);
      validateAndProceed(existing, savedCenter);
    } else {
      setStep('login');
    }
  }, [step]);

  // Route to clock-in or clients based on clock status.
  // FULL LAUNCH (2026-07-07): clock-in gate live for ALL schedule-role staff.
  // Verified end-to-end (real Zenoti checkin/checkout, delayed early-punch
  // delivery, app-punches-win sync). Was soft-launched owner-only first.
  async function routeAfterAuth(sess) {
    // Non-schedule roles (front desk, etc.) bypass the clock-in gate
    if (!hasModule(sess.opsRole, 'schedule')) {
      setStaffView('tasks');
      setStep('clients');
      return;
    }
    // Check if already clocked in today (from session cache)
    const todayStr = new Date().toLocaleDateString('en-US', { timeZone: 'America/Vancouver' });
    if (sess.clockedInDate === todayStr && sess.clockedInAt) {
      setStep('clients');
      return;
    }
    // Check server
    const status = await checkClockStatus(sess);
    if (status?.clockedIn && !status?.clockedOut) {
      // Already clocked in — save to session and proceed
      const updated = { ...sess, clockedInAt: status.clockInTime, clockedInDate: todayStr };
      saveSession(updated);
      setSession(updated);
      setStep('clients');
    } else {
      setStep('clock-in');
    }
  }

  // Validate existing session (24-hour re-check)
  async function validateAndProceed(sess, centerInfo) {
    const hoursSinceValidation = (Date.now() - (sess.lastValidated || 0)) / (1000 * 60 * 60);
    if (hoursSinceValidation < 24) {
      setSession(sess);
      await routeAfterAuth(sess);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/staff/validate-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: sess.token })
      });
      const data = await res.json();
      if (data.valid) {
        const updated = { ...sess, lastValidated: Date.now() };
        saveSession(updated);
        setSession(updated);
        await routeAfterAuth(updated);
      } else {
        clearSession();
        setSession(null);
        setLoginError('Session expired. Please sign in again.');
        setStep('login');
      }
    } catch {
      setSession(sess);
      await routeAfterAuth(sess);
    }
  }

  // Login
  async function handleLogin(e) {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/kiosk/employee-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password, centerId: center.centerId })
      });
      const data = await res.json();
      if (data.success) {
        // Silent ops platform login
        let opsJwt = null;
        let opsRole = null;
        let opsUserId = null;
        let opsEmail = null;
        let opsFirstName = null;
        let opsIsAdmin = false;
        let opsPermissions = [];
        let opsModules = [];
        try {
          const opsRes = await fetch(`${API_URL}/api/ops-auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_name: username.trim(), password }),
          });
          console.log('[OPS LOGIN] status:', opsRes.status);
          if (opsRes.ok) {
            const opsData = await opsRes.json();
            opsJwt = opsData.token;
            opsRole = opsData.user?.role || null;
            opsUserId = opsData.user?.id || null;
            opsEmail = opsData.user?.email || null;
            opsFirstName = opsData.user?.firstName || null;
            opsIsAdmin = opsData.user?.isAdmin || false;
            opsPermissions = opsData.permissions || [];
            opsModules = opsData.modules || [];
            console.log('[OPS LOGIN] success, role:', opsRole);
            setOpsToken(opsJwt);
          } else {
            console.log('[OPS LOGIN] failed:', await opsRes.text());
          }
        } catch (opsErr) { console.log('[OPS LOGIN] error:', opsErr.message); }

        const sess = {
          employeeId: data.employee?.id,
          employeeName: data.employee?.name,
          token: data.token,
          centerId: center.centerId,
          loginTime: Date.now(),
          lastValidated: Date.now(),
          opsToken: opsJwt,
          opsRole,
          opsUserId,
          opsEmail,
          opsFirstName,
          opsIsAdmin,
          opsPermissions,
          modules: opsModules,
        };
        saveSession(sess);
        setSession(sess);
        await routeAfterAuth(sess);
      } else {
        setLoginError(data.error || 'Invalid credentials. Please try again.');
      }
    } catch {
      setLoginError('Connection error. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  }

  // Clock status check
  async function checkClockStatus(sess) {
    try {
      const params = new URLSearchParams({ employeeId: sess.employeeId, centerId: sess.centerId });
      const res = await fetch(`${API_URL}/api/staff/clock-status?${params}`);
      const data = await res.json();
      setClockStatus(data);
      return data;
    } catch (e) {
      console.log('[CLOCK] Status check error:', e.message);
      return null;
    }
  }

  // One-shot phone position for the clock-in geofence. Resolves null on any
  // failure (denied, timeout, no GPS) — the SERVER decides whether location
  // is required for this center, so an unconfigured center keeps working.
  function getPositionOnce() {
    return new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  }

  // Clock in
  async function handleClockIn() {
    if (!session) return;
    if (blockIfImpersonating()) return;
    setClockLoading(true);
    setClockError('');
    try {
      const pos = await getPositionOnce();
      const res = await fetch(`${API_URL}/api/staff/clock-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId: session.employeeId, centerId: activeCenterId, lat: pos?.lat, lng: pos?.lng })
      });
      const data = await res.json();
      if (data.success) {
        setClockStatus(prev => ({ ...prev, clockedIn: true, clockInTime: data.clockInTime, clockedOut: false, centerName: data.centerName || null }));
        // Server may have auto-detected the salon they're physically at
        // (clock_in_auto_center) — snap the location picker to it so their
        // schedule and tasks show the right salon, not a stale selection.
        if (data.centerId && data.centerId !== activeCenterId) {
          const loc = LOCATIONS.find(l => l.centerId === data.centerId);
          if (loc) setLocation(loc.key);
        }
        // Save clock-in to session so we don't re-prompt
        const updated = { ...session, clockedInAt: data.clockInTime, clockedInDate: new Date().toLocaleDateString('en-US', { timeZone: 'America/Vancouver' }) };
        saveSession(updated);
        setSession(updated);
        if (!hasModule(session.opsRole, 'schedule')) {
          setStaffView('tasks');
        }
        setStep('clients');
      } else if (data.error === 'already_clocked_in') {
        setClockStatus(prev => ({ ...prev, clockedIn: true, clockInTime: data.clockInTime }));
        setStep('clients');
      } else if (data.error === 'location_required') {
        setClockError(`Clock-in needs your location so we know you're at ${data.centerName || 'the salon'}. Turn on Location for this app in your phone settings, then try again.`);
      } else if (data.error === 'outside_geofence') {
        const away = data.distanceM >= 1000 ? `${(data.distanceM / 1000).toFixed(1)} km` : `${data.distanceM} m`;
        setClockError(`You can clock in once you arrive at ${data.centerName || 'the salon'} — you're about ${away} away.`);
      } else {
        setClockError(data.error || 'Failed to clock in');
      }
    } catch {
      setClockError('Connection error. Please try again.');
    } finally {
      setClockLoading(false);
    }
  }

  // Clock out
  async function handleClockOut() {
    if (!session) return;
    if (blockIfImpersonating()) return;
    setClockLoading(true);
    setClockError('');
    try {
      const res = await fetch(`${API_URL}/api/staff/clock-out`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId: session.employeeId, centerId: activeCenterId })
      });
      const data = await res.json();
      if (data.success) {
        setClockStatus(prev => ({ ...prev, clockedOut: true, clockOutTime: data.clockOutTime, totalHours: data.totalHours }));
        setShowClockOut(false);
      } else if (data.error === 'tasks_incomplete') {
        setClockError(`Complete ${data.pendingTasks} task${data.pendingTasks !== 1 ? 's' : ''} and ${data.pendingChecklists} checklist${data.pendingChecklists !== 1 ? 's' : ''} before clocking out`);
      } else {
        setClockError(data.error || 'Failed to clock out');
      }
    } catch {
      setClockError('Connection error. Please try again.');
    } finally {
      setClockLoading(false);
    }
  }

  // Refresh clock status whenever the Time Clock page is opened.
  useEffect(() => {
    if (staffView === 'time_clock' && session?.employeeId) {
      setClockError('');
      checkClockStatus(session);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffView, session?.employeeId]);

  // Load today's clients
  // background=true refreshes the list in place without flipping the loading
  // spinner, so the 30s auto-refresh (and manual refresh) don't blank the
  // schedule out from under staff mid-glance. First load still shows a spinner.
  // Ops login tokens last 7 days but this session lives longer — swap an
  // expired one for a fresh token (backend allows ≤30 days past expiry).
  // Returns the new token, 'expired' (must sign in again), or null (network).
  const refreshOpsToken = useCallback(async () => {
    const old = getSession()?.opsToken || opsToken;
    if (!old) return 'expired';
    try {
      const r = await fetch(`${API_URL}/api/ops-auth/refresh`, {
        method: 'POST', headers: { Authorization: `Bearer ${old}` },
      });
      if (r.status === 401) return 'expired';
      if (!r.ok) return null;
      const { token } = await r.json();
      if (!token) return null;
      const updated = { ...(getSession() || {}), opsToken: token };
      saveSession(updated);
      setOpsToken(token);
      setSession(prev => (prev ? { ...prev, opsToken: token } : prev));
      return token;
    } catch {
      return null;
    }
  }, [opsToken]);

  const loadClients = useCallback(async (background = false) => {
    if (!dataSession?.employeeId || !activeCenterId) return;
    if (!background) setLoadingClients(true);
    try {
      let url;
      if (scheduleDate === today) {
        const params = new URLSearchParams({
          centerId: activeCenterId,
          therapistId: dataSession.employeeId,
          employeeToken: session.token,
        });
        url = `${API_URL}/api/staff/my-appointments?${params}`;
      } else {
        const params = new URLSearchParams({
          centerId: activeCenterId,
          therapistId: dataSession.employeeId,
          date: scheduleDate,
        });
        url = `${API_URL}/api/staff/my-appointments-by-date?${params}`;
      }
      // Schedule reads require the ops login (2026-10-02) — own schedule only;
      // View As sends the impersonation header.
      let res = await fetch(url, { headers: buildAuthHeaders(opsToken, impersonatedAs) });
      if (res.status === 401) {
        const fresh = await refreshOpsToken();
        if (fresh === 'expired') {
          clearSession();
          setSession(null);
          setLoginError('Session expired. Please sign in again.');
          setStep('login');
          return;
        }
        if (!fresh) return; // network blip — keep the current list, next poll retries
        res = await fetch(url, { headers: buildAuthHeaders(fresh, impersonatedAs) });
      }
      if (!res.ok) {
        console.error(`Failed to load clients: HTTP ${res.status}`);
        return;
      }
      const data = await res.json();
      setAppointments(data.appointments || []);
      setBlockouts(data.blockouts || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to load clients:', err);
    } finally {
      setLoadingClients(false);
    }
  }, [dataSession, activeCenterId, session?.token, scheduleDate, today, opsToken, impersonatedAs, refreshOpsToken]);

  useEffect(() => {
    if (step === 'clients') loadClients();
  }, [step, loadClients]);

  // Clear any stale treatment-log save error when opening/closing a form.
  useEffect(() => { setTreatmentLogError(''); }, [expandedForm]);

  // Auto-refresh schedule every 30 seconds
  useEffect(() => {
    if (staffView !== 'clients' || !session) return;
    if (scheduleDate !== today) return; // don't poll past/future days
    const interval = setInterval(() => {
      if (step === 'clients') loadClients(true); // background — don't blank the list
    }, 30000);
    return () => clearInterval(interval);
  }, [staffView, step, session, loadClients, scheduleDate, today]);

  // Change-request status strip: refresh /mine on the schedule's existing
  // cadence (lastRefresh bumps on every loadClients pass — 30s poll, manual
  // Refresh, post-submit reload). NO new setInterval.
  useEffect(() => {
    if (staffView !== 'clients' || step !== 'clients' || !isTodaySchedule) return;
    loadCrMine();
    // loadCrMine is re-created per render (matches this file's handler style);
    // keying on lastRefresh gives exactly the schedule's refresh cadence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffView, step, isTodaySchedule, lastRefresh, opsToken]);

  // Deep link from a service-log reminder push: once the step machine has
  // naturally reached the schedule (login/geofence CANNOT be bypassed — we
  // only act on step==='clients') and today's list is loaded, open the
  // matching client's Forms & Logs with that treatment-log card expanded.
  // One-shot: consumed whether or not the appointment is found (it may be
  // another day's schedule by the time the tap happens — just land on the
  // schedule in that case).
  useEffect(() => {
    if (!logDeepLink) return;
    if (staffView !== 'clients') {
      // routeAfterAuth sent this role elsewhere (non-provider) — drop the link
      onLogDeepLinkConsumed?.();
      return;
    }
    if (step !== 'clients' || loadingClients) return;
    if (!appointments.length && !lastRefresh) return; // list not fetched yet
    onLogDeepLinkConsumed?.();
    const match = appointments.find(a =>
      a.appointmentId === logDeepLink.appointmentId
      || (a.serviceEntries || []).some(e => e.appointmentId === logDeepLink.appointmentId));
    if (!match) return;
    // Index into the SAME filtered list the forms step renders as `t-${i}`
    // (treatmentFormEntries: serviceEntries filtered by needsTreatmentLog).
    const logEntries = (match.serviceEntries || []).filter(e => e.needsTreatmentLog);
    // idx === -1 (e.g. serviceId mismatch) still opens Forms & Logs, just nothing pre-expanded
    const idx = logEntries.findIndex(e =>
      e.appointmentId === logDeepLink.appointmentId
      && (!e.serviceId || e.serviceId === logDeepLink.serviceId));
    (async () => {
      await selectClient(match);
      await goToForms(match);
      if (idx >= 0) { setEditingLogKey(null); setExpandedForm(`t-${idx}`); }
    })();
    // selectClient/goToForms are re-created per render; goToForms takes the client explicitly to avoid its stale closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logDeepLink, staffView, step, loadingClients, appointments, lastRefresh]);

  // Load today's staff actions (to show "already sent" badges)
  async function loadTodayActions() {
    if (!dataSession?.employeeId || !activeCenterId) return;
    try {
      const params = new URLSearchParams({ techId: dataSession.employeeId, centerId: activeCenterId });
      const res = await fetch(`${API_URL}/api/staff/actions-today?${params}`);
      const data = await res.json();
      setTodayActions(data.actions || []);
    } catch { setTodayActions([]); }
  }

  // Check if action already done for this guest today
  function getActionStatus(guestId, type) {
    return todayActions.find(a => a.guestZenotiId === guestId && a.type === type);
  }

  // Card quick actions → the same flows as the guest page's action cards.
  async function cardQuickAction(item, kind) {
    await selectClient(item);
    if (kind === 'log') await goToForms(item);
    else if (kind === 'recos') await goToRecommend();
    else setPendingAction(kind); // 'review' | 'referral' → the existing confirm modal
  }

  // Select a client — go to actions menu
  async function selectClient(appt) {
    setSelectedClient(appt);
    setSelectedItems([]);
    setActionMessage('');
    setPendingAction(null);
    setPastAppointments([]);
    setStep('actions');
    await loadTodayActions();
  }

  // Go to recommend screen (load items)
  async function goToRecommend() {
    setStaffTab('packages');
    setCurrentCategory(null);
    setCurrentSubCategory(null);
    setLoadingItems(true);
    setStep('recommend');
    try {
      const [pkgRes, prodRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/packages/${activeCenterId}`).then(r => r.json()).catch(() => ({ packages: [] })),
        fetch(`${API_URL}/api/products/${activeCenterId}`).then(r => r.json()).catch(() => ({ products: [] })),
        fetch(`${API_URL}/api/kiosk/centers/${activeCenterId}/full-catalog`).then(r => r.json()).catch(() => null)
      ]);
      setPackages(pkgRes.packages || []);
      setProducts(prodRes.products || []);
      setCatalog(catRes);
    } catch {
      setPackages([]);
      setProducts([]);
      setCatalog(null);
    } finally {
      setLoadingItems(false);
    }
  }

  // Send referral text
  async function sendReferralText() {
    if (!selectedClient || actionLoading) return;
    if (blockIfImpersonating()) return;
    setActionLoading(true);
    setActionMessage('');
    try {
      const res = await fetch(`${API_URL}/api/staff/send-referral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestId: selectedClient.guestId,
          guestName: selectedClient.guestName,
          centerId: activeCenterId,
          appointmentId: selectedClient.appointmentId,
          techId: session.employeeId,
          techName: session.employeeName
        })
      });
      const data = await res.json();
      if (data.success) {
        setStep('referral-sent');
      } else {
        setActionMessage(data.message || 'Could not send referral text.');
      }
      await loadTodayActions();
    } catch {
      setActionMessage('Connection error. Please try again.');
    } finally {
      setActionLoading(false);
    }
  }

  // Send review request
  async function sendReviewRequest() {
    if (!selectedClient || actionLoading) return;
    if (blockIfImpersonating()) return;
    setActionLoading(true);
    setActionMessage('');
    try {
      const res = await fetch(`${API_URL}/api/staff/send-review-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestId: selectedClient.guestId,
          guestName: selectedClient.guestName,
          centerId: activeCenterId,
          appointmentId: selectedClient.appointmentId,
          techId: session.employeeId,
          techName: session.employeeName
        })
      });
      const data = await res.json();
      if (data.success) {
        setStep('review-sent');
      } else {
        setActionMessage(data.message || 'Could not send review request.');
      }
      await loadTodayActions();
    } catch {
      setActionMessage('Connection error. Please try again.');
    } finally {
      setActionLoading(false);
    }
  }

  // ─── Modify Booking flow ───
  const [modifyMode, setModifyMode] = useState(null); // 'change' | 'add' | 'addon'
  const [addOns, setAddOns] = useState([]);
  const [modifyDoneMessage, setModifyDoneMessage] = useState('');

  async function loadCatalogIfNeeded() {
    if (modifyCatalog) return;
    setModifyLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/kiosk/centers/${activeCenterId}/full-catalog`);
      if (res.ok) setModifyCatalog(await res.json());
    } catch (err) {
      console.error('Load catalog error:', err);
    } finally {
      setModifyLoading(false);
    }
  }

  function goToModifyMenu() {
    setStep('modify-menu');
    setModifyService(null);
    setModifyCategory(null);
    setModifySearch('');
    setModifyMode(null);
    setActionMessage('');
  }

  async function markNoShow() {
    if (!selectedClient) return;
    if (blockIfImpersonating()) return;
    setNoShowLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/mark-no-show`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId: selectedClient.appointmentId,
          centerId: activeCenterId,
          employeeToken: session.token,
          guestName: selectedClient.guestName,
          employeeId: session.employeeId,
          employeeName: session.employeeName,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setActionMessage('Marked as no-show');
        loadClients();
        setTimeout(() => setStep('clients'), 1500);
      } else {
        setActionMessage(data.error || 'Failed to mark no-show');
      }
    } catch (err) {
      setActionMessage('Error marking no-show');
    } finally {
      setNoShowLoading(false);
      setPendingAction(null);
    }
  }

  async function goToForms(client = selectedClient) {
    if (!client) return;
    setStep('forms');
    setFormsLoading(true);
    setGuestForms([]);
    setTreatmentLogs([]);
    setSettingsHistory({});
    try {
      const groupId = client.appointmentGroupId || client.appointmentId;
      // Collect individual appointment IDs from service entries for service form fallback
      const indivIds = (client.serviceEntries || [])
        .map(s => s.appointmentId).filter(id => id && id !== groupId);
      const params = new URLSearchParams({ appointmentGroupId: groupId, guestId: client.guestId });
      if (indivIds.length > 0) params.set('appointmentIds', indivIds.join(','));
      const res = await fetch(`${API_URL}/api/staff/client-forms?${params}`);
      const data = await res.json();
      setGuestForms(data.forms || []);
      setGuestFormData(data.guestFormData || null);
    } catch (err) {
      console.error('Error loading forms:', err);
      setGuestForms([]);
    }
    setFormsLoading(false);

    // Load treatment log history (filled + unfilled) from our DB
    setTreatmentLogsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/treatment-log-history?guestId=${client.guestId}`);
      const data = await res.json();
      setTreatmentLogs(data.logs || []);
      // Index filled logs by appointmentId for quick lookup in current appointment forms
      const logMap = {};
      for (const log of (data.logs || [])) {
        if (log.filled && log.appointmentId) logMap[logKey(log.appointmentId, log.serviceId)] = log;
      }
      setTreatmentLogData(logMap);
    } catch (err) {
      console.error('Treatment log history error:', err);
    }
    setTreatmentLogsLoading(false);

    // Per-service settings history table (imported PDFs + structured logs)
    try {
      const r = await fetch(`${API_URL}/api/staff/treatment-log-settings-history?guestId=${client.guestId}`);
      const d = await r.json();
      setSettingsHistory(d.byService || {});
    } catch (err) {
      console.error('Settings history error:', err);
    }
  }

  // Cache key for a service's saved log. serviceId may be missing on legacy data;
  // fall back to appointmentId alone so old rows still resolve.
  const logKey = (appointmentId, serviceId) => serviceId ? `${appointmentId}|${serviceId}` : `${appointmentId}`;

  // logId set → edit-in-place (correcting an already-saved log); otherwise a
  // fresh fill. Editing is only offered while the log's day is still current.
  async function submitTreatmentLog(appointmentId, serviceId, serviceName, formData, logId = null) {
    if (!selectedClient || !session) return;
    if (blockIfImpersonating()) return;
    setTreatmentLogSaving(true);
    setTreatmentLogError('');
    try {
      const serviceType = getTreatmentLogType(serviceName);
      const res = await fetch(`${API_URL}/api/staff/treatment-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          zenotiAppointmentId: appointmentId,
          zenotiServiceId: serviceId || null,
          zenotiGuestId: selectedClient.guestId,
          employeeId: session.employeeId,
          centerId: activeCenterId,
          serviceType,
          serviceName,
          formData,
          ...(logId ? { logId } : {}),
        }),
      });
      const data = await res.json();
      if (data.success) {
        const savedId = data.id || logId || null;
        // Update the current-appointment forms map (surface A) so it re-renders
        // the viewer with the new data and keeps the row id for future edits.
        setTreatmentLogData(prev => ({ ...prev, [logKey(appointmentId, serviceId)]: { id: savedId, formData } }));
        // Update the matching row in the "Past Treatment Logs" list (surface B):
        // on edit, match by row id; on fresh fill, flip the first unfilled match.
        setTreatmentLogs(prev => prev.map(l => (
          l.appointmentId === appointmentId && ((serviceId && l.serviceId === serviceId) || (!serviceId && l.serviceName === serviceName)) && (logId ? l.id === logId : !l.filled)
            ? { ...l, id: savedId, filled: true, formData, createdAt: l.createdAt || new Date().toISOString() }
            : l
        )));
        setEditingLogKey(null);
        if (!logId) setExpandedForm(null); // fresh fill collapses; edit stays open to show the update
        setTreatmentLogError('');
        setActionMessage(logId ? 'Treatment log updated!' : 'Treatment log saved!');
        setTimeout(() => setActionMessage(''), 2000);
      } else {
        // Show the reason INLINE in the form — the step-gated actionMessage
        // isn't visible in every surface, which made failures read as
        // "nothing happens" (Yumeka's lash/brow log, 2026-07-09).
        setTreatmentLogError(data.error || 'Failed to save treatment log');
        setActionMessage(data.error || 'Failed to save treatment log');
      }
    } catch (err) {
      setTreatmentLogError('Could not save — check your connection and try again.');
      setActionMessage('Error saving treatment log');
    }
    setTreatmentLogSaving(false);
  }

  // Renders the right treatment-log form for a service type. Shared by the
  // fill and edit paths; pass existingData to pre-fill for an edit.
  function renderTreatmentForm(serviceName, { existingData = null, onSubmit }) {
    const logType = getTreatmentLogType(serviceName);
    const common = { onSubmit, saving: treatmentLogSaving, existingData };
    let form;
    if (logType === 'lash_lift') form = <LashLiftForm {...common} />;
    else if (logType === 'brow_lam') form = <BrowLamForm {...common} />;
    else if (logType === 'laser') form = <LaserForm {...common} />;
    else if (logType === 'tinting') form = <TintingForm {...common} />;
    else form = <div style={{ fontSize: 13, color: '#e65100' }}>Treatment log form for this service type coming soon.</div>;
    return (
      <>
        {treatmentLogError && (
          <div style={{ fontSize: 13, fontWeight: 600, color: '#c62828', background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
            {treatmentLogError}
          </div>
        )}
        {form}
      </>
    );
  }

  // Small link-style "Edit" / "Cancel" button used under a filled log's viewer.
  function editToggleBtn(label, onClick) {
    return (
      <button onClick={onClick} style={{
        marginTop: 10, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #d4899e',
        background: '#fff', color: '#d4899e', fontSize: 13, fontWeight: 600, cursor: 'pointer',
      }}>{label}</button>
    );
  }

  async function goToHistory() {
    if (!selectedClient) return;
    setStep('history');
    setPastLoading(true);
    setPastAppointments([]);
    try {
      const gId = selectedClient.guestZenotiId || selectedClient.guestId;
      const res = await fetch(`${API_URL}/api/staff/guest-history/${gId}?centerId=${activeCenterId}`);
      if (res.ok) {
        const data = await res.json();
        setPastAppointments(data.appointments || []);
      }
    } catch (err) {
      console.error('Past appointments error:', err);
    }
    setPastLoading(false);
  }

  function goToFeedback() {
    setFeedbackType('client_feedback');
    setFeedbackText('');
    setStep('feedback');
  }

  async function submitFeedback() {
    if (!feedbackText.trim() || !selectedClient || !session) return;
    if (blockIfImpersonating()) return;
    setFeedbackSaving(true);
    try {
      await fetch(`${API_URL}/api/staff/client-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestId: selectedClient.guestId,
          guestName: selectedClient.guestName,
          centerId: activeCenterId,
          employeeId: session.employeeId || session.therapistId,
          employeeName: session.employeeName,
          formType: feedbackType,
          description: feedbackText.trim(),
          appointmentId: selectedClient.appointmentId,
        }),
      });
      setStep('actions');
      setActionMessage('Feedback submitted');
      setTimeout(() => setActionMessage(''), 3000);
    } catch (err) {
      console.error('Feedback submit error:', err);
      alert('Failed to submit. Please try again.');
    }
    setFeedbackSaving(false);
  }

  async function goToNotes() {
    if (!selectedClient) return;
    setStep('notes');
    setNotesLoading(true);
    setClientNotes([]);
    setNewNote('');
    setNoteCategory('Lash and Brow');
    try {
      const res = await fetch(`${API_URL}/api/staff/client-notes/${selectedClient.guestId}`);
      const data = await res.json();
      setClientNotes(data.notes || []);
    } catch (err) {
      console.error('Error loading notes:', err);
    }
    setNotesLoading(false);
  }

  async function addNote() {
    if (!newNote.trim() || !selectedClient || !session) return;
    if (blockIfImpersonating()) return;
    setNoteSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/client-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestId: selectedClient.guestId,
          guestName: selectedClient.guestName,
          centerId: activeCenterId,
          techId: session.therapistId,
          techName: session.employeeName,
          note: newNote.trim(),
          category: noteCategory,
          appointmentId: selectedClient.appointmentId,
        }),
      });
      const data = await res.json();
      if (data.success && data.note) {
        setClientNotes(prev => [data.note, ...prev]);
        setNewNote('');
      }
    } catch (err) {
      console.error('Error adding note:', err);
    }
    setNoteSaving(false);
  }

  async function sendFormsText() {
    if (!selectedClient || !session) return;
    setPendingAction('forms');
  }

  async function confirmSendFormsText() {
    if (!selectedClient || !session) return;
    if (blockIfImpersonating()) return;
    setPendingAction(null);
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/send-forms-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestId: selectedClient.guestId,
          guestName: selectedClient.guestName,
          centerId: activeCenterId,
          appointmentId: selectedClient.appointmentId,
          techId: session.therapistId,
          techName: session.employeeName,
        }),
      });
      const data = await res.json();
      setActionMessage(data.message || (data.success ? 'Forms link sent!' : 'Failed to send.'));
      // Refresh today's actions so badge updates
      try {
        const actRes = await fetch(`${API_URL}/api/staff/actions-today?techId=${session.therapistId}&centerId=${activeCenterId}`);
        const actData = await actRes.json();
        setTodayActions(actData.actions || []);
      } catch {}
    } catch (err) {
      setActionMessage('Error sending forms text.');
    }
    setActionLoading(false);
    setTimeout(() => setActionMessage(''), 4000);
  }

  // Change Service flow
  async function goToChangeSelect() {
    setModifyMode('change');
    setStep('modify-select');
    await loadCatalogIfNeeded();
  }

  function selectServiceToReplace(svc) {
    setModifyService(svc);
    setModifyCategory(null);
    setModifySearch('');
    setStep('modify-pick');
  }

  async function confirmSwap(newService) {
    if (!selectedClient || !modifyService || modifyLoading) return;
    if (blockIfImpersonating()) return;
    setModifyLoading(true);
    setActionMessage('');
    try {
      const res = await fetch(`${API_URL}/api/staff/swap-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId: modifyService.appointmentId,
          oldServiceId: modifyService.serviceId,
          newServiceId: newService.id,
          centerId: activeCenterId,
          action: 'swap',
          oldServiceName: modifyService.serviceName,
          newServiceName: newService.display_name || newService.name,
          employeeId: session.employeeId,
          employeeName: session.employeeName,
          guestName: selectedClient.guestName,
          guestZenotiId: selectedClient.guestZenotiId,
          guestId: selectedClient.guestId,
          therapistId: session.employeeId,
          startTime: modifyService.startTime || selectedClient.startTime,
          invoiceId: selectedClient.invoiceId,
          isRequested: modifyService.isRequested || false,
          allServices: (selectedClient.serviceEntries || []).map(e => ({
            serviceId: e.serviceId, appointmentId: e.appointmentId,
            serviceName: e.serviceName, therapistId: e.therapistId,
            isRequested: e.isRequested || false,
          })),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setModifyDoneMessage(`Changed ${modifyService.serviceName} to ${newService.display_name || newService.name}`);
        setStep('modify-done');
        loadClients();
      } else {
        setActionMessage(data.message || 'Failed to swap service.');
      }
    } catch {
      setActionMessage('Connection error. Please try again.');
    } finally {
      setModifyLoading(false);
    }
  }

  // Add Service flow
  async function goToAddService() {
    setModifyMode('add');
    setModifyCategory(null);
    setModifySearch('');
    setStep('modify-pick');
    await loadCatalogIfNeeded();
  }

  async function confirmAddService(newService) {
    if (!selectedClient || modifyLoading) return;
    if (blockIfImpersonating()) return;
    setModifyLoading(true);
    setActionMessage('');
    try {
      const targetId = selectedClient.serviceEntries?.[0]?.appointmentId || selectedClient.appointmentId;
      const res = await fetch(`${API_URL}/api/staff/swap-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId: targetId,
          oldServiceId: null,
          newServiceId: newService.id,
          centerId: activeCenterId,
          action: 'add',
          newServiceName: newService.display_name || newService.name,
          employeeId: session.employeeId,
          employeeName: session.employeeName,
          guestName: selectedClient.guestName,
          guestZenotiId: selectedClient.guestZenotiId,
          guestId: selectedClient.guestId,
          therapistId: session.employeeId,
          startTime: selectedClient.startTime,
          invoiceId: selectedClient.invoiceId,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setModifyDoneMessage(`Added ${newService.display_name || newService.name} to booking`);
        setStep('modify-done');
        loadClients();
      } else {
        setActionMessage(data.message || 'Failed to add service.');
      }
    } catch {
      setActionMessage('Connection error. Please try again.');
    } finally {
      setModifyLoading(false);
    }
  }

  // Add-on flow
  async function goToAddOns() {
    setModifyMode('addon');
    setStep('modify-addons');
    setAddOns([]);
    setModifyLoading(true);
    try {
      // Get add-ons for the primary service
      const primaryDetail = (selectedClient.serviceDetails || [])[0];
      const primaryServiceId = primaryDetail?.serviceId;
      if (primaryServiceId) {
        const res = await fetch(`${API_URL}/api/kiosk/centers/${activeCenterId}/services/${primaryServiceId}/add-ons`);
        if (res.ok) {
          const data = await res.json();
          setAddOns(data.addOns || data || []);
        }
      }
      // If no add-ons from primary, fall back to catalog
      if (addOns.length === 0) {
        await loadCatalogIfNeeded();
      }
    } catch (err) {
      console.error('Load add-ons error:', err);
    } finally {
      setModifyLoading(false);
    }
  }

  async function confirmAddOn(addon) {
    if (!selectedClient || modifyLoading) return;
    if (blockIfImpersonating()) return;
    setModifyLoading(true);
    setActionMessage('');
    try {
      const targetId = selectedClient.serviceEntries?.[0]?.appointmentId || selectedClient.appointmentId;
      const res = await fetch(`${API_URL}/api/staff/swap-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId: targetId,
          oldServiceId: null,
          newServiceId: addon.id,
          centerId: activeCenterId,
          action: 'addon',
          newServiceName: addon.display_name || addon.name,
          employeeId: session.employeeId,
          employeeName: session.employeeName,
          guestName: selectedClient.guestName,
          guestZenotiId: selectedClient.guestZenotiId,
          guestId: selectedClient.guestId,
          therapistId: session.employeeId,
          startTime: selectedClient.startTime,
          invoiceId: selectedClient.invoiceId,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setModifyDoneMessage(`Added ${addon.display_name || addon.name} as add-on`);
        setStep('modify-done');
        loadClients();
      } else {
        setActionMessage(data.message || 'Failed to add add-on.');
      }
    } catch {
      setActionMessage('Connection error. Please try again.');
    } finally {
      setModifyLoading(false);
    }
  }

  // ──── Booking Corrections ("Fix booking") ────
  // Receptionless swap/add/remove on the staffer's own today appointment —
  // separate feature/backend from the legacy Modify Booking (ASPX) flow
  // above. See backend/routes/staff-corrections.js.
  function isTodayAppointment(appt) {
    if (!appt?.startTime) return false;
    // `today` converts a real instant (`new Date()`, an actual UTC moment) to
    // Pacific — that's a legitimate timezone conversion. `appt.startTime` is
    // NOT a real instant: it's Zenoti's/our DB's Pacific wall-clock string
    // with no timezone info attached, so running it through `new Date(...)`
    // + a SECOND timezone conversion double-converts and can land on the
    // wrong calendar day (see feedback_timezone_pattern.md). Read the digits
    // directly instead — the first 10 chars are already "YYYY-MM-DD" Pacific.
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Vancouver' });
    const apptDay = String(appt.startTime).slice(0, 10);
    return apptDay === today;
  }

  // Logs can be added/edited for the selected day if it's today or within the
  // last 7 days. Future days are view-only.
  function canLogForScheduleDate() {
    return scheduleOffset <= 0 && scheduleOffset >= -SCHEDULE_BACK_DAYS;
  }

  async function openCorrections() {
    if (!selectedClient) return;
    setCorrectionsOpen(true);
    setCorrectionsStep('lines');
    setCorrectionsBasket([]);
    setCorrectionsResults(null);
    setCorrectionsError('');
    setCorrectionsVisit(null);
    await loadCorrectionsVisit();
  }

  async function loadCorrectionsVisit() {
    if (!selectedClient) return;
    setCorrectionsLoading(true);
    setCorrectionsError('');
    try {
      const targetId = selectedClient.appointmentId;
      const res = await fetch(`${API_URL}/api/staff/corrections/visit?zenotiAppointmentId=${encodeURIComponent(targetId)}`, {
        headers: apiHeaders,
      });
      const data = await res.json();
      if (!res.ok) {
        setCorrectionsError(data.error || 'Could not load this visit.');
        setCorrectionsVisit(null);
      } else {
        setCorrectionsVisit(data);
      }
    } catch {
      setCorrectionsError('Connection error. Please try again.');
    } finally {
      setCorrectionsLoading(false);
    }
  }

  function closeCorrections() {
    setCorrectionsOpen(false);
    setCorrectionsStep('lines');
    setCorrectionsVisit(null);
    setCorrectionsBasket([]);
    setCorrectionsResults(null);
    setCorrectionsError('');
    setCorrectionsPickerMode(null);
    setCorrectionsReplaceTarget(null);
    setCorrectionsPendingService(null);
    setCorrectionsAddOnIds([]);
    setCorrectionsSearch('');
    setCorrectionsCategory(null);
  }

  // Overlay the pending basket onto the live invoice lines so the sheet
  // shows what the visit WILL look like, not just what GET /visit returned.
  function correctionsEffectiveLines() {
    if (!correctionsVisit) return [];
    let effective = correctionsVisit.lines.map(l => ({ ...l }));
    for (const change of correctionsBasket) {
      if (change.type === 'swap') {
        effective = effective.map(l => l.invoiceItemId === change.invoiceItemId
          ? {
            ...l,
            name: change.newServiceName,
            price: change.newServicePrice,
            _pendingLabel: change.addOnNames?.length ? `+ ${change.addOnNames.join(', ')}` : null,
          }
          : l);
      } else if (change.type === 'remove') {
        effective = effective.map(l => l.invoiceItemId === change.invoiceItemId ? { ...l, _removed: true } : l);
      } else if (change.type === 'add') {
        effective.push({
          invoiceItemId: `pending-add-${change.serviceId}`,
          serviceId: change.serviceId,
          name: change.serviceName,
          price: change.servicePrice,
          _pendingAdd: true,
          _pendingLabel: change.addOnNames?.length ? `+ ${change.addOnNames.join(', ')}` : null,
        });
      }
    }
    return effective;
  }

  // Plain-English basket summary, e.g. "Brazilian instead of French Bikini · + Hard Wax Upgrade · remove Underarms"
  function correctionsSummary() {
    if (correctionsBasket.length === 0) return '';
    return correctionsBasket.map(change => {
      if (change.type === 'swap') return `${change.newServiceName} instead of ${change.oldServiceName}`;
      if (change.type === 'add') return `+ ${change.serviceName}`;
      if (change.type === 'remove') return `remove ${change.serviceName}`;
      return '';
    }).filter(Boolean).join(' · ');
  }

  function startReplaceLine(line) {
    setCorrectionsPickerMode('replace');
    setCorrectionsReplaceTarget(line);
    setCorrectionsSearch('');
    setCorrectionsCategory(null);
    setCorrectionsStep('picker');
  }

  function startAddService() {
    setCorrectionsPickerMode('add');
    setCorrectionsReplaceTarget(null);
    setCorrectionsSearch('');
    setCorrectionsCategory(null);
    setCorrectionsStep('picker');
  }

  function toggleRemoveLine(line) {
    setCorrectionsBasket(prev => {
      const already = prev.find(c => c.type === 'remove' && c.invoiceItemId === line.invoiceItemId);
      if (already) return prev.filter(c => c !== already);
      // Removing a line supersedes any pending swap on it — don't send both.
      const withoutSwap = prev.filter(c => !(c.type === 'swap' && c.invoiceItemId === line.invoiceItemId));
      return [...withoutSwap, { type: 'remove', invoiceItemId: line.invoiceItemId, serviceName: line.name }];
    });
  }

  function undoBasketChange(change) {
    setCorrectionsBasket(prev => prev.filter(c => c !== change));
  }

  function pickCorrectionsService(svc) {
    if ((svc.addOns || []).length > 0) {
      setCorrectionsPendingService(svc);
      setCorrectionsAddOnIds([]);
      setCorrectionsStep('addons');
    } else {
      commitCorrectionsService(svc, []);
    }
  }

  function toggleCorrectionsAddOn(id) {
    setCorrectionsAddOnIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function commitCorrectionsService(svc, addOnIds) {
    const addOnNames = (svc.addOns || []).filter(a => addOnIds.includes(a.id)).map(a => a.name);
    if (correctionsPickerMode === 'replace' && correctionsReplaceTarget) {
      setCorrectionsBasket(prev => ([
        ...prev.filter(c => !(c.type === 'swap' && c.invoiceItemId === correctionsReplaceTarget.invoiceItemId)),
        {
          type: 'swap',
          invoiceItemId: correctionsReplaceTarget.invoiceItemId,
          newServiceId: svc.id,
          addOnIds,
          oldServiceName: correctionsReplaceTarget.name,
          newServiceName: svc.name,
          newServicePrice: svc.price,
          addOnNames,
        },
      ]));
    } else {
      setCorrectionsBasket(prev => [...prev, {
        type: 'add',
        serviceId: svc.id,
        addOnIds,
        serviceName: svc.name,
        servicePrice: svc.price,
        addOnNames,
      }]);
    }
    setCorrectionsPickerMode(null);
    setCorrectionsReplaceTarget(null);
    setCorrectionsPendingService(null);
    setCorrectionsAddOnIds([]);
    setCorrectionsStep('lines');
  }

  async function submitCorrections() {
    if (blockIfImpersonating()) return;
    if (!correctionsVisit || correctionsBasket.length === 0 || correctionsSubmitting) return;
    setCorrectionsSubmitting(true);
    setCorrectionsError('');
    try {
      const changes = correctionsBasket.map(c => {
        if (c.type === 'swap') return { type: 'swap', invoiceItemId: c.invoiceItemId, newServiceId: c.newServiceId, addOnIds: c.addOnIds };
        if (c.type === 'add') return { type: 'add', serviceId: c.serviceId, addOnIds: c.addOnIds };
        return { type: 'remove', invoiceItemId: c.invoiceItemId };
      });
      const res = await fetch(`${API_URL}/api/staff/corrections/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...apiHeaders },
        body: JSON.stringify({ zenotiAppointmentId: correctionsVisit.zenotiAppointmentId, changes }),
      });
      const data = await res.json();
      if (!res.ok) {
        setCorrectionsError(data.error || 'Failed to apply corrections.');
        return;
      }
      setCorrectionsResults(data.results || []);
      setCorrectionsStep('results');
      loadClients();
    } catch {
      // C2: a correction batch can take a minute or two and involves several
      // Zenoti writes — a fetch failure here (timeout, dropped connection)
      // does NOT mean nothing happened server-side. A plain "try again"
      // risks a double-submit; point staff at checking the visit first.
      setCorrectionsError('The correction may still be processing — check the visit before retrying.');
    } finally {
      setCorrectionsSubmitting(false);
    }
  }

  // ──── Appointment change requests ("Modify" on the schedule card) ────
  // Guest-approved changes routed through POST /api/staff/change-requests —
  // NEVER through the corrections /apply live-write endpoint. Change payload
  // construction mirrors commitCorrectionsService/submitCorrections exactly.

  // Checked-in gate for the card entry point. Both /my-appointments paths
  // emit the same status-label vocabulary: the DB path maps the mirror int
  // (3 = checked-in) to 'Checked-in' via lib/db-appointment-cards.js
  // STATUS_LABELS, and the live path adds 'Started' once service begins
  // (guest checked in AND on the table — exactly who this flow is for).
  // Backend guard 0 re-checks the mirror int authoritatively on every call.
  function crCardStage(card) {
    const list = (card.statuses || [card.status]).filter(Boolean);
    if (list.includes('Checked-in') || list.includes('Started')) return 'checked-in';
    if (list.includes('Completed') || list.includes('Closed')) return 'done';
    return 'pre'; // booked/confirmed — hint chip territory
  }

  function crResetPicker() {
    setCrPickerMode(null);
    setCrReplaceTarget(null);
    setCrPendingService(null);
    setCrAddOnIds([]);
    setCrSearch('');
    setCrAttachTarget(null);
  }

  // "Sent" labels on the card quick actions need today's actions loaded with
  // the schedule (previously only loaded on opening a guest). Backend/DB only.
  useEffect(() => {
    if (staffView === 'clients' && step === 'clients' && appointments.length > 0) loadTodayActions();
    // loadTodayActions is re-created per render; keyed on the list instead
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffView, step, appointments, activeCenterId]);

  // Visit preload — GET /visit waits on a throttled live Zenoti invoice read
  // (several seconds), so checked-in cards preload it in the background and
  // Modify opens instantly from the cache. Each appointment preloads ONCE per
  // page session (schedule refreshes don't re-fetch — Zenoti's 60/min quota);
  // a tap opens straight from the cached copy (no extra Zenoti read). Submit
  // re-reads the invoice server-side, so a slightly stale copy can't apply
  // anything wrong. Entries: appointmentId → { data } | { promise }.
  const crVisitCacheRef = useRef(new Map());
  const fetchCrVisit = useCallback(async (appointmentId) => {
    const res = await fetch(`${API_URL}/api/staff/change-requests/visit?zenotiAppointmentId=${encodeURIComponent(appointmentId)}`, {
      headers: apiHeaders,
    });
    const data = await res.json();
    if (res.ok) crVisitCacheRef.current.set(appointmentId, { data, at: Date.now() });
    else crVisitCacheRef.current.delete(appointmentId); // e.g. not-yet-checked-in 409 — retry on tap
    return { ok: res.ok, data };
  }, [apiHeaders]);

  useEffect(() => {
    if (!isTodaySchedule || !canModule(effSession, 'change_requests')) return undefined;
    const ids = appointments
      .filter(a => crCardStage(a) === 'checked-in' && a.appointmentId && !crVisitCacheRef.current.has(a.appointmentId))
      .map(a => a.appointmentId);
    if (ids.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      for (const id of ids) { // one at a time — the backend queue + z() pace Zenoti
        if (cancelled) return;
        if (crVisitCacheRef.current.has(id)) continue;
        const promise = fetchCrVisit(id).catch(() => { crVisitCacheRef.current.delete(id); });
        crVisitCacheRef.current.set(id, { promise });
        await promise;
      }
    })();
    return () => { cancelled = true; };
    // crCardStage is a pure helper; effSession only gates the module check
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments, isTodaySchedule, fetchCrVisit]);

  // Fresh-invoice adoption: take the new lines unless they'd orphan a picked
  // change (a swap/remove whose invoice line no longer exists) — then keep the
  // old copy; submit re-validates server-side and will say what changed.
  const crRefreshRef = useRef(null);
  function crAdoptFresh(prev, fresh) {
    if (!prev || prev.zenotiAppointmentId !== fresh.zenotiAppointmentId) return prev;
    const ids = new Set((fresh.lines || []).map(l => l.invoiceItemId));
    const orphaned = crBasketRef.current.some(c => c.invoiceItemId && !ids.has(c.invoiceItemId));
    return orphaned ? prev : fresh;
  }

  async function openChangeRequest(card) {
    setCrOpen(true);
    setCrTarget(card);
    setCrStep('edit');
    setCrBasket([]);
    setCrVisit(null);
    setCrError('');
    setCrNote('');
    setCrResult(null);
    crResetPicker();
    crKeyRef.current = null;
    const cached = crVisitCacheRef.current.get(card.appointmentId);
    if (cached?.data) {
      // Instant open from the preload. If the copy is older than 30s, re-read
      // in the background: the desk may have changed the invoice since (live
      // 10/01: an upper lip the desk had added was missing from the provider's
      // next request and price check). Price check waits on this refresh
      // (crGoConfirm), so the quoted price is always current.
      setCrVisit(cached.data);
      setCrLoading(false);
      if (Date.now() - (cached.at || 0) > CR_VISIT_FRESH_MS) {
        const refresh = fetchCrVisit(card.appointmentId).then(({ ok, data }) => {
          if (ok) setCrVisit(prev => crAdoptFresh(prev, data));
        }).catch(() => {});
        crRefreshRef.current = refresh;
        refresh.finally(() => { if (crRefreshRef.current === refresh) crRefreshRef.current = null; });
      }
      return;
    }
    setCrLoading(true);
    try {
      // A preload already in flight for this card — wait on it, not a 2nd call.
      const { ok, data } = cached?.promise
        ? await cached.promise.then(r => r || fetchCrVisit(card.appointmentId))
        : await fetchCrVisit(card.appointmentId);
      if (!ok) {
        // Surfaces the center-not-enabled 403 and not-checked-in 409
        // messages as friendly inline text.
        setCrError(data.error || 'Could not load this visit.');
        setCrVisit(null);
      } else {
        setCrVisit(data);
      }
    } catch {
      setCrError('Connection error. Please try again.');
    } finally {
      setCrLoading(false);
    }
  }

  function closeChangeRequest() {
    setCrOpen(false);
    setCrStep('edit');
    setCrVisit(null);
    setCrTarget(null);
    setCrBasket([]);
    setCrError('');
    setCrNote('');
    setCrResult(null);
    crResetPicker();
    crKeyRef.current = null;
  }

  // Overlay the pending basket onto the invoice lines — same idea as
  // correctionsEffectiveLines, but keeps BOOKED rows visually distinct from
  // this session's NEW/changed rows per the approved mockup.
  function crEffectiveLines() {
    if (!crVisit) return [];
    let effective = crVisit.lines.map(l => ({ ...l, _booked: true }));
    for (const change of crBasket) {
      if (change.type === 'swap') {
        effective = effective.map(l => l.invoiceItemId === change.invoiceItemId
          ? { ...l, _swap: change, _pendingLabel: change.addOnNames?.length ? `+ ${change.addOnNames.join(', ')}` : null }
          : l);
      } else if (change.type === 'remove') {
        effective = effective.map(l => l.invoiceItemId === change.invoiceItemId ? { ...l, _remove: change } : l);
      } else if (change.type === 'add') {
        effective.push({
          invoiceItemId: `pending-add-${change.serviceId}`,
          serviceId: change.serviceId,
          name: change.serviceName,
          price: crPrice.svcById.get(change.serviceId)?.price || 0,
          _add: change,
          _pendingLabel: change.addOnNames?.length ? `+ ${change.addOnNames.join(', ')}` : null,
        });
      }
    }
    return effective;
  }

  // Running summary + price delta from the capability catalog. DISPLAY ONLY —
  // the server recomputes both authoritatively at submit (guards.js
  // computeTotals); this just tells the esthetician what to quote.
  function crTotals() {
    if (!crVisit) return { changes: 0, minutes: 0, delta: 0, newTotal: 0 };
    const { svcById, aoById } = crPrice;
    const aoPrice = (id) => aoById.get(id)?.price || 0;
    let delta = 0;
    let minutes = 0;
    for (const c of crBasket) {
      if (c.type === 'add') {
        const s = svcById.get(c.serviceId);
        delta += s?.price || 0;
        minutes += s?.duration || 0;
        for (const a of c.addOnIds || []) delta += aoPrice(a);
      } else if (c.type === 'swap') {
        const line = crVisit.lines.find(l => l.invoiceItemId === c.invoiceItemId);
        const target = svcById.get(c.newServiceId);
        delta += (target?.price || 0) - (line?.price || 0);
        const oldDur = svcById.get(line?.serviceId)?.duration || 0;
        minutes += Math.max(0, (target?.duration || 0) - oldDur);
        for (const a of c.addOnIds || []) delta += aoPrice(a);
      } else if (c.type === 'remove') {
        const line = crVisit.lines.find(l => l.invoiceItemId === c.invoiceItemId);
        delta -= line?.price || 0;
      }
    }
    const base = (crVisit.lines || []).reduce((sum, l) => sum + (l.price || 0), 0);
    delta = Math.round(delta * 100) / 100;
    return { changes: crBasket.length, minutes, delta, newTotal: Math.round((base + delta) * 100) / 100 };
  }

  // Basket invariant, enforced in ONE place: at most ONE basket entry per
  // invoiceItemId. A new swap replaces a prior swap on that line; a remove
  // supersedes a prior swap; an attach edit passes the line's updated swap
  // back through here and lands in its place (position preserved so the
  // confirm summary doesn't reorder). Adds carry no invoiceItemId and always
  // append. Every basket mutation that targets an invoice line goes through
  // this helper — never push line-targeting changes into crBasket directly.
  function crMergeChange(basket, change) {
    if (!change.invoiceItemId) return [...basket, change];
    const idx = basket.findIndex(c => c.invoiceItemId === change.invoiceItemId);
    if (idx === -1) return [...basket, change];
    return basket.map((c, i) => (i === idx ? change : c));
  }

  function crStartReplace(line) {
    setCrPickerMode('replace');
    setCrReplaceTarget(line);
    setCrSearch('');
    setCrStep('picker');
  }

  function crStartAdd() {
    setCrPickerMode('add');
    setCrReplaceTarget(null);
    setCrSearch('');
    setCrStep('picker');
  }

  function crPickService(svc) {
    if ((svc.addOns || []).length > 0) {
      setCrPendingService(svc);
      setCrAddOnIds([]);
      setCrStep('addons');
    } else {
      crCommitService(svc, []);
    }
  }

  function crToggleAddOn(id) {
    setCrAddOnIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  // Identical change construction to the corrections flow
  // (commitCorrectionsService): swap = { type:'swap', invoiceItemId,
  // newServiceId, addOnIds }; add = { type:'add', serviceId, addOnIds }.
  function crCommitService(svc, addOnIds) {
    const addOnNames = (svc.addOns || []).filter(a => addOnIds.includes(a.id)).map(a => a.name);
    if (crPickerMode === 'replace' && crReplaceTarget) {
      setCrBasket(prev => crMergeChange(prev, {
        type: 'swap',
        invoiceItemId: crReplaceTarget.invoiceItemId,
        newServiceId: svc.id,
        addOnIds,
        oldServiceName: crReplaceTarget.name,
        newServiceName: svc.name,
        addOnNames,
      }));
    } else {
      setCrBasket(prev => [...prev, { type: 'add', serviceId: svc.id, addOnIds, serviceName: svc.name, addOnNames }]);
    }
    crResetPicker();
    setCrStep('edit');
  }

  // Removing a line supersedes any pending swap on it (crMergeChange) —
  // same rule as the corrections toggleRemoveLine. Tapping Remove on an
  // already-removed line undoes it.
  function crToggleRemove(line) {
    setCrBasket(prev => {
      const already = prev.find(c => c.type === 'remove' && c.invoiceItemId === line.invoiceItemId);
      if (already) return prev.filter(c => c !== already);
      return crMergeChange(prev, { type: 'remove', invoiceItemId: line.invoiceItemId, serviceName: line.name });
    });
  }

  function crUndo(change) {
    setCrBasket(prev => prev.filter(c => c !== change));
  }

  // "+ Add add-on" targets — v1 simplification: the add-on rides on a
  // service chosen from booked lines + this session's added/changed
  // services. A BOOKED line carrying add-ons becomes a same-service swap
  // ({ type:'swap', invoiceItemId, newServiceId: <same serviceId>, addOnIds })
  // — the exact payload the corrections flow already sends when a line is
  // "replaced" by its own service plus add-ons.
  function crAttachTargets() {
    if (!crVisit) return [];
    const { svcById } = crPrice;
    const targets = [];
    for (const line of crVisit.lines) {
      if (crBasket.some(c => c.type === 'remove' && c.invoiceItemId === line.invoiceItemId)) continue;
      const swap = crBasket.find(c => c.type === 'swap' && c.invoiceItemId === line.invoiceItemId);
      const svc = svcById.get(swap ? swap.newServiceId : line.serviceId);
      if (!svc || (svc.addOns || []).length === 0) continue; // can't price add-ons we can't see
      targets.push({ key: `line-${line.invoiceItemId}`, name: swap ? swap.newServiceName : line.name, svc, line, swap: swap || null });
    }
    crBasket.forEach((c, i) => {
      if (c.type !== 'add') return;
      const svc = svcById.get(c.serviceId);
      if (!svc || (svc.addOns || []).length === 0) return;
      targets.push({ key: `add-${i}`, name: c.serviceName, svc, addChange: c });
    });
    return targets;
  }

  function crPickAttachTarget(t) {
    setCrAttachTarget(t);
    setCrAddOnIds(t.swap ? [...(t.swap.addOnIds || [])] : t.addChange ? [...(t.addChange.addOnIds || [])] : []);
    setCrStep('attach-pick');
  }

  function crCommitAttach() {
    const t = crAttachTarget;
    if (!t) return;
    const addOnNames = (t.svc.addOns || []).filter(a => crAddOnIds.includes(a.id)).map(a => a.name);
    const ids = [...crAddOnIds];
    if (t.addChange) {
      // Add changes carry no invoiceItemId (not line-targeting), so this is
      // an in-place edit of the pending add rather than a crMergeChange.
      setCrBasket(prev => prev.map(c => c === t.addChange ? { ...c, addOnIds: ids, addOnNames } : c));
    } else if (t.swap) {
      setCrBasket(prev => {
        // De-selecting every add-on on a same-service swap leaves a no-op
        // change — drop it entirely rather than sending swap-to-itself.
        if (t.swap.sameService && ids.length === 0) return prev.filter(c => c !== t.swap);
        return crMergeChange(prev, { ...t.swap, addOnIds: ids, addOnNames });
      });
    } else if (ids.length > 0) {
      setCrBasket(prev => crMergeChange(prev, {
        type: 'swap',
        invoiceItemId: t.line.invoiceItemId,
        newServiceId: t.line.serviceId, // same service — add-ons ride on it
        addOnIds: ids,
        oldServiceName: t.line.name,
        newServiceName: t.line.name,
        addOnNames,
        sameService: true,
      }));
    }
    crResetPicker();
    setCrStep('edit');
  }

  async function crGoConfirm() {
    if (crBasket.length === 0) return;
    // Never quote a price off a stale invoice — finish any in-flight refresh.
    if (crRefreshRef.current) {
      setCrLoading(true);
      try { await crRefreshRef.current; } finally { setCrLoading(false); }
    }
    crKeyRef.current = (window.crypto?.randomUUID) ? window.crypto.randomUUID() : `cr-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setCrError('');
    setCrStep('confirm');
  }

  function crBackToEdit() {
    crKeyRef.current = null; // edited request = new submission → new key next confirm
    setCrError('');
    setCrStep('edit');
  }

  async function submitChangeRequest() {
    if (blockIfImpersonating()) return;
    if (!crVisit || crBasket.length === 0 || crSubmitting) return;
    setCrSubmitting(true);
    setCrError('');
    try {
      // Wire payload — strictly the corrections shapes, nothing extra.
      const changes = crBasket.map(c => {
        if (c.type === 'swap') return { type: 'swap', invoiceItemId: c.invoiceItemId, newServiceId: c.newServiceId, addOnIds: c.addOnIds || [] };
        if (c.type === 'add') return { type: 'add', serviceId: c.serviceId, addOnIds: c.addOnIds || [] };
        return { type: 'remove', invoiceItemId: c.invoiceItemId };
      });
      // Dev tripwire for the crMergeChange invariant (one change per invoice
      // line) — the backend also rejects duplicates with a 400.
      const itemIds = changes.map(c => c.invoiceItemId).filter(Boolean);
      console.assert(new Set(itemIds).size === itemIds.length, '[CR] duplicate invoiceItemId in basket');
      const idempotencyKey = crKeyRef.current || (crKeyRef.current = (window.crypto?.randomUUID) ? window.crypto.randomUUID() : `cr-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const res = await fetch(`${API_URL}/api/staff/change-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...apiHeaders },
        body: JSON.stringify({
          zenotiAppointmentId: crVisit.zenotiAppointmentId,
          changes,
          note: crNote.trim() || undefined,
          idempotencyKey,
          guestApproved: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // Stay on confirm — the key is unchanged, so a retry of THIS
        // confirm screen dedupes server-side instead of double-submitting.
        setCrError(data.error || 'Could not send the change — try again.');
        return;
      }
      // applied:true = auto-applied; deduped rows report via their status.
      const applied = data.applied === true || data.request?.status === 'auto_applied';
      setCrResult(applied ? 'applied' : 'sent');
      setCrStep('done');
      crKeyRef.current = null;
      crVisitCacheRef.current.delete(crVisit.zenotiAppointmentId); // invoice is changing — next Modify reloads
      loadCrMine();
      loadClients(true);
    } catch {
      setCrError('Connection issue — check "My change requests" on your schedule before retrying.');
    } finally {
      setCrSubmitting(false);
    }
  }

  // Status strip data. Refreshes piggyback on the schedule's existing
  // refresh cadence (the 30s loadClients poll updates lastRefresh, which
  // the effect below watches) — no new interval.
  async function loadCrMine() {
    if (!opsToken || !canModule(effSession, 'change_requests')) return;
    try {
      const res = await fetch(`${API_URL}/api/staff/change-requests/mine`, { headers: apiHeaders });
      if (!res.ok) return;
      const data = await res.json();
      setCrMine(Array.isArray(data.requests) ? data.requests : []);
    } catch { /* keep the last list — transient network issue */ }
  }

  // Toggle item selection
  function toggleItem(item, type) {
    setSelectedItems(prev => {
      const key = `${type}-${item.id}`;
      const exists = prev.find(p => `${p.type}-${p.id}` === key);
      if (exists) return prev.filter(p => `${p.type}-${p.id}` !== key);
      return [...prev, {
        id: item.id,
        name: item.display_name || item.name || item.displayName || '',
        price: getItemPrice(item, type),
        description: item.description || '',
        type,
        image: item.image || null
      }];
    });
  }

  // Check if item is selected
  function isSelected(id, type) {
    return selectedItems.some(p => p.id === id && p.type === type);
  }

  // Get selected item (for discount access)
  function getSelectedItem(id, type) {
    return selectedItems.find(p => p.id === id && p.type === type);
  }

  // Add discount to item
  function addDiscount(id, type) {
    setSelectedItems(prev => prev.map(item =>
      item.id === id && item.type === type
        ? { ...item, discount: { code: '', label: '' } }
        : item
    ));
  }

  // Remove discount from item
  function removeDiscount(id, type) {
    setSelectedItems(prev => prev.map(item =>
      item.id === id && item.type === type
        ? { ...item, discount: undefined }
        : item
    ));
  }

  // Update discount field
  function updateDiscount(id, type, field, value) {
    setSelectedItems(prev => prev.map(item =>
      item.id === id && item.type === type
        ? { ...item, discount: { ...item.discount, [field]: value } }
        : item
    ));
  }

  // Counts by type
  const pkgCount = selectedItems.filter(i => i.type === 'package').length;
  const prodCount = selectedItems.filter(i => i.type === 'product').length;
  const svcCount = selectedItems.filter(i => i.type === 'service').length;
  const totalCount = selectedItems.length;

  // Build bottom bar label
  function getBottomLabel() {
    const parts = [];
    if (pkgCount) parts.push(`${pkgCount} pkg${pkgCount > 1 ? 's' : ''}`);
    if (prodCount) parts.push(`${prodCount} product${prodCount > 1 ? 's' : ''}`);
    if (svcCount) parts.push(`${svcCount} svc${svcCount > 1 ? 's' : ''}`);
    return parts.join(' + ');
  }

  // Save recommendations
  async function saveRecommendation() {
    if (!selectedItems.length || !selectedClient) return;
    if (blockIfImpersonating()) return;
    setSaving(true);
    try {
      // Clean up discount objects — remove if both fields empty
      const cleaned = selectedItems.map(item => {
        if (item.discount && !item.discount.code && !item.discount.label) {
          const { discount, ...rest } = item;
          return rest;
        }
        return item;
      });
      await fetch(`${API_URL}/api/staff/recommend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestId: selectedClient.guestId,
          centerId: activeCenterId,
          appointmentId: selectedClient.appointmentId,
          techId: session.employeeId,
          techName: session.employeeName,
          packages: cleaned
        })
      });
      setStep('saved');
    } catch {
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // Sign out
  function handleSignOut() {
    clearSession();
    setSession(null);
    setOpsToken(null);
    setStep('login');
    if (onLayoutSignOut) onLayoutSignOut();
  }
  // Expose signOut to Layout via ref
  if (signOutRef) signOutRef.current = handleSignOut;

  // ---------- Category helpers ----------

  // Derive package category from name (Zenoti Catalog API doesn't return categories)
  function getPackageCategory(name) {
    const n = (name || '').toLowerCase();
    if (n.startsWith('laser')) return 'Laser';
    if (n.includes('lash lift') || n.includes('inlei')) return 'Lash Lift';
    if (n.includes('brow lam')) return 'Brow Lamination';
    if (n.includes('tint')) return 'Tinting';
    if (n.includes('spray tan')) return 'Spray Tan';
    if (n.includes('wax') || n.includes('brazilian')) return 'Waxing';
    if (n.includes('facial')) return 'Facial';
    return 'Other';
  }

  // Group packages by derived category
  function getPackageCategories() {
    const catMap = {};
    for (const p of packages) {
      const catName = getPackageCategory(p.name);
      if (!catMap[catName]) catMap[catName] = { id: catName, name: catName, count: 0 };
      catMap[catName].count++;
    }
    const cats = Object.values(catMap).sort((a, b) => a.name.localeCompare(b.name));
    return cats.length <= 1 ? [] : cats;
  }

  // Laser subcategories: 6-Pack vs 8-Pack
  function getPackageSubCategories(catId) {
    if (catId !== 'Laser') return [];
    const laserPkgs = packages.filter(p => getPackageCategory(p.name) === 'Laser');
    const subMap = {};
    for (const p of laserPkgs) {
      const n = p.name || '';
      const sub = /8.?pack/i.test(n) ? '8-Pack' : /6.?pack/i.test(n) ? '6-Pack' : 'Other';
      if (!subMap[sub]) subMap[sub] = { id: sub, name: sub, count: 0 };
      subMap[sub].count++;
    }
    const subs = Object.values(subMap).sort((a, b) => a.name.localeCompare(b.name));
    return subs.length <= 1 ? [] : subs;
  }

  // Product subcategories (backend already filters to retail only)
  function getProductSubCategories() {
    const catMap = {};
    for (const p of products) {
      const catId = p.subCategoryId || '__uncategorized';
      const catName = p.subCategoryName || 'Other';
      if (!catMap[catId]) catMap[catId] = { id: catId, name: catName, count: 0 };
      catMap[catId].count++;
    }
    return Object.values(catMap);
  }

  // Service categories from catalog
  function getServiceCategories() {
    if (!catalog?.categories) return [];
    return catalog.categories
      .filter(cat => {
        const svcs = catalog.services?.[cat.id];
        return svcs && svcs.length > 0;
      })
      .map(cat => ({
        id: cat.id,
        name: cat.name,
        count: catalog.services[cat.id]?.length || 0
      }));
  }

  // Service subcategories within a category
  function getServiceSubCategories(catId) {
    const svcs = catalog?.services?.[catId] || [];
    const subMap = {};
    for (const s of svcs) {
      const subName = s.sub_category_name || 'Other';
      if (!subMap[subName]) subMap[subName] = { id: subName, name: subName, count: 0 };
      subMap[subName].count++;
    }
    const subs = Object.values(subMap);
    return subs.length <= 1 ? [] : subs; // no drill-down if only one subcategory
  }

  // Get items for current tab + category
  function getCurrentItems() {
    if (staffTab === 'packages') {
      if (getPackageCategories().length > 0 && !currentCategory) return [];
      if (currentCategory) {
        const catPkgs = packages.filter(p => getPackageCategory(p.name) === currentCategory);
        const subs = getPackageSubCategories(currentCategory);
        if (subs.length > 1 && !currentSubCategory) return []; // show subcategory drill-down
        if (currentSubCategory) {
          return catPkgs.filter(p => {
            const n = p.name || '';
            const sub = /8.?pack/i.test(n) ? '8-Pack' : /6.?pack/i.test(n) ? '6-Pack' : 'Other';
            return sub === currentSubCategory;
          });
        }
        return catPkgs;
      }
      return packages; // flat if no categories
    }
    if (staffTab === 'products') {
      const subCats = getProductSubCategories();
      if (subCats.length > 1 && !currentCategory) return []; // show subcategory drill-down
      if (currentCategory) return products.filter(p => (p.subCategoryId || '__uncategorized') === currentCategory);
      return products; // flat if no subcategories
    }
    if (staffTab === 'services') {
      if (!currentCategory) return [];
      const catServices = catalog?.services?.[currentCategory] || [];
      const subs = getServiceSubCategories(currentCategory);
      if (subs.length > 1 && !currentSubCategory) return []; // show subcategory drill-down
      if (currentSubCategory) return catServices.filter(s => (s.sub_category_name || 'Other') === currentSubCategory);
      return catServices;
    }
    return [];
  }

  // Should show category drill-down?
  function shouldShowCategories() {
    if (staffTab === 'packages') return getPackageCategories().length > 0 && !currentCategory;
    if (staffTab === 'products') return getProductSubCategories().length > 1 && !currentCategory;
    if (staffTab === 'services') return !currentCategory;
    return false;
  }

  // Get price from various field structures — for services, use therapist-specific price
  function getItemPrice(item, type) {
    const basePrice = item.price || item.price_info?.sale_price || item.price_info?.price || 0;
    if (type === 'service' && session?.employeeId && catalog?.therapists) {
      const serviceTherapists = catalog.therapists[item.id] || [];
      const match = serviceTherapists.find(t => t.Id === session.employeeId);
      if (match?.Price != null) return match.Price;
    }
    return basePrice;
  }

  // Render a single selectable item card
  function renderItemCard(item, type) {
    const selected = isSelected(item.id, type);
    const sel = getSelectedItem(item.id, type);
    const price = getItemPrice(item, type);
    return (
      <div key={`${type}-${item.id}`} className="staff-item-wrap">
        <button
          className={`staff-package-card ${selected ? 'selected' : ''}`}
          onClick={() => toggleItem(item, type)}
        >
          <div className="staff-package-check">{selected ? '✓' : ''}</div>
          {type === 'product' && item.image && (
            <img src={item.image.startsWith('http') ? item.image : `${API_URL}${item.image}`} alt="" className="staff-prod-thumb" />
          )}
          <div className="staff-package-info">
            <div className="staff-package-name">{item.display_name || item.name || item.displayName || ''}</div>
            {type === 'package' && item.services?.length > 0 && (
              <div className="staff-package-services">
                {item.services.map(s => s.name).join(', ')}
              </div>
            )}
            {type !== 'package' && item.description && (
              <div className="staff-package-services">{item.description}</div>
            )}
          </div>
          <div className="staff-package-price">
            ${price.toFixed(2)}
          </div>
        </button>
        {/* Discount code section */}
        {selected && sel?.discount && (
          <div className="staff-discount-row">
            <input
              className="staff-discount-code"
              type="text"
              placeholder="Coupon code"
              value={sel.discount.code || ''}
              onChange={e => updateDiscount(item.id, type, 'code', e.target.value)}
            />
            <button className="staff-discount-remove" onClick={() => removeDiscount(item.id, type)}>✕</button>
            <input
              className="staff-discount-label"
              type="text"
              placeholder="Label shown to client (e.g. 10% off)"
              value={sel.discount.label || ''}
              onChange={e => updateDiscount(item.id, type, 'label', e.target.value)}
            />
          </div>
        )}
        {selected && !sel?.discount && (
          <button className="staff-discount-add" onClick={() => addDiscount(item.id, type)}>
            + Add Discount Code
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="staff-container">
      {/* Clock-in badge — floating indicator when clocked in */}
      {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && step !== 'clock-in' && clockStatus?.clockedIn && !clockStatus?.clockedOut && (
        <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="clock-status-badge" onClick={() => { setClockError(''); setStaffView('time_clock'); }}>
            &#128336; {formatStoredTime(clockStatus.clockInTime)}
          </button>
        </div>
      )}

      {/* Lazy-loaded views: only mount when first visited, then keep mounted with display:none */}
      <Suspense fallback={<TabLoading />}>
        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'tasks') && (
          <LazyTab active={staffView === 'tasks'}>
            <StaffTasks session={dataSession} opsToken={opsToken} opsRole={dataSession?.effectiveRole || session?.opsRole} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'tasks') && (
          <LazyTab active={staffView === 'task_calendar'}>
            <TaskCalendar session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'hr') && (
          <LazyTab active={staffView === 'hr'}>
            <StaffHR session={session} opsToken={opsToken} opsRole={session?.opsRole} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'performance') && (
          <LazyTab active={staffView === 'performance'}>
            <StaffTasks session={session} opsToken={opsToken} opsRole={session?.opsRole} topTab="performance" />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'analytics') && (
          <LazyTab active={staffView === 'analytics'}>
            <StaffTasks session={session} opsToken={opsToken} opsRole={session?.opsRole} topTab="analytics" />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'kiosk') && (
          <LazyTab active={staffView === 'kiosk'}>
            <KioskAnalytics session={session} opsToken={opsToken} opsRole={session?.opsRole} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'guests') && (
          <LazyTab active={staffView === 'guests'}>
            <MasterTracker session={session} opsToken={opsToken} opsRole={session?.opsRole} lockedView="client" />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'analytics') && (
          <LazyTab active={staffView === 'google_reviews'}>
            <MasterTracker session={session} opsToken={opsToken} opsRole={session?.opsRole} lockedView="google_reviews" />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'service_logs') && (
          <LazyTab active={staffView === 'service_logs'}>
            <ServiceLogs opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'reporting') && (
          <LazyTab active={staffView === 'reporting'}>
            <StaffReporting session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'poppy_reports') && (
          <LazyTab active={staffView === 'poppy_reports'}>
            <PoppyReports session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'poppy_book') && (
          <LazyTab active={staffView === 'poppy_book'}>
            <PoppyBook session={session} opsToken={opsToken} onNavigate={setStaffView} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'poppy_guests') && (
          <LazyTab active={staffView === 'poppy_guests'}>
            <PoppyGuestProfile session={session} opsToken={opsToken} onNavigate={setStaffView} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'poppy_master_data') && (
          <LazyTab active={staffView === 'poppy_master_data'}>
            <MasterData session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'poppy_blockouts') && (
          <LazyTab active={staffView === 'poppy_blockouts'}>
            <PoppyBlockoutTypes session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'poppy_divergence') && (
          <LazyTab active={staffView === 'poppy_divergence'}>
            <PoppyDivergenceReview session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'service_clearance') && (
          <LazyTab active={staffView === 'service_clearance'}>
            <ServiceClearances session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'poppy_configuration') && (
          <LazyTab active={staffView === 'poppy_configuration'}>
            <PoppyConfiguration session={session} onNavigate={setStaffView} />
          </LazyTab>
        )}

        {/* Business details — entered via the Configuration hub row (no own
            nav button); same module gate as the hub. */}
        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'poppy_configuration') && (
          <LazyTab active={staffView === 'poppy_business_details'}>
            <PoppyBusinessDetails opsToken={opsToken} />
          </LazyTab>
        )}

        {/* Appointments + Notifications config — same hub-row entry pattern
            and module gate as Business details. */}
        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'poppy_configuration') && (
          <LazyTab active={staffView === 'poppy_appointments_config'}>
            <PoppyAppointmentsConfig opsToken={opsToken} />
          </LazyTab>
        )}
        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'poppy_configuration') && (
          <LazyTab active={staffView === 'poppy_notifications_config'}>
            <PoppyNotificationsConfig opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'inventory') && (
          <LazyTab active={staffView === 'inventory'}>
            <StaffInventory session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'hr') && (
          <LazyTab active={staffView === 'payroll'}>
            <Payroll session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' &&
          (session?.opsRole === 'owner' || canAccessByKey(session, 'month_end')) && (
          <LazyTab active={staffView === 'month-end'}>
            <MonthEnd session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'calloff') && (
          <LazyTab active={staffView === 'optimizer'}>
            <ScheduleOptimizer session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'calloff') && (
          <LazyTab active={staffView === 'calloff'}>
            <CallOffAdvisor session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'late_starts') && (
          <LazyTab active={staffView === 'late_starts'}>
            <StaffAdvisor session={session} opsToken={opsToken} active={staffView === 'late_starts'} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'sync') && (
          <LazyTab active={staffView === 'sync'}>
            <ScheduleSync session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'campaigns') && (
          <LazyTab active={staffView === 'campaigns'}>
            <CampaignMonitor session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'hr') && (
          <LazyTab active={staffView === 'checklists'}>
            <ChecklistSettings session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {/* Gate must match the nav item + backend routes ('audits', the
            module labeled Morning Routine) — gating on 'performance' left
            audits-only grantees a nav option whose tab rendered nothing. */}
        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'audits') && (
          <LazyTab active={staffView === 'audits'}>
            <AuditHome session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'performance') && (
          <LazyTab active={staffView === 'inbox'}>
            <PerformanceInbox session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'hr') && (
          <LazyTab active={staffView === 'routing'}>
            <PerformanceRouting session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canAccessSettings && (
          <LazyTab active={staffView === 'settings'}>
            <SettingsHub session={session} opsToken={opsToken} onNavigate={setStaffView} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canAccessSettings && (
          <LazyTab active={staffView === 'settings_team'}>
            <TeamSettings session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canAccessSettings && (
          <LazyTab active={staffView === 'settings_checklists'}>
            <ChecklistSettings session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canAccessSettings && (
          <LazyTab active={staffView === 'settings_performance'}>
            <PerformanceRouting session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canAccessSettings && (
          <LazyTab active={staffView === 'settings_integrations'}>
            <ScheduleSync session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canAccessSettings && (
          <LazyTab active={staffView === 'settings_slowtime'}>
            <SlowTimePool session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canAccessSettings && (
          <LazyTab active={staffView === 'settings_kiosk'}>
            <KioskPaymentSettings session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canAccessSettings && (
          <LazyTab active={staffView === 'settings_late_start'}>
            <LateStartSettings session={session} opsToken={opsToken} />
            <EarlyDepartureSettings session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'pay_config') && (
          <LazyTab active={staffView === 'settings_pay'}>
            <PayConfigSettings session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && session?.opsRole === 'owner' && (
          <LazyTab active={staffView === 'settings_marketing'}>
            <MarketingSettings session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && session?.opsRole === 'owner' && (
          <LazyTab active={staffView === 'settings_kpi_reports'}>
            <KPIReportsSettings session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canAccessSettings && (
          <LazyTab active={staffView === 'settings_kpi_targets'}>
            <KPITargetsSettings session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'booking_order') && (
          <LazyTab active={staffView === 'booking_order'}>
            <BookingOrderSettings session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'change_requests_log') && (
          <LazyTab active={staffView === 'change_requests'}>
            <ChangeRequestsLog session={session} opsToken={opsToken} />
          </LazyTab>
        )}

        {session && step !== 'login' && step !== 'geofence' && step !== 'blocked' && canModule(session, 'me') && (
          <LazyTab active={staffView === 'me'}>
            <StaffMe session={dataSession} />
          </LazyTab>
        )}
      </Suspense>

      {/* GEOFENCE CHECK */}
      {step === 'geofence' && (
        <div className="staff-screen staff-center">
          <div className="staff-spinner" />
          <p className="staff-loading-text">Checking location...</p>
        </div>
      )}

      {/* BLOCKED — outside geofence */}
      {step === 'blocked' && (
        <div className="staff-screen staff-center">
          <div className="staff-icon">📍</div>
          <h2 className="staff-title">Location Required</h2>
          <p className="staff-subtitle">{geoError}</p>
          <button className="staff-btn-primary" onClick={() => { setGeoError(''); setStep('geofence'); }}>
            Try Again
          </button>
        </div>
      )}

      {/* LOGIN */}
      {step === 'login' && (
        <div className="staff-screen staff-center">
          <h2 className="staff-title">Staff Sign In</h2>
          <p className="staff-subtitle">{center?.name || 'Cutie Pie Wax Bar'}</p>
          <form className="staff-form" onSubmit={handleLogin}>
            <select
              className="staff-input"
              value={center?.centerId || ''}
              onChange={e => setCenter(CENTER_COORDS.find(c => c.centerId === e.target.value))}
            >
              {CENTER_COORDS.map(c => (
                <option key={c.centerId} value={c.centerId}>{c.name}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoComplete="username"
              className="staff-input"
            />
            <div className="staff-password-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                className="staff-input"
              />
              <button
                type="button"
                className="staff-password-toggle"
                onClick={() => setShowPassword(p => !p)}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁'}
              </button>
            </div>
            {loginError && <div className="staff-error">{loginError}</div>}
            <button type="submit" className="staff-btn-primary" disabled={loginLoading}>
              {loginLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      )}

      {/* TIME CLOCK PAGE (nav destination, under More) */}
      {staffView === 'time_clock' && step !== 'login' && step !== 'geofence' && step !== 'blocked' && (
        <div className="staff-screen staff-center">
          <div className="clock-gate">
            <div className="clock-gate-icon">🕐</div>
            <h2 className="staff-title">Time Clock</h2>
            <p className="staff-subtitle">Hi {session?.employeeName?.split(' ')[0]}!</p>

            {clockStatus?.scheduledStart && (
              <div className="clock-shift-info">
                Today's shift: {formatClockTime(clockStatus.scheduledStart)}
                {clockStatus.scheduledEnd ? ` – ${formatClockTime(clockStatus.scheduledEnd)}` : ''}
              </div>
            )}

            {/* Status */}
            {clockStatus?.clockedIn && !clockStatus?.clockedOut && (
              <div className="clock-status-badge" style={{ margin: '14px 0' }}>
                ✓ Clocked in {clockStatus.clockInTime ? `at ${formatStoredTime(clockStatus.clockInTime)}` : ''}{clockStatus.centerName ? ` · ${clockStatus.centerName}` : ''}
              </div>
            )}
            {clockStatus?.clockedOut && (
              <div className="clock-shift-info" style={{ color: '#4caf50', fontWeight: 600 }}>
                👋 Clocked out{clockStatus.clockOutTime ? ` at ${formatStoredTime(clockStatus.clockOutTime)}` : ''}
                {clockStatus.totalHours != null ? ` · ${clockStatus.totalHours.toFixed(2)} hrs today` : ''}
              </div>
            )}
            {!clockStatus?.clockedIn && !clockStatus?.clockedOut && (
              <div className="clock-shift-info">You're not clocked in yet.</div>
            )}

            {/* Early-clock-in notice (only when not yet clocked in and early) */}
            {!clockStatus?.clockedIn && clockStatus?.scheduledStart && (() => {
              const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Vancouver' }));
              const [h, m] = clockStatus.scheduledStart.split(':').map(Number);
              const shiftStart = new Date(now); shiftStart.setHours(h, m, 0, 0);
              const fiveMinBefore = new Date(shiftStart.getTime() - 5 * 60 * 1000);
              if (now < fiveMinBefore) {
                return <div className="clock-adjust-notice">Clock-in will be recorded as {fiveMinBefore.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>;
              }
              return null;
            })()}

            {clockError && <div className="staff-error">{clockError}</div>}

            {/* Action button */}
            {!clockStatus?.clockedOut && (
              clockStatus?.clockedIn ? (
                <button className="staff-btn-primary clock-btn" onClick={handleClockOut} disabled={clockLoading}>
                  {clockLoading ? 'Clocking out...' : 'Clock Out'}
                </button>
              ) : (
                <button className="staff-btn-primary clock-btn" onClick={handleClockIn} disabled={clockLoading}>
                  {clockLoading ? 'Clocking in...' : 'Clock In'}
                </button>
              )
            )}

            {!canWrite && (
              <div className="clock-shift-info" style={{ marginTop: 12, fontSize: 13, color: '#b08' }}>
                Viewing as someone else — clock actions are disabled.
              </div>
            )}
          </div>
        </div>
      )}

      {/* CLOCK IN GATE */}
      {step === 'clock-in' && (
        <div className="staff-screen staff-center">
          <div className="clock-gate">
            <div className="clock-gate-icon">&#128336;</div>
            <h2 className="staff-title">Clock In</h2>
            <p className="staff-subtitle">Hi {session?.employeeName?.split(' ')[0]}!</p>
            {clockStatus?.scheduledStart && (
              <div className="clock-shift-info">
                Your shift starts at {(() => {
                  const [h, m] = clockStatus.scheduledStart.split(':').map(Number);
                  const d = new Date(); d.setHours(h, m);
                  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                })()}
              </div>
            )}
            {clockStatus?.scheduledStart && (() => {
              const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Vancouver' }));
              const [h, m] = clockStatus.scheduledStart.split(':').map(Number);
              const shiftStart = new Date(now); shiftStart.setHours(h, m, 0, 0);
              const fiveMinBefore = new Date(shiftStart.getTime() - 5 * 60 * 1000);
              if (now < fiveMinBefore) {
                return (
                  <div className="clock-adjust-notice">
                    Clock-in will be recorded as {fiveMinBefore.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </div>
                );
              }
              return null;
            })()}
            {clockError && <div className="staff-error">{clockError}</div>}
            <button className="staff-btn-primary clock-btn" onClick={handleClockIn} disabled={clockLoading}>
              {clockLoading ? 'Clocking in...' : 'Clock In'}
            </button>
          </div>
        </div>
      )}

      {/* CLIENT LIST */}
      {staffView === 'clients' && step === 'clients' && (
        <div className="staff-screen">
          <h2 className="staff-title">Schedule</h2>
          <p className="staff-subtitle">Tap a client to get started</p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, margin: '8px 0 14px' }}>
            <button
              aria-label="Previous day"
              disabled={scheduleOffset <= -SCHEDULE_BACK_DAYS}
              style={{ opacity: scheduleOffset <= -SCHEDULE_BACK_DAYS ? 0.3 : 1, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => setScheduleDate(d => shiftDateStr(d, -1))}
            >&lsaquo;</button>
            <button
              onClick={() => setScheduleDate(today)}
              title={isTodaySchedule ? undefined : 'Back to today'}
              aria-label={isTodaySchedule ? undefined : 'Back to today'}
              style={{ fontWeight: 700, fontSize: 14, background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {isTodaySchedule ? 'Today' : new Date(`${scheduleDate}T00:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' })}
            </button>
            <button
              aria-label="Next day"
              disabled={scheduleOffset >= SCHEDULE_FWD_DAYS}
              style={{ opacity: scheduleOffset >= SCHEDULE_FWD_DAYS ? 0.3 : 1, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => setScheduleDate(d => shiftDateStr(d, 1))}
            >&rsaquo;</button>
          </div>
          {loadingClients ? (
            <div className="staff-center"><div className="staff-spinner" /><p className="staff-loading-text">Loading...</p></div>
          ) : appointments.length === 0 && blockouts.length === 0 ? (
            <>
              <div className="staff-empty">{isTodaySchedule ? 'No appointments found for today.' : 'No appointments for this day.'}</div>
              {/* No gaps on an appointment-free day — show every duty (incl. no-gap) here. */}
              {myDuties.length > 0 && (
                <div style={{ textAlign: 'center', fontSize: 11, padding: '6px 0' }}>
                  {myDuties.map(d => dutyPill(d, { showTime: true }))}
                </div>
              )}
            </>
          ) : (
            <div className="staff-list">
              {(() => {
                // Filter out No Show, then merge + sort.
                const items = [
                  ...appointments.filter(a => a.status !== 'No Show').map(a => ({ ...a, _type: 'appt' })),
                  ...blockouts.map(b => ({ ...b, _type: 'blockout' })),
                ].sort((a, b) => new Date(a.startTime || 0) - new Date(b.startTime || 0));

                // Most-progressed status across multi-service appointments.
                const STATUS_RANK = { 'Booked': 0, 'Confirmed': 1, 'Checked-in': 2, 'Started': 3, 'Closed': 4, 'Completed': 4 };
                const mostProgressedStatus = (it) => {
                  const list = (it.statuses || [it.status]).filter(Boolean);
                  if (list.length === 0) return it.status || 'Booked';
                  return list.reduce((best, s) => (STATUS_RANK[s] ?? -1) > (STATUS_RANK[best] ?? -1) ? s : best, list[0]);
                };

                // Background colour by lifecycle state.
                const cardBg = (it) => {
                  const s = mostProgressedStatus(it);
                  if (s === 'Checked-in' || s === 'Started') return '#dcfce7'; // mint — actively servicing
                  if (s === 'Completed' || s === 'Closed') return '#f3f4f6'; // grey — done
                  const start = it.startTime ? new Date(it.startTime) : null;
                  if ((s === 'Booked' || s === 'Confirmed') && start && new Date() > start) return '#fef3c7'; // amber — late
                  return null; // default white card
                };

                // Card times are Pacific wall-clock strings — read the digits, never re-zone.
                const cardDayMin = (s) => { const m = /T(\d{2}):(\d{2})/.exec(String(s || '')); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
                const dutyBlock = (d) => dutyPill(d);
                const lastEnd = items.length ? cardDayMin(items[items.length - 1].endTime) : null;
                const tailDuties = myDuties.filter(d => d.noGap || (lastEnd != null && d.plannedStartMin >= lastEnd));

                const rows = items.map((item, idx) => {
                  const prev = idx > 0 ? items[idx - 1] : null;
                  const gapMin = (prev?.endTime && item.startTime)
                    ? Math.round((new Date(item.startTime) - new Date(prev.endTime)) / 60000)
                    : 0;
                  const gapStart = prev?.endTime ? cardDayMin(prev.endTime) : null;
                  const gapEnd = cardDayMin(item.startTime);
                  const dutiesHere = gapStart != null && gapEnd != null
                    ? myDuties.filter(d => d.plannedStartMin != null && !d.noGap && d.plannedStartMin >= gapStart && d.plannedStartMin < gapEnd) : [];
                  const gapNode = gapMin > 0 ? (
                    <div style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', padding: '4px 0', fontWeight: 500 }}>
                      ⏱ {gapMin} min gap
                      {dutiesHere.length > 0 && <div>{dutiesHere.map(dutyBlock)}</div>}
                    </div>
                  ) : null;

                  if (item._type === 'blockout') {
                    return (
                      <React.Fragment key={`bo-frag-${idx}`}>
                        {gapNode}
                        <div style={{
                          padding: '12px 16px', borderRadius: 12, background: '#f8f4f0',
                          border: '1.5px solid #e8ddd4', borderLeft: '6px solid #e67e22',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: '#e67e22' }}>{item.reason}</span>
                            <span style={{ fontSize: 13, color: '#999' }}>
                              {item.time}{item.endTimeStr ? ` – ${item.endTimeStr}` : ''}
                            </span>
                          </div>
                          {item.notes && item.notes !== item.reason && (
                            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{item.notes}</div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  }

                  const bg = cardBg(item);
                  const baseStyle = {};
                  if (bg) baseStyle.background = bg;
                  if ((item.serviceEntries || []).some(e => e.isRequested)) baseStyle.borderLeft = '8px solid #27ae60';

                  return (
                    <React.Fragment key={`appt-frag-${item.appointmentId || idx}`}>
                      {gapNode}
                      <button className={`staff-client-card ${item.isPaid ? 'staff-client-paid' : ''}`} style={baseStyle} onClick={() => selectClient(item)}>
                        <div className="staff-client-name-row">
                          <span className="staff-client-name">{item.guestName}</span>
                          {item.isNewClient ? (
                            <span className="staff-client-new-badge">NEW</span>
                          ) : (item.visitCount > 0) && (
                            <span className="staff-client-visit-badge">{visitBadgeLabel(item.visitCount)}</span>
                          )}
                          {item.needsForms && <span className="staff-client-forms-badge">FORMS</span>}
                          {item.hasClientNotes && (
                            <span className="staff-client-notes-badge" title={`${item.clientNoteCount} client note${item.clientNoteCount === 1 ? '' : 's'}`}>📝</span>
                          )}
                        </div>
                        {(item.serviceEntries || []).map((entry, i) => (
                          entry.isAddOn ? (
                            <div key={i} className="staff-client-addon-row">
                              <span className="staff-client-addon-icon">&#x2295;</span>
                              <span className="staff-client-svc">{entry.serviceName}</span>
                              {entry.roomName && <span className="staff-client-room" style={getRoomColor(entry.roomName) ? { background: getRoomColor(entry.roomName).bg, color: getRoomColor(entry.roomName).color } : undefined}>{entry.roomName}</span>}
                              {entry.isPaid && <span className="staff-client-paid-badge">PAID</span>}
                            </div>
                          ) : (
                            <div key={i}>
                              <div className="staff-client-service-row">
                                <span className="staff-client-time">
                                  {entry.time}{entry.endTimeStr ? ` – ${entry.endTimeStr}` : ''}
                                </span>
                                <span className="staff-dot">·</span>
                                <span className="staff-client-svc">{entry.serviceName}</span>
                                {entry.roomName && <span className="staff-client-room" style={getRoomColor(entry.roomName) ? { background: getRoomColor(entry.roomName).bg, color: getRoomColor(entry.roomName).color } : undefined}>{entry.roomName}</span>}
                                {entry.needsTreatmentLog && <span className="staff-client-log-badge">LOG</span>}
                                {entry.isPaid && <span className="staff-client-paid-badge">PAID</span>}
                              </div>
                              {(entry.addOns || []).map((addon, j) => (
                                <div key={`addon-${j}`} className="staff-client-addon-row">
                                  <span className="staff-client-addon-icon">&#x2295;</span>
                                  <span className="staff-client-svc">{addon}</span>
                                </div>
                              ))}
                            </div>
                          )
                        ))}
                        {/* Kiosk-picked add-ons (server: card.kioskAddOns) — Zenoti can't
                            take them directly any more, so the desk adds them; the
                            provider sees them here right away (plus a push). */}
                        {(item.kioskAddOns || []).map((k, i) => {
                          const added = k.status === 'done' || k.status === 'auto_applied';
                          const cant = k.status === 'cant';
                          return (
                            <div key={`kiosk-${i}`} className={`kiosk-addon-row ${added ? 'kiosk-addon-added' : cant ? 'kiosk-addon-cant' : ''}`}>
                              <span aria-hidden="true">🛎</span>
                              <span className="kiosk-addon-name">{k.name}{k.price ? ` · $${Number(k.price) % 1 ? Number(k.price).toFixed(2) : Number(k.price)}` : ''}</span>
                              <span className="kiosk-addon-status">{added ? '✓ added' : cant ? 'desk couldn’t add' : 'picked at kiosk · desk adding…'}</span>
                            </div>
                          );
                        })}
                        {item.notes && (
                          <div style={{
                            fontSize: 12, color: '#6b7280', marginTop: 6,
                            background: 'rgba(0,0,0,0.04)', padding: '6px 8px', borderRadius: 6,
                            overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                            WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', textAlign: 'left',
                          }}>
                            📝 {item.notes}
                          </div>
                        )}
                        {/* Change-request entry — today only, module-gated.
                            Checked-in → Modify; not yet checked-in → muted
                            hint chip; finished/other days → nothing. Spans
                            (not buttons) because the card itself is a
                            <button>; stopPropagation keeps card taps intact. */}
                        {isTodaySchedule && canModule(effSession, 'change_requests') && crCardStage(item) === 'checked-in' && (
                          <span
                            className="cr-modify-chip"
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); openChangeRequest(item); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openChangeRequest(item); } }}
                          >✎ Modify</span>
                        )}
                        {isTodaySchedule && canModule(effSession, 'change_requests') && crCardStage(item) === 'pre' && (
                          <span className="cr-hint-chip" aria-hidden="true">Available once your guest checks in</span>
                        )}
                        {/* Quick actions — the guest-page actions staff use most
                            (60-day usage 10/01: logs 684, review requests 331,
                            referrals 246, recos 4). Icon + small label, no
                            press-and-hold. Log only while one is missing
                            (card.needsLog from the server). Today + past days.
                            Spans, not buttons: the card itself is a <button>. */}
                        {scheduleOffset <= 0 && (() => {
                          const reviewSent = !!getActionStatus(item.guestId, 'review_request');
                          const referralSent = !!getActionStatus(item.guestId, 'referral_text');
                          const quick = (key, icon, label, kind, extra = '') => (
                            <span
                              key={key}
                              className={`card-quick ${extra}`}
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); cardQuickAction(item, kind); }}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); cardQuickAction(item, kind); } }}
                            >
                              <span className="card-quick-icon" aria-hidden="true">{icon}</span>
                              <span className="card-quick-label">{label}</span>
                            </span>
                          );
                          return (
                            <div className="card-quick-row">
                              {item.needsLog && quick('log', '📋', 'Log', 'log', 'card-quick-alert')}
                              {quick('review', '⭐', item.hasLeftReview ? 'Reviewed' : reviewSent ? 'Sent' : 'Review', 'review', (item.hasLeftReview || reviewSent) ? 'card-quick-done' : '')}
                              {quick('referral', '📲', referralSent ? 'Sent' : 'Referral', 'referral', referralSent ? 'card-quick-done' : '')}
                              {quick('recos', '💝', 'Recos', 'recos')}
                            </div>
                          );
                        })()}
                        <div className="staff-client-status">{mostProgressedStatus(item)}</div>
                      </button>
                    </React.Fragment>
                  );
                });
                return (
                  <>
                    {rows}
                    {tailDuties.length > 0 && (
                      <div style={{ textAlign: 'center', fontSize: 11, padding: '6px 0' }}>{tailDuties.map(dutyBlock)}</div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
          {/* My change requests — compact status strip (today only). Data
              refreshes on the schedule's existing cadence via lastRefresh. */}
          {isTodaySchedule && canModule(effSession, 'change_requests') && crMine.length > 0 && (
            <div className="cr-mine">
              <div className="cr-mine-title">My change requests</div>
              {crMine.map(r => {
                const meta = CR_STATUS_META[r.status] || CR_STATUS_META.open;
                const summary = (r.display || []).map(d => `${d.verb} ${d.label}`).join(' · ');
                return (
                  <div key={r.id} className="cr-mine-row">
                    <div className="cr-mine-main">
                      <span className="cr-mine-guest">{r.guestName}</span>
                      {summary && <span className="cr-mine-summary">{summary}</span>}
                      {r.status === 'cant' && r.cantReason && (
                        <span className="cr-mine-reason">“{r.cantReason}”</span>
                      )}
                    </div>
                    <span className={`cr-chip ${meta.cls}`}>{meta.label}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 8 }}>
            <button className="staff-btn-secondary staff-refresh" onClick={() => loadClients(true)}>
              Refresh
            </button>
            {lastRefresh && (
              <span style={{ fontSize: 11, color: '#bbb' }}>
                Updated {lastRefresh.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>
      )}

      {/* RECOMMEND — TABBED */}
      {staffView === 'clients' && step === 'recommend' && selectedClient && (
        <div className="staff-screen staff-recommend-screen">
          <button className="staff-back" onClick={() => setStep('actions')}>&larr; Back</button>
          <h2 className="staff-title">Recommend for {selectedClient.guestName}</h2>
          <p className="staff-subtitle">Select items to recommend at checkout</p>

          {/* Tab bar */}
          <div className="staff-tabs">
            {['packages', 'products', 'services'].map(tab => (
              <button
                key={tab}
                className={`staff-tab ${staffTab === tab ? 'active' : ''}`}
                onClick={() => { setStaffTab(tab); setCurrentCategory(null); setCurrentSubCategory(null); }}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === 'packages' && pkgCount > 0 && <span className="staff-tab-badge">{pkgCount}</span>}
                {tab === 'products' && prodCount > 0 && <span className="staff-tab-badge">{prodCount}</span>}
                {tab === 'services' && svcCount > 0 && <span className="staff-tab-badge">{svcCount}</span>}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="staff-tab-content">
            {loadingItems ? (
              <div className="staff-center"><div className="staff-spinner" /><p className="staff-loading-text">Loading...</p></div>
            ) : (
              <>
                {/* Category drill-down */}
                {shouldShowCategories() && (
                  <div className="staff-list">
                    {(staffTab === 'packages' ? getPackageCategories() : staffTab === 'products' ? getProductSubCategories() : getServiceCategories()).map(cat => {
                      const selCount = selectedItems.filter(i => {
                        if (staffTab === 'packages') return i.type === 'package' && packages.find(p => p.id === i.id && getPackageCategory(p.name) === cat.id);
                        if (staffTab === 'products') return i.type === 'product' && products.find(p => p.id === i.id && (p.subCategoryId || '__uncategorized') === cat.id);
                        return i.type === 'service' && catalog?.services?.[cat.id]?.find(s => s.id === i.id);
                      }).length;
                      return (
                        <button
                          key={cat.id}
                          className="staff-cat-btn"
                          onClick={() => setCurrentCategory(cat.id)}
                        >
                          <span>{cat.name} ({cat.count})</span>
                          <span className="staff-cat-right">
                            {selCount > 0 && <span className="staff-cat-badge">{selCount}</span>}
                            <span className="staff-cat-chevron">&rsaquo;</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Package subcategory drill-down */}
                {staffTab === 'packages' && currentCategory && !currentSubCategory && getPackageSubCategories(currentCategory).length > 1 && (
                  <>
                    <button className="staff-back" onClick={() => { setCurrentCategory(null); setCurrentSubCategory(null); }}>
                      &larr; Categories
                    </button>
                    <div className="staff-list">
                      {getPackageSubCategories(currentCategory).map(sub => {
                        const selCount = selectedItems.filter(i => {
                          if (i.type !== 'package') return false;
                          const p = packages.find(pk => pk.id === i.id);
                          if (!p || getPackageCategory(p.name) !== currentCategory) return false;
                          const n = p.name || '';
                          const pSub = /8.?pack/i.test(n) ? '8-Pack' : /6.?pack/i.test(n) ? '6-Pack' : 'Other';
                          return pSub === sub.id;
                        }).length;
                        return (
                          <button key={sub.id} className="staff-cat-btn" onClick={() => setCurrentSubCategory(sub.id)}>
                            <span>{sub.name} ({sub.count})</span>
                            <span className="staff-cat-right">
                              {selCount > 0 && <span className="staff-cat-badge">{selCount}</span>}
                              <span className="staff-cat-chevron">&rsaquo;</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Service subcategory drill-down */}
                {staffTab === 'services' && currentCategory && !currentSubCategory && getServiceSubCategories(currentCategory).length > 1 && (
                  <>
                    <button className="staff-back" onClick={() => { setCurrentCategory(null); setCurrentSubCategory(null); }}>
                      &larr; Categories
                    </button>
                    <div className="staff-list">
                      {getServiceSubCategories(currentCategory).map(sub => {
                        const selCount = selectedItems.filter(i =>
                          i.type === 'service' && (catalog?.services?.[currentCategory] || []).find(s => s.id === i.id && (s.sub_category_name || 'Other') === sub.id)
                        ).length;
                        return (
                          <button key={sub.id} className="staff-cat-btn" onClick={() => setCurrentSubCategory(sub.id)}>
                            <span>{sub.name} ({sub.count})</span>
                            <span className="staff-cat-right">
                              {selCount > 0 && <span className="staff-cat-badge">{selCount}</span>}
                              <span className="staff-cat-chevron">&rsaquo;</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Back button — products */}
                {currentCategory && staffTab === 'products' && (
                  <button className="staff-back" onClick={() => setCurrentCategory(null)}>
                    &larr; Categories
                  </button>
                )}
                {/* Back button — packages (with subcategory support) */}
                {staffTab === 'packages' && currentCategory && (currentSubCategory || getPackageSubCategories(currentCategory).length <= 1) && (
                  <button className="staff-back" onClick={() => {
                    if (currentSubCategory) { setCurrentSubCategory(null); }
                    else { setCurrentCategory(null); }
                  }}>
                    &larr; {currentSubCategory ? 'Subcategories' : 'Categories'}
                  </button>
                )}
                {staffTab === 'services' && currentCategory && (currentSubCategory || getServiceSubCategories(currentCategory).length <= 1) && (
                  <button className="staff-back" onClick={() => {
                    if (currentSubCategory) { setCurrentSubCategory(null); }
                    else { setCurrentCategory(null); }
                  }}>
                    &larr; {currentSubCategory ? 'Subcategories' : 'Categories'}
                  </button>
                )}

                {/* Item list */}
                {!shouldShowCategories() && !(staffTab === 'packages' && currentCategory && !currentSubCategory && getPackageSubCategories(currentCategory).length > 1) && !(staffTab === 'services' && currentCategory && !currentSubCategory && getServiceSubCategories(currentCategory).length > 1) && (
                  <div className="staff-list">
                    {staffTab === 'packages' && getCurrentItems().length === 0 && (
                      <div className="staff-empty">No packages available.</div>
                    )}
                    {staffTab === 'packages' && getCurrentItems().map(pkg => renderItemCard(pkg, 'package'))}

                    {staffTab === 'products' && getCurrentItems().length === 0 && (
                      <div className="staff-empty">No products available.</div>
                    )}
                    {staffTab === 'products' && getCurrentItems().map(prod => renderItemCard(prod, 'product'))}

                    {staffTab === 'services' && getCurrentItems().length === 0 && !currentCategory && (
                      <div className="staff-empty">No services available.</div>
                    )}
                    {staffTab === 'services' && currentCategory && getCurrentItems().map(svc => renderItemCard(svc, 'service'))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Bottom bar */}
          {totalCount > 0 && (
            <div className="staff-bottom-bar">
              <div className="staff-bottom-summary">{getBottomLabel()}</div>
              <button className="staff-btn-primary" onClick={saveRecommendation} disabled={saving}>
                {saving ? 'Saving...' : `Send Recommendation`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ACTIONS MENU */}
      {staffView === 'clients' && step === 'actions' && selectedClient && (
        <div className="staff-screen">
          <button className="staff-back" onClick={() => setStep('clients')}>&larr; Back</button>
          <h2 className="staff-title">{selectedClient.guestName}</h2>
          <p className="staff-subtitle">{selectedClient.time} &middot; {selectedClient.services.join(', ')}</p>

          <div className="staff-actions-grid">
            <button className="staff-action-card" onClick={goToRecommend}>
              <div className="staff-action-icon">💝</div>
              <div className="staff-action-label">Recommendations</div>
              <div className="staff-action-desc">Suggest packages, products & services</div>
              {getActionStatus(selectedClient.guestId, 'recommendation') && (
                <div className="staff-action-badge">Sent today</div>
              )}
            </button>

            <button
              className={`staff-action-card ${actionLoading ? 'loading' : ''}`}
              onClick={() => setPendingAction('referral')}
              disabled={actionLoading}
            >
              <div className="staff-action-icon">📲</div>
              <div className="staff-action-label">Referral Text</div>
              <div className="staff-action-desc">Send referral link for them to share</div>
              {getActionStatus(selectedClient.guestId, 'referral_text') && (
                <div className="staff-action-badge">Sent today</div>
              )}
            </button>

            <button
              className={`staff-action-card ${actionLoading ? 'loading' : ''}`}
              onClick={() => setPendingAction('review')}
              disabled={actionLoading}
            >
              <div className="staff-action-icon">⭐</div>
              <div className="staff-action-label">Review Request</div>
              <div className="staff-action-desc">
                {selectedClient.hasLeftReview
                  ? 'This client has already left a Google review'
                  : 'Ask for a Google review'}
              </div>
              {selectedClient.hasLeftReview && (
                <div className="staff-action-badge" style={{ background: '#eafaf1', color: '#27ae60' }}>Already reviewed</div>
              )}
              {getActionStatus(selectedClient.guestId, 'review_request') && (
                <div className="staff-action-badge">Sent today</div>
              )}
            </button>

            <button className="staff-action-card" onClick={() => goToForms()}>
              <div className="staff-action-icon">📋</div>
              <div className="staff-action-label">Forms & Logs</div>
              <div className="staff-action-desc">Guest forms, service forms & treatment logs</div>
              {selectedClient.needsForms && (
                <div className="staff-action-badge" style={{ background: '#fff3e0', color: '#e65100' }}>Needs forms</div>
              )}
              {(selectedClient.serviceEntries || []).some(e => e.needsTreatmentLog) && !selectedClient.needsForms && (
                <div className="staff-action-badge" style={{ background: '#fff3e0', color: '#e65100' }}>Needs log</div>
              )}
            </button>

            <button className="staff-action-card" onClick={goToNotes}>
              <div className="staff-action-icon">📝</div>
              <div className="staff-action-label">Client Notes</div>
              <div className="staff-action-desc">View preferences & add notes</div>
            </button>

            <button className="staff-action-card" onClick={goToHistory}>
              <div className="staff-action-icon">📖</div>
              <div className="staff-action-label">Client History</div>
              <div className="staff-action-desc">Past visits & services</div>
            </button>

            <button className="staff-action-card" onClick={goToFeedback}>
              <div className="staff-action-icon">💬</div>
              <div className="staff-action-label">Client Feedback</div>
              <div className="staff-action-desc">Report feedback or complaint</div>
            </button>

            {canAccessByKey(effSession, 'corrections') && isTodayAppointment(selectedClient) && (
              <button className="staff-action-card" onClick={openCorrections}>
                <div className="staff-action-icon">✏️</div>
                <div className="staff-action-label">Fix Booking</div>
                <div className="staff-action-desc">Swap, add, or remove a service on this visit</div>
              </button>
            )}

            <button className="staff-action-card coming-soon" disabled title="Coming soon">
              <div className="staff-action-icon">🔄</div>
              <div className="staff-action-label">Modify Booking</div>
              <div className="staff-action-desc">Change, add, or add-on services</div>
              <div className="staff-action-badge future">Future Release</div>
            </button>

            <button className="staff-action-card coming-soon" disabled title="Coming soon">
              <div className="staff-action-icon">🚫</div>
              <div className="staff-action-label">Mark No-Show</div>
              <div className="staff-action-desc">Client didn't show up</div>
              <div className="staff-action-badge future">Future Release</div>
            </button>
          </div>

          {actionMessage && <div className="staff-action-message">{actionMessage}</div>}
          {actionLoading && <div className="staff-center" style={{ marginTop: 16 }}><div className="staff-spinner" /></div>}
        </div>
      )}

      {/* CLIENT HISTORY VIEW */}
      {staffView === 'clients' && step === 'history' && selectedClient && (
        <div className="staff-screen">
          <button className="staff-back" onClick={() => setStep('actions')}>&larr; Back</button>
          <h2 className="staff-title">Client History</h2>
          <p className="staff-subtitle">{selectedClient.guestName}</p>

          {pastLoading ? (
            <div className="staff-center"><div className="staff-spinner" /><p className="staff-loading-text">Loading history...</p></div>
          ) : pastAppointments.length === 0 ? (
            <div className="staff-empty">No past visits found.</div>
          ) : (
            <div className="staff-list" style={{ marginTop: 12 }}>
              {pastAppointments.map((pa, i) => (
                <div key={i} style={{
                  padding: '12px 14px', borderRadius: 12, border: '1px solid #f0e0e6',
                  marginBottom: 8, background: '#fff',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#333' }}>
                      {new Date(pa.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 8,
                      background: pa.status === 'Completed' ? '#eafaf1' : '#fdeaea',
                      color: pa.status === 'Completed' ? '#27ae60' : '#e74c3c',
                    }}>{pa.status}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>{pa.services.join(', ')}</div>
                  {pa.provider && <div style={{ fontSize: 12, color: '#999', marginTop: 3 }}>with {pa.provider}{pa.center ? ` — ${pa.center}` : ''}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CLIENT FEEDBACK */}
      {staffView === 'clients' && step === 'feedback' && selectedClient && (
        <div className="staff-screen">
          <button className="staff-back" onClick={() => setStep('actions')}>&larr; Back</button>
          <h2 className="staff-title">Client Feedback</h2>
          <p className="staff-subtitle">{selectedClient.guestName}</p>

          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <select
              className="staff-input"
              value={feedbackType}
              onChange={e => setFeedbackType(e.target.value)}
              style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #e0d0d6', fontSize: 14, fontFamily: 'Lato, sans-serif' }}
            >
              <option value="client_feedback">Client Feedback</option>
              <option value="client_complaint">Client Complaint</option>
            </select>
            <textarea
              className="staff-input"
              placeholder="Describe the feedback or issue..."
              value={feedbackText}
              onChange={e => setFeedbackText(e.target.value)}
              rows={5}
              style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #e0d0d6', fontSize: 14, fontFamily: 'Lato, sans-serif', resize: 'vertical' }}
            />
            <button
              className="staff-btn-primary"
              onClick={submitFeedback}
              disabled={!feedbackText.trim() || feedbackSaving}
              style={{ opacity: (!feedbackText.trim() || feedbackSaving) ? 0.5 : 1 }}
            >
              {feedbackSaving ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {/* FORMS VIEW */}
      {staffView === 'clients' && step === 'forms' && selectedClient && (() => {
        const gForms = guestForms.filter(f => f.formType === 'guest');
        const sForms = guestForms.filter(f => f.formType === 'service');
        // One treatment-log form per treatment-log-qualifying service on the
        // appointment (was: one per appointment, dropping additional services
        // sharing the same appointment — bug #5). Built from serviceEntries
        // rather than the Zenoti-sourced guestForms list, and "filled" comes
        // from our own DB cache (treatmentLogData), keyed by (appt, serviceId).
        const treatmentFormEntries = (selectedClient.serviceEntries || [])
          .filter(e => e.needsTreatmentLog)
          .map(e => ({
            formType: 'treatment_log',
            name: e.serviceName ? `${e.serviceName} — Treatment Log` : 'Treatment Log',
            appointmentId: e.appointmentId,
            serviceId: e.serviceId || null,
            serviceName: e.serviceName,
            filled: !!treatmentLogData[logKey(e.appointmentId, e.serviceId)],
          }));
        const hasUnfilledGuest = gForms.some(f => !f.filled);
        return (
        <div className="staff-screen">
          <button className="staff-back" onClick={() => setStep('actions')}>&larr; Back</button>
          <h2 className="staff-title">Forms & Logs</h2>
          <p className="staff-subtitle">{selectedClient.guestName}</p>

          {/* Text forms link — for unfilled guest/service forms */}
          {!formsLoading && hasUnfilledGuest && (
            <div className="staff-forms-text-row">
              <button
                className="staff-btn-primary"
                style={{ flex: 1 }}
                onClick={sendFormsText}
                disabled={actionLoading}
              >
                {actionLoading ? 'Sending...' : 'Text Forms Link'}
              </button>
              {getActionStatus(selectedClient.guestId, 'forms_text') && (
                <span className="staff-forms-sent-badge">Texted today</span>
              )}
            </div>
          )}

          {actionMessage && step === 'forms' && <div className="staff-action-message">{actionMessage}</div>}

          {formsLoading ? (
            <div className="staff-center"><div className="staff-spinner" /><p className="staff-loading-text">Loading forms...</p></div>
          ) : guestForms.length === 0 && treatmentFormEntries.length === 0 ? (
            <div className="staff-empty">No forms found for this appointment.</div>
          ) : (
            <div style={{ marginTop: 8 }}>
              {/* Render form cards — all types */}
              {[
                { label: 'Guest Forms', forms: gForms, prefix: 'g' },
                { label: 'Service Forms', forms: sForms, prefix: 's' },
                { label: 'Treatment Logs', forms: treatmentFormEntries, prefix: 't' },
              ].filter(g => g.forms.length > 0).map(group => (
                <div key={group.prefix} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, paddingLeft: 2 }}>{group.label}</div>
                  {group.forms.map((form, i) => {
                    const key = `${group.prefix}-${i}`;
                    const isExpanded = expandedForm === key;
                    const isTreatment = group.prefix === 't';
                    const borderColor = form.filled ? '#d5f0e0' : (isTreatment ? '#ffe0b2' : '#f0e0e6');
                    const bgColor = form.filled ? '#f0faf4' : (isTreatment ? '#fff8e1' : '#fff');
                    return (
                      <div key={key}>
                        <button
                          onClick={() => { setEditingLogKey(null); setExpandedForm(isExpanded ? null : key); }}
                          style={{
                            width: '100%', padding: '12px 14px', borderRadius: 12, marginBottom: isExpanded ? 0 : 8,
                            border: `1.5px solid ${borderColor}`, background: bgColor,
                            cursor: 'pointer', textAlign: 'left',
                            borderBottomLeftRadius: isExpanded ? 0 : 12,
                            borderBottomRightRadius: isExpanded ? 0 : 12,
                          }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{form.name}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{
                                fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 8,
                                background: form.filled ? '#eafaf1' : (isTreatment ? '#fff3e0' : '#fdeaea'),
                                color: form.filled ? '#27ae60' : (isTreatment ? '#e65100' : '#e74c3c'),
                              }}>{form.filled ? (isTreatment ? 'Submitted' : 'Completed') : (isTreatment ? 'Needs filling' : 'Not filled')}</span>
                              <span style={{ fontSize: 10, color: '#ccc' }}>{isExpanded ? '▲' : '▼'}</span>
                            </div>
                          </div>
                          {form.serviceName && (
                            <div style={{ fontSize: 12, color: '#888', marginTop: 3 }}>For: {form.serviceName}</div>
                          )}
                        </button>
                        {isExpanded && (
                          <div style={{
                            padding: '12px 14px', marginBottom: 8,
                            border: `1.5px solid ${borderColor}`, borderTop: 'none',
                            borderBottomLeftRadius: 12, borderBottomRightRadius: 12,
                            background: '#fff',
                          }}>
                            {form.filled && form.formType === 'guest' && guestFormData ? (
                              <GuestFormViewer data={guestFormData} />
                            ) : isTreatment ? (() => {
                              const apptId = form.appointmentId;
                              const svcId = form.serviceId || null;
                              const existingLog = apptId ? treatmentLogData[logKey(apptId, svcId)] : null;
                              const existingFormData = existingLog?.formData ? (typeof existingLog.formData === 'string' ? JSON.parse(existingLog.formData) : existingLog.formData) : null;
                              const svcName = form.serviceName || '';
                              if (existingFormData) {
                                const editKey = `edit-${key}`;
                                const canEdit = canLogForScheduleDate() && !!existingLog?.id;
                                if (editingLogKey === editKey && canEdit) {
                                  return (
                                    <div>
                                      {renderTreatmentForm(svcName, { existingData: existingFormData, onSubmit: (data) => submitTreatmentLog(apptId, svcId, svcName, data, existingLog.id) })}
                                      {editToggleBtn('Cancel', () => setEditingLogKey(null))}
                                    </div>
                                  );
                                }
                                return (
                                  <div>
                                    <TreatmentLogViewer data={existingFormData} />
                                    {canEdit && editToggleBtn('Edit', () => setEditingLogKey(editKey))}
                                  </div>
                                );
                              }
                              // Legacy/defensive: unreachable now that filled is derived from our DB cache (a filled treatment log always has a local copy).
                              // Filled in Zenoti but no local copy — content isn't retrievable via API
                              if (form.filled) {
                                const zenotiUrl = apptId
                                  ? `https://cutiepiewaxbar.zenoti.com/Appointment/AppointmentBook.aspx?AppointmentId=${apptId}`
                                  : 'https://cutiepiewaxbar.zenoti.com/';
                                return (
                                  <div>
                                    <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
                                      📝 Filled in Zenoti — view in Zenoti to see content.
                                    </div>
                                    <a
                                      href={zenotiUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        display: 'inline-block', padding: '8px 14px', borderRadius: 8,
                                        background: '#F78DA7', color: '#fff', fontSize: 13, fontWeight: 600,
                                        textDecoration: 'none',
                                      }}
                                    >
                                      Open in Zenoti ↗
                                    </a>
                                  </div>
                                );
                              }
                              if (!canLogForScheduleDate()) {
                                return (
                                  <div style={{ fontSize: 13, color: '#999', fontStyle: 'italic' }}>
                                    Log available on the appointment day.
                                  </div>
                                );
                              }
                              return renderTreatmentForm(svcName, { onSubmit: (data) => submitTreatmentLog(apptId, svcId, svcName, data) });
                            })() : form.filled ? (
                              <div style={{ fontSize: 13, color: '#888', fontStyle: 'italic' }}>Form data submitted — view details in Zenoti admin.</div>
                            ) : (
                              <div style={{ fontSize: 13, color: '#999' }}>Not yet filled. Use "Text Forms Link" to send to client.</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Per-service settings history (imported PDFs + structured logs) */}
          {Object.keys(settingsHistory).length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, paddingLeft: 2 }}>
                Settings History
              </div>
              {Object.entries(settingsHistory).map(([svcName, rows]) => (
                <div key={svcName} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6, paddingLeft: 2 }}>
                    🔆 {svcName}
                  </div>
                  <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#fafafa', borderBottom: '1px solid #eee' }}>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#666', whiteSpace: 'nowrap' }}>Date</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Area</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#666', whiteSpace: 'nowrap' }}>Settings</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Skin</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Hair</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Coarse</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Density</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Reaction</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(row => {
                          const dateStr = (() => {
                            if (!row.date) return '—';
                            const d = new Date(row.date);
                            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                            return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
                          })();
                          const onClick = row.id ? () => {
                            window.open(`${API_URL}/api/staff/treatment-log-pdf-url?id=${encodeURIComponent(row.id)}`, '_blank', 'noopener');
                          } : undefined;
                          const settingsStr = [
                            row.jPerMs2 != null ? `${row.jPerMs2} J` : null,
                            row.ms != null ? `${row.ms} ms` : null,
                            row.pulses != null ? `${row.pulses} p` : null,
                            row.spotSite || null,
                          ].filter(Boolean).join(' · ') || '—';
                          return (
                            <tr
                              key={row.rowKey || row.id}
                              onClick={onClick}
                              style={{ borderTop: row.isSecondArea ? '1px dashed #f0f0f0' : '1px solid #f0f0f0', cursor: onClick ? 'pointer' : 'default' }}
                            >
                              <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: row.isSecondArea ? '#bbb' : '#333' }}>
                                {row.isSecondArea ? '↳ same visit' : dateStr}
                              </td>
                              <td style={{ padding: '8px 10px', fontWeight: 600, color: '#555' }}>{row.area ?? '—'}</td>
                              <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{settingsStr}</td>
                              <td style={{ padding: '8px 10px' }}>{row.skinType ?? '—'}</td>
                              <td style={{ padding: '8px 10px' }}>{row.hairColor ?? '—'}</td>
                              <td style={{ padding: '8px 10px' }}>{row.coarseness ?? '—'}</td>
                              <td style={{ padding: '8px 10px' }}>{row.density ?? '—'}</td>
                              <td style={{ padding: '8px 10px' }}>{row.reaction ?? '—'}</td>
                              <td style={{ padding: '8px 10px', maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.notes || ''}>
                                {row.notes ?? '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Past Treatment Logs — filled & unfilled */}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, paddingLeft: 2 }}>
              Past Treatment Logs
            </div>
            {treatmentLogsLoading ? (
              <div style={{ textAlign: 'center', padding: '16px 0', color: '#999', fontSize: 13 }}>Loading history...</div>
            ) : treatmentLogs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '16px 0', color: '#bbb', fontSize: 13 }}>No past treatment logs found.</div>
            ) : (
              treatmentLogs.map((log, i) => {
                const rowKey = `tl-${i}`;
                const isExpanded = expandedForm === rowKey;
                const fd = log.formData ? (typeof log.formData === 'string' ? JSON.parse(log.formData) : log.formData) : null;
                const isFilled = log.filled;
                // A missed log stays fillable until the end of the appointment's
                // day (Pacific). `log.date` is a Pacific wall-clock string — read
                // the YYYY-MM-DD prefix directly (see feedback_timezone_pattern).
                const todayPacific = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Vancouver' });
                const isToday = log.date ? String(log.date).slice(0, 10) === todayPacific : false;
                const canFill = !isFilled && isToday && !!log.appointmentId;
                const clickable = isFilled || canFill;
                const borderColor = isFilled ? '#d5f0e0' : (canFill ? '#ffe0b2' : '#f5d0d0');
                const bgColor = isFilled ? '#f0faf4' : (canFill ? '#fff8e1' : '#fdf0f0');
                const dateStr = log.date ? new Date(log.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown date';
                return (
                  <div key={rowKey} style={{ marginBottom: 8 }}>
                    <button
                      onClick={() => { if (!clickable) return; setEditingLogKey(null); setExpandedForm(isExpanded ? null : rowKey); }}
                      style={{
                        width: '100%', padding: '12px 14px', borderRadius: 12,
                        border: `1.5px solid ${borderColor}`, background: bgColor,
                        cursor: clickable ? 'pointer' : 'default', textAlign: 'left',
                        borderBottomLeftRadius: isExpanded ? 0 : 12,
                        borderBottomRightRadius: isExpanded ? 0 : 12,
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{dateStr}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                            background: isFilled ? '#d5f0e0' : (canFill ? '#fff3e0' : '#fde0e0'),
                            color: isFilled ? '#2e7d32' : (canFill ? '#e65100' : '#c62828'),
                          }}>
                            {isFilled ? 'Filled' : (canFill ? 'Tap to fill' : 'Not Filled')}
                          </span>
                          {clickable && <span style={{ fontSize: 10, color: '#ccc' }}>{isExpanded ? '▲' : '▼'}</span>}
                        </div>
                      </div>
                      <div style={{ fontSize: 13, color: '#555', fontWeight: 500, marginTop: 3 }}>{log.serviceName}</div>
                      {isFilled && log.createdAt && (
                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                          Filled {new Date(log.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(log.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          {log.provider ? ` by ${log.provider}` : ''}
                        </div>
                      )}
                    </button>
                    {isExpanded && ((isFilled && fd) || canFill) && (() => {
                      const apptId = log.appointmentId;
                      const svcId = log.serviceId || null;
                      const svcName = log.serviceName || '';
                      const canEdit = isFilled && isToday && !!log.id;
                      const editKey = `edit-${rowKey}`;
                      const boxStyle = {
                        padding: '12px 14px',
                        border: `1.5px solid ${borderColor}`, borderTop: 'none',
                        borderBottomLeftRadius: 12, borderBottomRightRadius: 12,
                        background: '#fff',
                      };
                      if (isFilled && fd) {
                        if (editingLogKey === editKey && canEdit) {
                          return (
                            <div style={boxStyle}>
                              {renderTreatmentForm(svcName, { existingData: fd, onSubmit: (data) => submitTreatmentLog(apptId, svcId, svcName, data, log.id) })}
                              {editToggleBtn('Cancel', () => setEditingLogKey(null))}
                            </div>
                          );
                        }
                        return (
                          <div style={boxStyle}>
                            <TreatmentLogViewer data={fd} />
                            {canEdit && editToggleBtn('Edit', () => setEditingLogKey(editKey))}
                          </div>
                        );
                      }
                      return (
                        <div style={boxStyle}>
                          {renderTreatmentForm(svcName, { onSubmit: (data) => submitTreatmentLog(apptId, svcId, svcName, data) })}
                        </div>
                      );
                    })()}
                  </div>
                );
              })
            )}
          </div>
        </div>
      );})()}

      {/* CLIENT NOTES VIEW */}
      {staffView === 'clients' && step === 'notes' && selectedClient && (
        <div className="staff-screen">
          <button className="staff-back" onClick={() => setStep('actions')}>&larr; Back</button>
          <h2 className="staff-title">Notes for {selectedClient.guestName}</h2>
          <p className="staff-subtitle">Preferences, allergies & session notes</p>

          {/* Add note form */}
          <div className="staff-note-form">
            <select
              className="staff-input staff-note-category"
              value={noteCategory}
              onChange={e => setNoteCategory(e.target.value)}
            >
              <option value="Lash and Brow">Lash and Brow</option>
              <option value="Service Preferences">Service Preferences</option>
              <option value="Personal Connections">Personal Connections</option>
            </select>
            <textarea
              className="staff-input staff-note-textarea"
              placeholder="Add a note..."
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              rows={3}
            />
            <button
              className="staff-btn-primary"
              onClick={addNote}
              disabled={noteSaving || !newNote.trim()}
            >
              {noteSaving ? 'Saving...' : 'Add Note'}
            </button>
          </div>

          {/* Notes list */}
          {notesLoading ? (
            <div className="staff-center"><div className="staff-spinner" /><p className="staff-loading-text">Loading notes...</p></div>
          ) : clientNotes.length === 0 ? (
            <div className="staff-empty">No notes yet for this client.</div>
          ) : (
            <div className="staff-notes-list">
              {clientNotes.map((n, i) => (
                <div key={n.id || i} className={`staff-note-card ${n.source === 'zenoti' ? 'staff-note-zenoti' : ''}`}>
                  <div className="staff-note-meta">
                    <span className="staff-note-author">{n.techName || 'Unknown'}</span>
                    {n.category && (
                      <span className={`staff-note-cat staff-note-cat-${(n.category || '').replace(/\s+/g, '-').toLowerCase()}`}>{n.category}</span>
                    )}
                    {n.source === 'zenoti' && <span className="staff-note-source">Zenoti</span>}
                    <span className="staff-note-date">
                      {n.createdAt ? new Date(n.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                      {n.createdAt ? ` ${new Date(n.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}
                    </span>
                  </div>
                  <div className="staff-note-text">{n.note}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SAVED CONFIRMATION — Recommendation */}
      {staffView === 'clients' && step === 'saved' && (
        <div className="staff-screen staff-center">
          <div className="staff-icon">✓</div>
          <h2 className="staff-title">Recommendation Sent</h2>
          <p className="staff-subtitle">
            {selectedClient?.guestName} will see your recommendation{totalCount > 1 ? 's' : ''} at checkout.
          </p>
          <button className="staff-btn-primary" onClick={() => setStep('actions')}>
            Back to {selectedClient?.guestName}
          </button>
          <button className="staff-btn-secondary" style={{ marginTop: 8 }} onClick={() => setStep('clients')}>
            All Clients
          </button>
        </div>
      )}

      {/* REFERRAL SENT CONFIRMATION */}
      {staffView === 'clients' && step === 'referral-sent' && (
        <div className="staff-screen staff-center">
          <div className="staff-icon">📲</div>
          <h2 className="staff-title">Referral Text Sent!</h2>
          <p className="staff-subtitle">
            {selectedClient?.guestName} will receive a text with their personal referral link to share with friends.
          </p>
          <button className="staff-btn-primary" onClick={() => setStep('actions')}>
            Back to {selectedClient?.guestName}
          </button>
          <button className="staff-btn-secondary" style={{ marginTop: 8 }} onClick={() => setStep('clients')}>
            All Clients
          </button>
        </div>
      )}

      {/* REVIEW REQUEST SENT CONFIRMATION */}
      {staffView === 'clients' && step === 'review-sent' && (
        <div className="staff-screen staff-center">
          <div className="staff-icon">⭐</div>
          <h2 className="staff-title">Review Request Sent!</h2>
          <p className="staff-subtitle">
            {selectedClient?.guestName} will receive a text asking them to leave a Google review.
          </p>
          <button className="staff-btn-primary" onClick={() => setStep('actions')}>
            Back to {selectedClient?.guestName}
          </button>
          <button className="staff-btn-secondary" style={{ marginTop: 8 }} onClick={() => setStep('clients')}>
            All Clients
          </button>
        </div>
      )}

      {/* MODIFY BOOKING — Menu: Choose Change/Add/Add-on */}
      {staffView === 'clients' && step === 'modify-menu' && selectedClient && (
        <div className="staff-screen">
          <button className="staff-back" onClick={() => setStep('actions')}>&larr; Back</button>
          <h2 className="staff-title">Modify Booking</h2>
          <p className="staff-subtitle">What would you like to do?</p>
          <div className="staff-actions-grid">
            <button className="staff-action-card" onClick={goToChangeSelect}>
              <div className="staff-action-icon">🔄</div>
              <div className="staff-action-label">Change Service</div>
              <div className="staff-action-desc">Swap an existing service for a different one</div>
            </button>
            <button className="staff-action-card" onClick={goToAddService}>
              <div className="staff-action-icon">➕</div>
              <div className="staff-action-label">Add Service</div>
              <div className="staff-action-desc">Add another service to this booking</div>
            </button>
            <button className="staff-action-card" onClick={goToAddOns}>
              <div className="staff-action-icon">✨</div>
              <div className="staff-action-label">Add Add-on</div>
              <div className="staff-action-desc">Add an enhancement or add-on service</div>
            </button>
          </div>
        </div>
      )}

      {/* MODIFY — Select which service to change */}
      {staffView === 'clients' && step === 'modify-select' && selectedClient && (
        <div className="staff-screen">
          <button className="staff-back" onClick={() => setStep('modify-menu')}>&larr; Back</button>
          <h2 className="staff-title">Change Service</h2>
          <p className="staff-subtitle">Which service do you want to change?</p>
          {modifyLoading && (
            <div className="staff-center"><div className="staff-spinner" /><p className="staff-loading-text">Loading...</p></div>
          )}
          <div className="staff-list">
            {(selectedClient.serviceDetails || selectedClient.services.map((s) => ({ serviceName: s, appointmentId: selectedClient.appointmentId, serviceId: null }))).map((svc, i) => (
              <button key={i} className="staff-client-card" onClick={() => selectServiceToReplace(svc)}>
                <div className="staff-client-name">{svc.serviceName}</div>
                <div className="staff-client-details"><span>Tap to change this service</span></div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* MODIFY — Pick service (shared by Change + Add flows) */}
      {staffView === 'clients' && step === 'modify-pick' && (
        <div className="staff-screen">
          <button className="staff-back" onClick={() => setStep(modifyMode === 'change' ? 'modify-select' : 'modify-menu')}>&larr; Back</button>
          <h2 className="staff-title">{modifyMode === 'change' ? `Replace: ${modifyService?.serviceName}` : 'Add a Service'}</h2>
          <p className="staff-subtitle">{modifyMode === 'change' ? 'Choose the new service' : 'Choose a service to add'}</p>
          <input type="text" placeholder="Search services..." value={modifySearch} onChange={e => setModifySearch(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #f0e0e6', fontSize: 15, marginBottom: 12, boxSizing: 'border-box' }} />
          {modifyLoading && <div className="staff-center"><div className="staff-spinner" /></div>}
          {modifyCatalog && !modifyCategory && !modifySearch && (
            <div className="staff-list">
              {(modifyCatalog.categories || []).map(cat => (
                <button key={cat.id} className="staff-client-card" onClick={() => setModifyCategory(cat.id)}>
                  <div className="staff-client-name">{cat.name}</div>
                  <div className="staff-client-details"><span>{(modifyCatalog.services?.[cat.id] || []).length} services</span></div>
                </button>
              ))}
            </div>
          )}
          {modifyCatalog && modifyCategory && !modifySearch && (
            <>
              <button className="staff-back" onClick={() => setModifyCategory(null)} style={{ marginBottom: 8 }}>&larr; All Categories</button>
              <div className="staff-list">
                {(modifyCatalog.services?.[modifyCategory] || []).map(svc => (
                  <button key={svc.id} className="staff-client-card" onClick={() => modifyMode === 'change' ? confirmSwap(svc) : confirmAddService(svc)} disabled={modifyLoading}>
                    <div className="staff-client-name">{svc.display_name || svc.name}</div>
                    <div className="staff-client-details">{svc.duration && <span>{svc.duration} min</span>}{svc.price?.sales > 0 && <><span className="staff-dot">·</span><span>${svc.price.sales.toFixed(0)}</span></>}</div>
                  </button>
                ))}
              </div>
            </>
          )}
          {modifyCatalog && modifySearch && (() => {
            const q = modifySearch.toLowerCase();
            const allServices = Object.values(modifyCatalog.services || {}).flat();
            const matches = allServices.filter(s => (s.display_name || s.name || '').toLowerCase().includes(q)).slice(0, 30);
            return (
              <div className="staff-list">
                {matches.length === 0 && <p className="staff-loading-text" style={{ textAlign: 'center', padding: 16 }}>No services found</p>}
                {matches.map(svc => (
                  <button key={svc.id} className="staff-client-card" onClick={() => modifyMode === 'change' ? confirmSwap(svc) : confirmAddService(svc)} disabled={modifyLoading}>
                    <div className="staff-client-name">{svc.display_name || svc.name}</div>
                    <div className="staff-client-details">{svc.duration && <span>{svc.duration} min</span>}{svc.price?.sales > 0 && <><span className="staff-dot">·</span><span>${svc.price.sales.toFixed(0)}</span></>}</div>
                  </button>
                ))}
              </div>
            );
          })()}
          {actionMessage && <div className="staff-action-message">{actionMessage}</div>}
        </div>
      )}

      {/* MODIFY — Add-ons list */}
      {staffView === 'clients' && step === 'modify-addons' && selectedClient && (
        <div className="staff-screen">
          <button className="staff-back" onClick={() => setStep('modify-menu')}>&larr; Back</button>
          <h2 className="staff-title">Add Add-on</h2>
          <p className="staff-subtitle">Choose an add-on for {selectedClient.guestName}</p>
          {modifyLoading && (
            <div className="staff-center"><div className="staff-spinner" /><p className="staff-loading-text">Loading add-ons...</p></div>
          )}
          {!modifyLoading && addOns.length > 0 && (
            <div className="staff-list">
              {addOns.map(addon => (
                <button key={addon.id} className="staff-client-card" onClick={() => confirmAddOn(addon)} disabled={modifyLoading}>
                  <div className="staff-client-name">{addon.display_name || addon.name}</div>
                  <div className="staff-client-details">
                    {addon.duration && <span>{addon.duration} min</span>}
                    {addon.price?.sales > 0 && <><span className="staff-dot">·</span><span>${addon.price.sales.toFixed(0)}</span></>}
                  </div>
                </button>
              ))}
            </div>
          )}
          {!modifyLoading && addOns.length === 0 && modifyCatalog && (
            <>
              <p className="staff-loading-text" style={{ textAlign: 'center', padding: '8px 0', color: '#999' }}>No specific add-ons found. Browse all services:</p>
              <div className="staff-list">
                {(modifyCatalog.categories || []).map(cat => (
                  <button key={cat.id} className="staff-client-card" onClick={() => { setModifyCategory(cat.id); setStep('modify-addon-cat'); }}>
                    <div className="staff-client-name">{cat.name}</div>
                    <div className="staff-client-details"><span>{(modifyCatalog.services?.[cat.id] || []).length} services</span></div>
                  </button>
                ))}
              </div>
            </>
          )}
          {actionMessage && <div className="staff-action-message">{actionMessage}</div>}
        </div>
      )}

      {/* MODIFY — Add-on category drill-down (fallback) */}
      {staffView === 'clients' && step === 'modify-addon-cat' && modifyCatalog && modifyCategory && (
        <div className="staff-screen">
          <button className="staff-back" onClick={() => { setModifyCategory(null); setStep('modify-addons'); }}>&larr; Back</button>
          <h2 className="staff-title">Add Add-on</h2>
          <p className="staff-subtitle">Choose a service to add</p>
          <div className="staff-list">
            {(modifyCatalog.services?.[modifyCategory] || []).map(svc => (
              <button key={svc.id} className="staff-client-card" onClick={() => confirmAddOn(svc)} disabled={modifyLoading}>
                <div className="staff-client-name">{svc.display_name || svc.name}</div>
                <div className="staff-client-details">
                  {svc.duration && <span>{svc.duration} min</span>}
                  {svc.price?.sales > 0 && <><span className="staff-dot">·</span><span>${svc.price.sales.toFixed(0)}</span></>}
                </div>
              </button>
            ))}
          </div>
          {actionMessage && <div className="staff-action-message">{actionMessage}</div>}
        </div>
      )}

      {/* MODIFY — Done */}
      {staffView === 'clients' && step === 'modify-done' && (
        <div className="staff-screen staff-center">
          <div className="staff-icon">✓</div>
          <h2 className="staff-title">Booking Updated!</h2>
          <p className="staff-subtitle">{modifyDoneMessage || `Service updated for ${selectedClient?.guestName}.`}</p>
          <button className="staff-btn-primary" onClick={() => setStep('actions')}>Back to {selectedClient?.guestName}</button>
          <button className="staff-btn-secondary" style={{ marginTop: 8 }} onClick={() => setStep('clients')}>All Clients</button>
        </div>
      )}

      {/* CONFIRM SEND MODAL */}
      {pendingAction && selectedClient && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: 20,
        }} onClick={() => setPendingAction(null)}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '28px 24px', maxWidth: 340, width: '100%',
            textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>
              {pendingAction === 'referral' ? '📲' : pendingAction === 'forms' ? '📋' : pendingAction === 'noshow' ? '🚫' : '⭐'}
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#333', margin: '0 0 8px' }}>
              {pendingAction === 'referral' ? 'Send Referral Text?' : pendingAction === 'forms' ? 'Text Forms Link?' : pendingAction === 'noshow' ? 'Mark as No-Show?' : 'Send Review Request?'}
            </h3>
            <p style={{ fontSize: 14, color: '#666', margin: '0 0 20px', lineHeight: 1.5 }}>
              {pendingAction === 'referral'
                ? `This will text ${selectedClient.guestName.split(' ')[0]} a referral link they can share with friends.`
                : pendingAction === 'forms'
                ? `This will text ${selectedClient.guestName.split(' ')[0]} a link to complete their intake forms.`
                : pendingAction === 'noshow'
                ? `This will mark ${selectedClient.guestName}'s appointment as a no-show. This action cannot be undone.`
                : `This will text ${selectedClient.guestName.split(' ')[0]} a link to leave a Google review.`
              }
            </p>
            {pendingAction === 'review' && selectedClient.hasLeftReview && (
              <p style={{
                fontSize: 13, color: '#b25f00', background: '#fff4e0',
                border: '1px solid #ffd9a8', borderRadius: 8,
                padding: '10px 12px', margin: '0 0 20px', lineHeight: 1.4,
              }}>
                ⚠️ This client has already left a Google review — are you sure?
              </p>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setPendingAction(null)}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 10, border: '1.5px solid #e0d0d6',
                  background: '#fff', color: '#666', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (pendingAction === 'referral') sendReferralText();
                  else if (pendingAction === 'forms') confirmSendFormsText();
                  else if (pendingAction === 'noshow') { markNoShow(); return; }
                  else sendReviewRequest();
                  setPendingAction(null);
                }}
                disabled={actionLoading || noShowLoading}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 10, border: 'none',
                  background: pendingAction === 'noshow' ? '#e74c3c' : '#d4899e', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                  opacity: (actionLoading || noShowLoading) ? 0.6 : 1,
                }}
              >
                {noShowLoading ? 'Marking...' : actionLoading ? 'Sending...' : pendingAction === 'noshow' ? 'Confirm No-Show' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CLOCK OUT MODAL */}
      {showClockOut && (
        <div className="staff-modal-overlay" onClick={() => setShowClockOut(false)}>
          <div className="staff-modal clock-out-modal" onClick={e => e.stopPropagation()}>
            <button className="staff-modal-close" onClick={() => setShowClockOut(false)}>&times;</button>
            <div className="clock-gate-icon">&#128336;</div>
            <h3 style={{ margin: '0 0 8px', fontSize: 20 }}>Clock Out</h3>
            {clockStatus?.clockInTime && (
              <div style={{ fontSize: 14, color: '#888', marginBottom: 12 }}>
                Clocked in at {formatStoredTime(clockStatus.clockInTime)}
              </div>
            )}
            {clockStatus?.clockedOut ? (
              <div style={{ color: '#4caf50', fontWeight: 600, fontSize: 16, marginBottom: 16 }}>
                Already clocked out at {formatStoredTime(clockStatus.clockOutTime)}
                <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>{clockStatus.totalHours?.toFixed(1)} hours worked</div>
              </div>
            ) : (
              <>
                {!clockStatus?.tasksComplete && (
                  <div className="clock-tasks-warning">
                    <div style={{ fontWeight: 600, marginBottom: 6, color: '#e67e22' }}>Complete your tasks first:</div>
                    {clockStatus?.pendingItems?.map((item, i) => (
                      <div key={i} style={{ fontSize: 13, color: '#666', padding: '2px 0' }}>• {item}</div>
                    ))}
                    <button className="staff-btn-secondary" style={{ marginTop: 12 }}
                      onClick={() => { setShowClockOut(false); setStaffView('tasks'); }}>
                      Go to Tasks
                    </button>
                  </div>
                )}
                {clockStatus?.tasksComplete && (
                  <>
                    {clockError && <div className="staff-error" style={{ marginBottom: 12 }}>{clockError}</div>}
                    <button className="staff-btn-primary clock-btn" onClick={handleClockOut} disabled={clockLoading}>
                      {clockLoading ? 'Clocking out...' : 'Clock Out'}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* FIX BOOKING — Booking Corrections sheet */}
      {/* ──── Change-request modal ("Modify" flow) — separate from the
           corrections modal below; submits requests, never live-writes. ──── */}
      {crOpen && crTarget && (
        <div className="staff-modal-overlay" onClick={closeChangeRequest}>
          <div className="staff-modal corrections-modal cr-modal" onClick={e => e.stopPropagation()}>
            <button className="staff-modal-close" onClick={closeChangeRequest}>&times;</button>

            {/* ── EDIT ── */}
            {crStep === 'edit' && (
              <>
                <h3 className="cr-title">Modify services</h3>
                <p className="cr-guest-line">{crVisit?.guestName || crTarget.guestName}{crTarget.time ? <> &middot; {crTarget.time}</> : null}</p>

                {crLoading && <div className="staff-center"><div className="staff-spinner" /></div>}

                {!crLoading && crError && !crVisit && (
                  <>
                    <div className="staff-error" style={{ marginBottom: 12 }}>{crError}</div>
                    <button className="staff-btn-primary" style={{ width: '100%' }} onClick={closeChangeRequest}>Close</button>
                  </>
                )}

                {!crLoading && crVisit && (
                  <>
                    {(crVisit.isClosed || crVisit.packagePayer) && (
                      <div className="cr-soft-note">
                        {crVisit.isClosed
                          ? 'This visit looks checked out already — anything you send goes to the front desk to sort out.'
                          : 'This visit is paid with a package or membership — your request will go to the front desk to key in.'}
                      </div>
                    )}

                    <div className="cr-lines">
                      {crEffectiveLines().map((line, i) => {
                        if (line._add) {
                          return (
                            <div key={line.invoiceItemId || i} className="cr-line cr-line-new">
                              <div className="cr-line-main">
                                <span className="cr-tag cr-tag-new">NEW</span>
                                <span className="cr-line-name">{line.name}</span>
                                <span className="cr-line-price">{crMoney(line.price)}</span>
                                <button className="cr-line-x" aria-label={`Remove ${line.name}`} onClick={() => crUndo(line._add)}>✕</button>
                              </div>
                              {line._pendingLabel && <div className="cr-line-sub">{line._pendingLabel}</div>}
                            </div>
                          );
                        }
                        if (line._remove) {
                          return (
                            <div key={line.invoiceItemId || i} className="cr-line cr-line-removed">
                              <div className="cr-line-main">
                                <span className="cr-tag cr-tag-remove">REMOVE</span>
                                <span className="cr-line-name" style={{ textDecoration: 'line-through' }}>{line.name}</span>
                                <span className="cr-line-price">{crMoney(line.price)}</span>
                                <button className="cr-line-x" aria-label={`Undo remove ${line.name}`} onClick={() => crUndo(line._remove)}>Undo</button>
                              </div>
                            </div>
                          );
                        }
                        if (line._swap) {
                          const sameSvc = line._swap.sameService;
                          return (
                            <div key={line.invoiceItemId || i} className="cr-line cr-line-new">
                              <div className="cr-line-main">
                                <span className="cr-tag cr-tag-new">{sameSvc ? 'ADD-ON' : 'SWAP'}</span>
                                <span className="cr-line-name">{line._swap.newServiceName}</span>
                                <span className="cr-line-price">{crMoney(sameSvc ? line.price : (crPrice.svcById.get(line._swap.newServiceId)?.price || 0))}</span>
                                <button className="cr-line-x" aria-label={`Undo change to ${line.name}`} onClick={() => crUndo(line._swap)}>✕</button>
                              </div>
                              {!sameSvc && <div className="cr-line-sub">was {line._swap.oldServiceName}</div>}
                              {line._pendingLabel && <div className="cr-line-sub">{line._pendingLabel}</div>}
                            </div>
                          );
                        }
                        return (
                          <div key={line.invoiceItemId || i} className="cr-line">
                            <div className="cr-line-main">
                              <span className="cr-tag cr-tag-booked">BOOKED</span>
                              <span className="cr-line-name">{line.name}</span>
                              <span className="cr-line-price">{crMoney(line.price)}</span>
                            </div>
                            <div className="cr-line-actions">
                              <button className="cr-line-btn" onClick={() => crStartReplace(line)}>Change this service</button>
                              <button className="cr-line-btn cr-line-btn-danger" onClick={() => crToggleRemove(line)}>Remove</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="cr-dashed-row">
                      <button className="cr-dashed-btn" onClick={crStartAdd}>＋ Add service</button>
                      <button className="cr-dashed-btn" onClick={() => { setCrAttachTarget(null); setCrStep('attach'); }} disabled={crAttachTargets().length === 0}>＋ Add add-on</button>
                    </div>

                    {crBasket.length > 0 && (() => {
                      const t = crTotals();
                      return (
                        <div className="cr-summary">
                          <span>{t.changes} change{t.changes === 1 ? '' : 's'}{t.minutes > 0 ? ` · +${t.minutes} min` : ''}</span>
                          <span className={t.delta >= 0 ? 'cr-delta-up' : 'cr-delta-down'}>
                            {t.delta >= 0 ? '+' : '−'}{crMoney(Math.abs(t.delta))}
                          </span>
                        </div>
                      );
                    })()}

                    <textarea
                      className="cr-note"
                      placeholder="Note for the front desk (optional)"
                      value={crNote}
                      onChange={e => setCrNote(e.target.value)}
                      rows={2}
                    />

                    {crError && <div className="staff-error" style={{ marginBottom: 12 }}>{crError}</div>}

                    <button className="staff-btn-primary" style={{ width: '100%' }} disabled={crBasket.length === 0} onClick={crGoConfirm}>
                      Next — price check
                    </button>
                  </>
                )}
              </>
            )}

            {/* ── PICKER (add / change) ── */}
            {crStep === 'picker' && crVisit && (
              <>
                <button className="staff-back" onClick={() => { crResetPicker(); setCrStep('edit'); }}>&larr; Back</button>
                <h3 className="cr-title" style={{ marginTop: 8 }}>
                  {crPickerMode === 'replace' ? `Change: ${crReplaceTarget?.name}` : 'Add a service'}
                </h3>
                <input
                  type="text"
                  className="cr-search"
                  placeholder="Search your services..."
                  value={crSearch}
                  onChange={e => setCrSearch(e.target.value)}
                />
                <div className="cr-chip-list">
                  {(() => {
                    const q = crSearch.toLowerCase();
                    const matches = (crVisit.catalog || []).filter(s => !q || s.name.toLowerCase().includes(q));
                    if (matches.length === 0) return <p className="staff-loading-text" style={{ textAlign: 'center', padding: 16 }}>No services found</p>;
                    return matches.map(svc => (
                      <button key={svc.id} className="cr-svc-chip" onClick={() => crPickService(svc)}>
                        <span className="cr-svc-chip-name">{svc.name}</span>
                        <span className="cr-svc-chip-meta">{svc.duration ? `${svc.duration} min · ` : ''}{crMoney(svc.price)}</span>
                      </button>
                    ));
                  })()}
                </div>
              </>
            )}

            {/* ── ADD-ONS for a just-picked service ── */}
            {crStep === 'addons' && crPendingService && (
              <>
                <button className="staff-back" onClick={() => setCrStep('picker')}>&larr; Back</button>
                <h3 className="cr-title" style={{ marginTop: 8 }}>Add-ons for {crPendingService.name}</h3>
                <p className="cr-guest-line">Optional — tap Continue to skip</p>
                <div className="cr-chip-list">
                  {(crPendingService.addOns || []).map(addon => {
                    const checked = crAddOnIds.includes(addon.id);
                    return (
                      <button key={addon.id} className={`cr-svc-chip ${checked ? 'cr-svc-chip-on' : ''}`} onClick={() => crToggleAddOn(addon.id)}>
                        <span className="cr-svc-chip-name">{checked ? '✓ ' : ''}{addon.name}</span>
                        <span className="cr-svc-chip-meta">{crMoney(addon.price)}</span>
                      </button>
                    );
                  })}
                </div>
                <button className="staff-btn-primary" style={{ width: '100%', marginTop: 12 }} onClick={() => crCommitService(crPendingService, crAddOnIds)}>
                  Continue
                </button>
              </>
            )}

            {/* ── ATTACH: which service does the add-on ride on? ── */}
            {crStep === 'attach' && crVisit && (
              <>
                <button className="staff-back" onClick={() => { crResetPicker(); setCrStep('edit'); }}>&larr; Back</button>
                <h3 className="cr-title" style={{ marginTop: 8 }}>On which service?</h3>
                <div className="cr-chip-list">
                  {crAttachTargets().map(t => (
                    <button key={t.key} className="cr-svc-chip" onClick={() => crPickAttachTarget(t)}>
                      <span className="cr-svc-chip-name">{t.name}</span>
                      <span className="cr-svc-chip-meta">{(t.svc.addOns || []).length} add-on{(t.svc.addOns || []).length === 1 ? '' : 's'}</span>
                    </button>
                  ))}
                  {crAttachTargets().length === 0 && (
                    <p className="staff-loading-text" style={{ textAlign: 'center', padding: 16 }}>No add-ons available for these services.</p>
                  )}
                </div>
              </>
            )}

            {/* ── ATTACH-PICK: toggle the add-ons ── */}
            {crStep === 'attach-pick' && crAttachTarget && (
              <>
                <button className="staff-back" onClick={() => setCrStep('attach')}>&larr; Back</button>
                <h3 className="cr-title" style={{ marginTop: 8 }}>Add-ons on {crAttachTarget.name}</h3>
                <div className="cr-chip-list">
                  {(crAttachTarget.svc.addOns || []).map(addon => {
                    const checked = crAddOnIds.includes(addon.id);
                    return (
                      <button key={addon.id} className={`cr-svc-chip ${checked ? 'cr-svc-chip-on' : ''}`} onClick={() => crToggleAddOn(addon.id)}>
                        <span className="cr-svc-chip-name">{checked ? '✓ ' : ''}{addon.name}</span>
                        <span className="cr-svc-chip-meta">{crMoney(addon.price)}</span>
                      </button>
                    );
                  })}
                </div>
                <button className="staff-btn-primary" style={{ width: '100%', marginTop: 12 }} onClick={crCommitAttach}>
                  Done
                </button>
              </>
            )}

            {/* ── CONFIRM: guest price-OK gate ── */}
            {crStep === 'confirm' && crVisit && (() => {
              const t = crTotals();
              const guestFirst = (crVisit.guestName || 'your guest').split(' ')[0];
              return (
                <>
                  <h3 className="cr-title">Price check</h3>
                  <div className="cr-confirm-summary">
                    {crBasket.map((c, i) => (
                      <div key={i} className="cr-confirm-row">
                        {c.type === 'add' && <>ADD {c.serviceName}{c.addOnNames?.length ? ` + ${c.addOnNames.join(', ')}` : ''}</>}
                        {c.type === 'swap' && (c.sameService
                          ? <>ADD-ON {c.addOnNames?.join(', ')} (on {c.newServiceName})</>
                          : <>SWAP {c.oldServiceName} → {c.newServiceName}{c.addOnNames?.length ? ` + ${c.addOnNames.join(', ')}` : ''}</>)}
                        {c.type === 'remove' && <>REMOVE {c.serviceName}</>}
                      </div>
                    ))}
                  </div>
                  <div className="cr-ok-block">
                    <div className="cr-ok-price">
                      Was {crMoney(Math.round((t.newTotal - t.delta) * 100) / 100)} → New total: <b>{crMoney(t.newTotal)}</b>{' '}
                      <span className={t.delta >= 0 ? 'cr-delta-up' : 'cr-delta-down'}>({t.delta >= 0 ? '+' : '−'}{crMoney(Math.abs(t.delta))})</span>
                    </div>
                    <p className="cr-ok-text">Tell {guestFirst} the new price. Only send once they've OK'd it.</p>
                    {crError && <div className="staff-error" style={{ marginBottom: 10 }}>{crError}</div>}
                    <button className="cr-ok-send" disabled={crSubmitting} onClick={submitChangeRequest}>
                      {crSubmitting
                        ? 'Sending…'
                        : crVisit.mode === 'auto' ? "They've OK'd the price — apply it" : "They've OK'd the price — send it"}
                    </button>
                    <button className="cr-ok-back" onClick={crBackToEdit}>&lsaquo; Back to editing</button>
                  </div>
                </>
              );
            })()}

            {/* ── DONE ── */}
            {crStep === 'done' && (
              <div className="cr-done">
                <div className="cr-done-icon">✓</div>
                <p className="cr-done-text">
                  {crResult === 'applied'
                    ? "Done — it's on the invoice."
                    : 'Sent — front desk will have it in Zenoti before checkout.'}
                </p>
                <button className="staff-btn-primary" style={{ width: '100%' }} onClick={closeChangeRequest}>Done</button>
              </div>
            )}
          </div>
        </div>
      )}

      {correctionsOpen && selectedClient && (
        <div className="staff-modal-overlay" onClick={closeCorrections}>
          <div className="staff-modal corrections-modal" onClick={e => e.stopPropagation()}>
            <button className="staff-modal-close" onClick={closeCorrections}>&times;</button>

            {correctionsStep === 'lines' && (
              <>
                <h3 style={{ margin: '0 0 4px', fontSize: 20 }}>Fix Booking</h3>
                <p style={{ fontSize: 14, color: '#888', margin: '0 0 16px' }}>{selectedClient.guestName}</p>

                {correctionsLoading && (
                  <div className="staff-center"><div className="staff-spinner" /></div>
                )}

                {!correctionsLoading && correctionsError && (
                  <div className="staff-error" style={{ marginBottom: 12 }}>{correctionsError}</div>
                )}

                {!correctionsLoading && correctionsVisit && correctionsVisit.isClosed && (
                  <div className="staff-empty">This invoice is already closed. Corrections must be done at the desk.</div>
                )}

                {!correctionsLoading && correctionsVisit && !correctionsVisit.isClosed && correctionsVisit.packagePayer && (
                  <div className="staff-empty">This visit was paid with a package or membership. Corrections must be done at the desk.</div>
                )}

                {!correctionsLoading && correctionsVisit && !correctionsVisit.isClosed && !correctionsVisit.packagePayer && (
                  <>
                    <div className="staff-list" style={{ marginBottom: 12 }}>
                      {correctionsEffectiveLines().map((line, i) => {
                        const pendingChange = line._pendingAdd
                          ? correctionsBasket.find(c => c.type === 'add' && c.serviceId === line.serviceId)
                          : correctionsBasket.find(c => (c.type === 'swap' || c.type === 'remove') && c.invoiceItemId === line.invoiceItemId);
                        return (
                          <div key={line.invoiceItemId || i} className="staff-client-card" style={{ cursor: 'default', opacity: line._removed ? 0.5 : 1 }}>
                            <div className="staff-client-name">
                              {line._removed ? <span style={{ textDecoration: 'line-through' }}>{line.name}</span> : line.name}
                              {line._pendingAdd && <span className="staff-action-badge" style={{ marginLeft: 8, background: '#eafaf1', color: '#27ae60' }}>New</span>}
                              {pendingChange?.type === 'swap' && <span className="staff-action-badge" style={{ marginLeft: 8, background: '#fff3e0', color: '#e65100' }}>Changed</span>}
                            </div>
                            {(line.price > 0 || line._pendingLabel) && (
                              <div className="staff-client-details">
                                {line.price > 0 && <span>${line.price.toFixed(0)}</span>}
                                {line.price > 0 && line._pendingLabel && <span className="staff-dot">·</span>}
                                {line._pendingLabel && <span>{line._pendingLabel}</span>}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                              {!line._removed && !line._pendingAdd && (
                                <>
                                  <button className="staff-btn-secondary" style={{ padding: '6px 12px', fontSize: 13 }} onClick={() => startReplaceLine(line)}>Replace</button>
                                  <button className="staff-btn-secondary" style={{ padding: '6px 12px', fontSize: 13, color: '#e74c3c' }} onClick={() => toggleRemoveLine(line)}>Remove</button>
                                </>
                              )}
                              {pendingChange && (
                                <button className="staff-btn-secondary" style={{ padding: '6px 12px', fontSize: 13 }} onClick={() => undoBasketChange(pendingChange)}>Undo</button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <button className="staff-btn-secondary" style={{ width: '100%', marginBottom: 12 }} onClick={startAddService}>+ Add a Service</button>

                    {correctionsBasket.length > 0 && (
                      <div className="corrections-summary">{correctionsSummary()}</div>
                    )}

                    <button className="staff-btn-primary" style={{ width: '100%' }} disabled={correctionsBasket.length === 0 || correctionsSubmitting} onClick={submitCorrections}>
                      {correctionsSubmitting ? 'Applying — this can take a minute or two, keep the sheet open…' : 'Confirm Changes'}
                    </button>
                  </>
                )}
              </>
            )}

            {correctionsStep === 'picker' && (
              <>
                <button className="staff-back" onClick={() => setCorrectionsStep('lines')}>&larr; Back</button>
                <h3 style={{ margin: '8px 0 4px', fontSize: 18 }}>
                  {correctionsPickerMode === 'replace' ? `Replace: ${correctionsReplaceTarget?.name}` : 'Add a Service'}
                </h3>
                <input
                  type="text"
                  placeholder="Search services..."
                  value={correctionsSearch}
                  onChange={e => setCorrectionsSearch(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #f0e0e6', fontSize: 15, margin: '8px 0 12px', boxSizing: 'border-box' }}
                />
                {(() => {
                  const svcCatalog = correctionsVisit?.catalog || [];
                  const q = correctionsSearch.toLowerCase();
                  const categories = [...new Set(svcCatalog.map(s => s.category || 'Other'))];

                  if (q) {
                    const matches = svcCatalog.filter(s => s.name.toLowerCase().includes(q)).slice(0, 30);
                    return (
                      <div className="staff-list">
                        {matches.length === 0 && <p className="staff-loading-text" style={{ textAlign: 'center', padding: 16 }}>No services found</p>}
                        {matches.map(svc => (
                          <button key={svc.id} className="staff-client-card" onClick={() => pickCorrectionsService(svc)}>
                            <div className="staff-client-name">{svc.name}</div>
                            <div className="staff-client-details">
                              {svc.duration && <span>{svc.duration} min</span>}
                              {svc.price > 0 && <><span className="staff-dot">·</span><span>${svc.price.toFixed(0)}</span></>}
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  }

                  if (!correctionsCategory) {
                    return (
                      <div className="staff-list">
                        {categories.map(cat => (
                          <button key={cat} className="staff-client-card" onClick={() => setCorrectionsCategory(cat)}>
                            <div className="staff-client-name">{cat}</div>
                            <div className="staff-client-details"><span>{svcCatalog.filter(s => (s.category || 'Other') === cat).length} services</span></div>
                          </button>
                        ))}
                      </div>
                    );
                  }

                  return (
                    <>
                      <button className="staff-back" onClick={() => setCorrectionsCategory(null)} style={{ marginBottom: 8 }}>&larr; All Categories</button>
                      <div className="staff-list">
                        {svcCatalog.filter(s => (s.category || 'Other') === correctionsCategory).map(svc => (
                          <button key={svc.id} className="staff-client-card" onClick={() => pickCorrectionsService(svc)}>
                            <div className="staff-client-name">{svc.name}</div>
                            <div className="staff-client-details">
                              {svc.duration && <span>{svc.duration} min</span>}
                              {svc.price > 0 && <><span className="staff-dot">·</span><span>${svc.price.toFixed(0)}</span></>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </>
            )}

            {correctionsStep === 'addons' && correctionsPendingService && (
              <>
                <button className="staff-back" onClick={() => setCorrectionsStep('picker')}>&larr; Back</button>
                <h3 style={{ margin: '8px 0 4px', fontSize: 18 }}>Add-ons for {correctionsPendingService.name}</h3>
                <p style={{ fontSize: 13, color: '#888', margin: '0 0 12px' }}>Optional — tap Continue to skip</p>
                <div className="staff-list" style={{ marginBottom: 12 }}>
                  {(correctionsPendingService.addOns || []).map(addon => {
                    const checked = correctionsAddOnIds.includes(addon.id);
                    return (
                      <button
                        key={addon.id}
                        className="staff-client-card"
                        onClick={() => toggleCorrectionsAddOn(addon.id)}
                        style={checked ? { borderColor: '#d4899e', background: '#fdf4f7' } : undefined}
                      >
                        <div className="staff-client-name">{checked ? '✓ ' : ''}{addon.name}</div>
                        <div className="staff-client-details">{addon.price > 0 && <span>${addon.price.toFixed(0)}</span>}</div>
                      </button>
                    );
                  })}
                  {(correctionsPendingService.addOns || []).length === 0 && (
                    <p className="staff-loading-text" style={{ textAlign: 'center', padding: 16 }}>No add-ons for this service.</p>
                  )}
                </div>
                <button className="staff-btn-primary" style={{ width: '100%' }} onClick={() => commitCorrectionsService(correctionsPendingService, correctionsAddOnIds)}>
                  Continue
                </button>
              </>
            )}

            {correctionsStep === 'results' && (
              <>
                <h3 style={{ margin: '0 0 12px', fontSize: 20 }}>{correctionsResultsTitle(correctionsResults)}</h3>
                <div className="staff-list" style={{ marginBottom: 16 }}>
                  {(correctionsResults || []).map((r, i) => (
                    <div key={i} className="staff-client-card" style={{ cursor: 'default' }}>
                      <div className="staff-client-name">
                        {r.status === 'success' && '✅ '}
                        {r.status === 'past_shifted' && '🟡 '}
                        {r.status === 'flagged' && '⚠️ '}
                        {r.status === 'failed' && '❌ '}
                        {r.status === 'skipped' && '⏭️ '}
                        {r.type === 'swap' ? 'Swap' : r.type === 'add' ? 'Add' : 'Remove'}
                      </div>
                      <div className="staff-client-details"><span>{r.detail}</span></div>
                    </div>
                  ))}
                </div>
                {correctionsError && <div className="staff-error" style={{ marginBottom: 12 }}>{correctionsError}</div>}
                <button className="staff-btn-primary" style={{ width: '100%' }} onClick={closeCorrections}>Done</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
